import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { adminApi } from '../lib/api';
import { formatDate, formatPrice, CATEGORIES, SEASONS, RESERVATION_STATUS, PAYMENT_STATUS, INGREDIENT_CATEGORIES, UNITS } from '../lib/utils';
import { 
  LayoutDashboard, Users, ChefHat, Utensils, MapPin, Calendar, 
  BarChart3, Settings, LogOut, Plus, Pencil, Trash2, Copy, 
  ChevronRight, Search, Filter, X, Check, CheckCircle, Loader2, Menu, Euro, Image, Phone, Mail, Download, FileText, AlertTriangle, RefreshCw, CreditCard, Clock, Wallet, TrendingUp, ArrowDownCircle, ArrowUpCircle, Sun, Snowflake, Leaf, Archive, Play, FlaskConical, Eye, UserPlus, Sliders, ScrollText, Star, EyeOff
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RichTextEditor from '../components/RichTextEditor';
import { StructuredFormatsEditor } from '../components/StructuredFormatsEditor';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import ConfigurationCenter from '../components/ConfigurationCenter';
import AuditLogViewer from '../components/AuditLogViewer';
import Logo from '../components/Logo';
import MediaUpload from '../components/MediaUpload';
import SAPPageEditor from '../components/SAPPageEditor';
import UnmetDemandDashboard from '../components/UnmetDemandDashboard';
import DriveArchiveManagement from '../components/DriveArchiveManagement';
import PromoManagement from '../components/PromoManagement';
import AddressExceptionsManagement from '../components/AddressExceptionsManagement';
import QuantityCheck from '../components/QuantityCheck';

const NAV_ITEMS = [
  { path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard },
  { path: '/admin/configuration', label: 'Configuration', icon: Sliders, highlight: true },
  { path: '/admin/unmet-demand', label: 'Demande non satisfaite', icon: TrendingUp, highlight: true },
  { path: '/admin/audit-logs', label: 'Journal', icon: ScrollText },
  { path: '/admin/simulation', label: 'Mode Simulation', icon: FlaskConical },
  { path: '/admin/finances', label: 'Finances', icon: Wallet },
  { path: '/admin/promo', label: 'Offre découverte', icon: Star },
  { path: '/admin/settings', label: 'Paramètres site', icon: Settings },
  { path: '/admin/pages', label: 'Pages éditables', icon: FileText },
  { path: '/admin/content-seo', label: 'Contenu & SEO', icon: FileText },
  { path: '/admin/sap-page', label: 'Page SAP', icon: FileText },
  { path: '/admin/pricing', label: 'Tarifs', icon: Euro },
  { path: '/admin/seasons', label: 'Saisons & Menus', icon: Calendar },
  { path: '/admin/recipes', label: 'Recettes', icon: ChefHat },
  { path: '/admin/home-preparations', label: 'Préparations maison', icon: ChefHat },
  { path: '/admin/menus', label: 'Menus', icon: Utensils },
  { path: '/admin/meal-structure', label: 'Structure des repas', icon: Utensils },
  { path: '/admin/sectors', label: 'Secteurs', icon: MapPin },
  { path: '/admin/address-exceptions', label: 'Adresses hors zone', icon: MapPin },
  { path: '/admin/quantity-check', label: 'Contrôle quantités', icon: Star },
  { path: '/admin/waiting-list', label: 'Liste d\'attente', icon: MapPin },
  { path: '/admin/employees', label: 'Employés', icon: Users },
  { path: '/admin/timeslots', label: 'Créneaux', icon: Calendar },
  { path: '/admin/reservations', label: 'Réservations', icon: Calendar },
  { path: '/admin/recurring-slots', label: 'Récurrents', icon: RefreshCw },
  { path: '/admin/payment-failures', label: 'Échecs paiement', icon: AlertTriangle },
  { path: '/admin/emails', label: 'Emails', icon: Mail },
  { path: '/admin/reviews', label: 'Avis clients', icon: Star },
  { path: '/admin/drive', label: 'Archivage Drive', icon: Archive },
  { path: '/admin/kitchen-equipment', label: 'Équipements cuisine', icon: ChefHat },
  { path: '/admin/statistics', label: 'Statistiques', icon: BarChart3 },
  { path: '/admin/users', label: 'Utilisateurs', icon: Users },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#2A312C] text-white z-50 flex flex-col transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <Logo variant="white" inverted={true} />
          <p className="text-xs text-white/60 mt-1">Administration</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#A5BBA8] text-[#2A312C]'
                  : 'text-white/70 hover:bg-white/10'
              }`}
              data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#A5BBA8] flex items-center justify-center text-[#2A312C] font-semibold">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-white/60">Admin</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="header-glass sticky top-0 z-30 h-16 flex items-center px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-4"
          >
            <Menu className="w-6 h-6 text-[#2A312C]" />
          </button>
          <h1 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2A312C]">
            {NAV_ITEMS.find(item => item.path === location.pathname)?.label || 'Administration'}
          </h1>
        </header>

        <main className="p-4 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="configuration" element={<ConfigurationCenter />} />
            <Route path="unmet-demand" element={<UnmetDemandDashboard />} />
            <Route path="audit-logs" element={<AuditLogViewer />} />
            <Route path="finances" element={<FinancesDashboard />} />
            <Route path="promo" element={<PromoManagement />} />
            <Route path="settings" element={<SiteSettingsManagement />} />
            <Route path="pages" element={<PagesManagement />} />
            <Route path="content-seo" element={<SeoContentManagement />} />
            <Route path="pricing" element={<PricingManagement />} />
            <Route path="simulation" element={<SimulationMode />} />
            <Route path="sap-page" element={<SAPPageEditor />} />
            <Route path="seasons" element={<SeasonsManagement />} />
            <Route path="recipes" element={<RecipesManagement />} />
            <Route path="home-preparations" element={<HomePreparationsManagement />} />
            <Route path="menus" element={<MenusManagement />} />
            <Route path="meal-structure" element={<MealStructureManagement />} />
            <Route path="sectors" element={<SectorsManagement />} />
            <Route path="address-exceptions" element={<AddressExceptionsManagement />} />
            <Route path="quantity-check" element={<QuantityCheck />} />
            <Route path="waiting-list" element={<WaitingListManagement />} />
            <Route path="employees" element={<EmployeesManagement />} />
            <Route path="timeslots" element={<TimeSlotsManagement />} />
            <Route path="reservations" element={<ReservationsManagement />} />
            <Route path="recurring-slots" element={<RecurringSlotsManagement />} />
            <Route path="payment-failures" element={<PaymentFailuresManagement />} />
            <Route path="emails" element={<EmailsManagement />} />
            <Route path="reviews" element={<ReviewsManagement />} />
            <Route path="drive" element={<DriveArchiveManagement />} />
            <Route path="kitchen-equipment" element={<KitchenEquipmentManagement />} />
            <Route path="statistics" element={<StatisticsView />} />
            <Route path="users" element={<UsersManagement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// Dashboard Overview
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminApi.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Réservations totales" 
          value={stats?.total_reservations || 0} 
          icon={Calendar}
        />
        <StatCard 
          label="Réservations confirmées" 
          value={stats?.confirmed_reservations || 0} 
          icon={Check}
          color="text-[#A5BBA8]"
        />
        <StatCard 
          label="Interventions terminées" 
          value={stats?.completed_reservations || 0} 
          icon={ChefHat}
          color="text-[#C86B51]"
        />
        <StatCard 
          label="Chiffre d'affaires" 
          value={formatPrice(stats?.total_revenue || 0)} 
          icon={BarChart3}
        />
      </div>

      {/* Cancellations & Refunds */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-default">
          <p className="text-sm text-[#5C6A61] mb-1">Annulations</p>
          <p className="text-2xl font-semibold text-red-600">{stats?.cancelled_reservations || 0}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61] mb-1">Annulations tardives</p>
          <p className="text-2xl font-semibold text-red-800">{stats?.late_cancellations || 0}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61] mb-1">Montant remboursé</p>
          <p className="text-2xl font-semibold text-purple-600">{formatPrice(stats?.total_refunded || 0)}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-default">
          <h3 className="font-semibold text-[#2A312C] mb-4">Villes les plus demandées</h3>
          {stats?.top_cities?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_cities.slice(0, 5).map(([city, count], index) => (
                <div key={city} className="flex items-center justify-between">
                  <span className="text-[#5C6A61]">{city}</span>
                  <span className="font-semibold text-[#2A312C]">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#5C6A61]">Aucune donnée</p>
          )}
        </div>

        <div className="card-default">
          <h3 className="font-semibold text-[#2A312C] mb-4">Demandes hors secteur</h3>
          <p className="text-3xl font-semibold text-[#C86B51]">{stats?.out_of_sector_requests || 0}</p>
          <p className="text-sm text-[#5C6A61]">demandes enregistrées</p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-[#2A312C]" }) {
  return (
    <div className="card-default">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div>
          <p className={`text-2xl font-semibold ${color}`}>{value}</p>
          <p className="text-sm text-[#5C6A61]">{label}</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SIMULATION MODE - Full testing environment
// =============================================================================

function SimulationMode() {
  const [simulationStatus, setSimulationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [testUser, setTestUser] = useState(null);
  const [testReservation, setTestReservation] = useState(null);
  const [journal, setJournal] = useState([]);
  const [history, setHistory] = useState([]);
  const [employeeView, setEmployeeView] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [virtualSlots, setVirtualSlots] = useState([]);
  const [activeTab, setActiveTab] = useState('client');
  
  useEffect(() => {
    loadStatus();
    loadHistory();
  }, []);
  
  useEffect(() => {
    if (simulationStatus?.active) {
      loadJournal();
    }
  }, [simulationStatus]);
  
  const loadStatus = async () => {
    try {
      const res = await adminApi.getSimulationStatus();
      setSimulationStatus(res.data);
      if (res.data.test_user_id) {
        setTestUser({ user_id: res.data.test_user_id });
      }
    } catch (error) {
      console.error('Error loading simulation status:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadJournal = async () => {
    try {
      const res = await adminApi.getSimulationJournal();
      setJournal(res.data.logs || []);
    } catch (error) {
      console.error('Error loading journal:', error);
    }
  };
  
  const loadHistory = async () => {
    try {
      const res = await adminApi.getSimulationHistory();
      setHistory(res.data.sessions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };
  
  const loadVirtualSlots = async () => {
    try {
      const res = await adminApi.getVirtualSlots(selectedDate);
      setVirtualSlots(res.data.slots || []);
    } catch (error) {
      console.error('Error loading virtual slots:', error);
    }
  };
  
  const handleStartSimulation = async () => {
    setActionLoading(true);
    try {
      const res = await adminApi.startSimulation();
      toast.success('Mode simulation activé !');
      setSimulationStatus({ active: true, ...res.data });
      loadJournal();
    } catch (error) {
      toast.error('Erreur lors du démarrage de la simulation');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleStopSimulation = async (cleanup = true) => {
    if (!window.confirm(cleanup 
      ? 'Arrêter la simulation et supprimer toutes les données de test ?' 
      : 'Arrêter la simulation en conservant les données de test ?'
    )) return;
    
    setActionLoading(true);
    try {
      await adminApi.stopSimulation(cleanup);
      toast.success('Mode simulation désactivé');
      setSimulationStatus({ active: false });
      setTestUser(null);
      setTestReservation(null);
      setEmployeeView(null);
      loadHistory();
    } catch (error) {
      toast.error('Erreur lors de l\'arrêt de la simulation');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCreateTestUser = async () => {
    setActionLoading(true);
    try {
      const res = await adminApi.createTestUser();
      toast.success('Utilisateur test créé !');
      setTestUser(res.data.user);
      loadJournal();
    } catch (error) {
      toast.error('Erreur lors de la création de l\'utilisateur test');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCreateReservation = async () => {
    setActionLoading(true);
    try {
      const res = await adminApi.createSimulationReservation({
        date: selectedDate,
        slot_type: 'matin',
        household_size: 4,
        formula: 'moyen',
        options: { dessert: true },
        total_price: 225.0
      });
      toast.success('Réservation test créée !');
      setTestReservation(res.data.reservation);
      loadJournal();
    } catch (error) {
      toast.error('Erreur lors de la création de la réservation');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleSimulatePayment = async () => {
    if (!testReservation) {
      toast.error('Créez d\'abord une réservation');
      return;
    }
    setActionLoading(true);
    try {
      const res = await adminApi.simulatePayment(testReservation.reservation_id);
      toast.success('Paiement simulé avec succès !');
      setTestReservation({ ...testReservation, status: 'confirmee', payment_status: 'paid' });
      loadJournal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la simulation du paiement');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleViewEmployeeView = async () => {
    if (!testReservation) {
      toast.error('Créez d\'abord une réservation');
      return;
    }
    setActionLoading(true);
    try {
      const res = await adminApi.getSimulationEmployeeView(testReservation.reservation_id);
      setEmployeeView(res.data);
      setActiveTab('employee');
    } catch (error) {
      toast.error('Erreur lors du chargement de la vue employé');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCleanupAll = async () => {
    if (!window.confirm('Supprimer TOUTES les données de simulation de la base ? Cette action est irréversible.')) return;
    
    setActionLoading(true);
    try {
      const res = await adminApi.cleanupAllSimulationData();
      toast.success(`Données supprimées: ${res.data.deleted.users} utilisateurs, ${res.data.deleted.reservations} réservations`);
      setSimulationStatus({ active: false });
      setTestUser(null);
      setTestReservation(null);
      loadHistory();
    } catch (error) {
      toast.error('Erreur lors du nettoyage');
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="simulation-mode">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-purple-600" />
            Mode Simulation
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Testez l'intégralité du site sans impact sur les données réelles
          </p>
        </div>
        <div className="flex gap-2">
          {!simulationStatus?.active ? (
            <button 
              onClick={handleStartSimulation}
              disabled={actionLoading}
              className="btn-primary flex items-center gap-2"
              data-testid="start-simulation-btn"
            >
              {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              Démarrer la simulation
            </button>
          ) : (
            <>
              <button 
                onClick={() => handleStopSimulation(true)}
                disabled={actionLoading}
                className="btn-outline text-red-600 border-red-200 hover:bg-red-50"
              >
                Arrêter et nettoyer
              </button>
              <button 
                onClick={() => handleStopSimulation(false)}
                disabled={actionLoading}
                className="btn-outline"
              >
                Arrêter (conserver données)
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Info banner */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-800">Mode simulation</h3>
            <p className="text-sm text-purple-700 mt-1">
              Toutes les actions effectuées ici sont isolées de la production :
            </p>
            <ul className="text-sm text-purple-700 mt-2 space-y-1">
              <li>• Aucun paiement Stripe réel</li>
              <li>• Emails redirigés vers : <strong>lythahenin@gmail.com</strong></li>
              <li>• Créneaux 100% virtuels (pas d'impact sur le planning réel)</li>
              <li>• Données supprimées automatiquement après 5 minutes d'inactivité</li>
            </ul>
          </div>
        </div>
      </div>
      
      {simulationStatus?.active ? (
        <>
          {/* Status Card */}
          <div className="card-default bg-green-50 border-green-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800">Simulation active</h3>
                <p className="text-sm text-green-700">
                  ID: {simulationStatus.simulation_id} | 
                  Réservations: {simulationStatus.reservations_count || 0} | 
                  Emails: {simulationStatus.emails_count || 0}
                </p>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b border-[#E3DED5]">
            {[
              { id: 'client', label: 'Parcours Client', icon: Users },
              { id: 'employee', label: 'Vue Employé', icon: Eye },
              { id: 'journal', label: 'Journal', icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-[#A5BBA8] text-[#2A312C]' 
                    : 'border-transparent text-[#5C6A61] hover:text-[#2A312C]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          {activeTab === 'client' && (
            <div className="space-y-6">
              {/* Step 1: Create Test User */}
              <div className="card-default">
                <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white flex items-center justify-center text-sm">1</span>
                  Créer un utilisateur test
                </h3>
                {testUser ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800">
                      <strong>Utilisateur créé :</strong> {testUser.email || testUser.user_id}
                    </p>
                  </div>
                ) : (
                  <button 
                    onClick={handleCreateTestUser}
                    disabled={actionLoading}
                    className="btn-outline flex items-center gap-2"
                    data-testid="create-test-user-btn"
                  >
                    <UserPlus className="w-5 h-5" />
                    Créer Client TEST
                  </button>
                )}
              </div>
              
              {/* Step 2: Select Date and Slot */}
              <div className="card-default">
                <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white flex items-center justify-center text-sm">2</span>
                  Sélectionner date et créneau
                </h3>
                <div className="flex gap-4 items-end">
                  <div>
                    <Label>Date de prestation</Label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="input-default"
                    />
                  </div>
                  <button 
                    onClick={loadVirtualSlots}
                    className="btn-outline"
                  >
                    Voir créneaux virtuels
                  </button>
                </div>
                {virtualSlots.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {virtualSlots.slice(0, 6).map(slot => (
                      <div key={slot.slot_id} className="p-3 border border-[#E3DED5] rounded-lg bg-purple-50">
                        <p className="font-medium text-sm">{slot.time_range}</p>
                        <p className="text-xs text-[#5C6A61]">{slot.employee_name}</p>
                        <Badge className="bg-purple-100 text-purple-700 text-xs mt-1">Virtuel</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Step 3: Create Reservation */}
              <div className="card-default">
                <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white flex items-center justify-center text-sm">3</span>
                  Créer une réservation test
                </h3>
                {testReservation ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800">
                      <strong>Réservation créée :</strong> {testReservation.reservation_id}
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Date: {testReservation.date} | Statut: {testReservation.status} | Total: {testReservation.total_price}€
                    </p>
                  </div>
                ) : (
                  <button 
                    onClick={handleCreateReservation}
                    disabled={actionLoading || !testUser}
                    className="btn-outline flex items-center gap-2"
                    data-testid="create-reservation-btn"
                  >
                    <Calendar className="w-5 h-5" />
                    Créer Réservation TEST
                  </button>
                )}
              </div>
              
              {/* Step 4: Simulate Payment */}
              <div className="card-default">
                <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white flex items-center justify-center text-sm">4</span>
                  Simuler le paiement
                </h3>
                {testReservation?.status === 'confirmee' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <strong>Paiement simulé avec succès</strong>
                    </p>
                  </div>
                ) : (
                  <button 
                    onClick={handleSimulatePayment}
                    disabled={actionLoading || !testReservation}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="simulate-payment-btn"
                  >
                    <CreditCard className="w-5 h-5" />
                    Simuler le paiement
                  </button>
                )}
                <p className="text-xs text-[#5C6A61] mt-2">
                  Aucun paiement Stripe réel ne sera effectué. L'email de confirmation sera envoyé à l'adresse de simulation.
                </p>
              </div>
              
              {/* Step 5: View Employee Panel */}
              <div className="card-default">
                <h3 className="font-semibold text-[#2A312C] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white flex items-center justify-center text-sm">5</span>
                  Voir la vue employé
                </h3>
                <button 
                  onClick={handleViewEmployeeView}
                  disabled={actionLoading || !testReservation}
                  className="btn-outline flex items-center gap-2"
                  data-testid="view-employee-btn"
                >
                  <Eye className="w-5 h-5" />
                  Ouvrir la vue employé
                </button>
              </div>
            </div>
          )}
          
          {activeTab === 'employee' && (
            <div className="space-y-4">
              {employeeView ? (
                <>
                  <div className="card-default bg-purple-50 border-purple-200">
                    <Badge className="bg-purple-600 text-white mb-3">VUE EMPLOYÉ - SIMULATION</Badge>
                    <h3 className="font-semibold text-lg text-[#2A312C]">
                      Intervention du {employeeView.reservation?.date}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="card-default">
                      <h4 className="font-semibold text-[#2A312C] mb-3">Informations Client</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Nom:</strong> {employeeView.client?.name}</p>
                        <p><strong>Téléphone:</strong> {employeeView.client?.phone}</p>
                        <p><strong>Adresse:</strong> {employeeView.client?.address}, {employeeView.client?.postal_code} {employeeView.client?.city}</p>
                        <p><strong>Foyer:</strong> {employeeView.client?.household_size} personnes</p>
                      </div>
                    </div>
                    
                    <div className="card-default">
                      <h4 className="font-semibold text-[#2A312C] mb-3">Détails Réservation</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Date:</strong> {employeeView.reservation?.date}</p>
                        <p><strong>Créneau:</strong> {employeeView.reservation?.time_slot}</p>
                        <p><strong>Formule:</strong> {employeeView.reservation?.formula}</p>
                        <p><strong>Statut:</strong> {employeeView.reservation?.status}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-default">
                    <h4 className="font-semibold text-[#2A312C] mb-3">Options sélectionnées</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(employeeView.options || {}).filter(([, v]) => v).map(([key]) => (
                        <Badge key={key} className="bg-[#A5BBA8]/20 text-[#2A312C]">{key}</Badge>
                      ))}
                      {Object.keys(employeeView.options || {}).filter(k => employeeView.options[k]).length === 0 && (
                        <span className="text-[#5C6A61]">Aucune option</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="card-default">
                    <h4 className="font-semibold text-[#2A312C] mb-3">Recettes à préparer</h4>
                    {employeeView.recipes?.length > 0 ? (
                      <div className="grid gap-2">
                        {employeeView.recipes.map(recipe => (
                          <div key={recipe.recipe_id} className="p-3 border border-[#E3DED5] rounded-lg">
                            <p className="font-medium">{recipe.name}</p>
                            <p className="text-sm text-[#5C6A61]">{recipe.category}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#5C6A61]">Aucune recette sélectionnée</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-[#5C6A61]">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-[#E3DED5]" />
                  <p>Créez une réservation test et cliquez sur "Ouvrir la vue employé"</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'journal' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-[#2A312C]">Journal de simulation</h3>
                <button onClick={loadJournal} className="btn-outline text-sm">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Actualiser
                </button>
              </div>
              
              {journal.length > 0 ? (
                <div className="space-y-2">
                  {journal.map((log, idx) => (
                    <div key={log.log_id || idx} className="card-default p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge className={
                            log.action_type === 'email_sent' ? 'bg-blue-100 text-blue-800' :
                            log.action_type === 'payment_simulated' ? 'bg-green-100 text-green-800' :
                            log.action_type === 'reservation_created' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {log.action_type}
                          </Badge>
                          <p className="text-sm text-[#2A312C] mt-1">{log.description}</p>
                        </div>
                        <span className="text-xs text-[#5C6A61]">
                          {new Date(log.created_at).toLocaleTimeString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#5C6A61]">
                  Aucune action enregistrée
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Inactive state */}
          <div className="text-center py-12">
            <FlaskConical className="w-16 h-16 mx-auto mb-4 text-[#E3DED5]" />
            <h3 className="text-xl font-semibold text-[#2A312C] mb-2">
              Mode simulation inactif
            </h3>
            <p className="text-[#5C6A61] max-w-md mx-auto mb-6">
              Démarrez une session de simulation pour tester l'intégralité du parcours client, 
              employé et administrateur sans impact sur les données réelles.
            </p>
            <button 
              onClick={handleStartSimulation}
              disabled={actionLoading}
              className="btn-primary mx-auto flex items-center gap-2"
            >
              {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              Démarrer la simulation
            </button>
          </div>
          
          {/* History */}
          {history.length > 0 && (
            <div className="card-default">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-[#2A312C]">Historique des simulations</h3>
                <button 
                  onClick={handleCleanupAll}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Tout supprimer
                </button>
              </div>
              <div className="space-y-2">
                {history.slice(0, 5).map(session => (
                  <div key={session.simulation_id} className="flex justify-between items-center p-3 border border-[#E3DED5] rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-[#2A312C]">{session.simulation_id}</p>
                      <p className="text-xs text-[#5C6A61]">
                        {new Date(session.created_at).toLocaleDateString('fr-FR')} - 
                        {session.log_count} actions
                      </p>
                    </div>
                    <Badge className={
                      session.status === 'active' ? 'bg-green-100 text-green-800' :
                      session.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                      'bg-orange-100 text-orange-800'
                    }>
                      {session.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// SAP PAGE MANAGEMENT - Admin interface for SAP/Tax Credit/Avance Immédiate page
// =============================================================================

function SAPPageManagement() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  useEffect(() => {
    loadContent();
  }, []);
  
  const loadContent = async () => {
    try {
      const res = await adminApi.getSAPPageContent();
      setContent(res.data);
    } catch (error) {
      console.error('Error loading SAP page content:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSAPPageContent(content);
      toast.success('Page SAP mise à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('Réinitialiser la page SAP avec le contenu par défaut ? Toutes vos modifications seront perdues.')) return;
    
    try {
      const res = await adminApi.resetSAPPageContent();
      setContent(res.data);
      toast.success('Page réinitialisée !');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };
  
  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };
  
  const updateStep = (index, field, value) => {
    const newSteps = [...(content.section4_steps || [])];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setContent(prev => ({ ...prev, section4_steps: newSteps }));
  };
  
  const updateFAQ = (index, field, value) => {
    const newFAQ = [...(content.faq_items || [])];
    newFAQ[index] = { ...newFAQ[index], [field]: value };
    setContent(prev => ({ ...prev, faq_items: newFAQ }));
  };
  
  const addFAQ = () => {
    const newFAQ = [...(content.faq_items || []), {
      question: 'Nouvelle question',
      answer: 'Réponse à la question',
      order: (content.faq_items?.length || 0) + 1
    }];
    setContent(prev => ({ ...prev, faq_items: newFAQ }));
  };
  
  const removeFAQ = (index) => {
    const newFAQ = content.faq_items.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, faq_items: newFAQ }));
  };
  
  const updateHighlightItem = (index, value) => {
    const newItems = [...(content.highlight_items || [])];
    newItems[index] = value;
    setContent(prev => ({ ...prev, highlight_items: newItems }));
  };
  
  const addHighlightItem = () => {
    const newItems = [...(content.highlight_items || []), 'Nouvel élément'];
    setContent(prev => ({ ...prev, highlight_items: newItems }));
  };
  
  const removeHighlightItem = (index) => {
    const newItems = content.highlight_items.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, highlight_items: newItems }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }
  
  if (!content) {
    return (
      <div className="text-center py-12">
        <p className="text-[#5C6A61]">Erreur lors du chargement du contenu</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="sap-page-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Page SAP / Crédit d'impôt
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Gérez le contenu de la page Service à la Personne et Avance Immédiate
          </p>
        </div>
        <div className="flex gap-2">
          <a 
            href="/sap" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-outline flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Voir la page
          </a>
          <button onClick={handleReset} className="btn-outline text-orange-600 border-orange-200 hover:bg-orange-50">
            Réinitialiser
          </button>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
      
      {/* Enable/Disable Toggle */}
      <div className="card-default flex items-center justify-between">
        <div>
          <p className="font-semibold text-[#2A312C]">Activer la page SAP</p>
          <p className="text-sm text-[#5C6A61]">Si désactivée, la page affichera un message "non disponible"</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.is_enabled}
            onChange={(e) => updateField('is_enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#A5BBA8]"></div>
        </label>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E3DED5] overflow-x-auto">
        {[
          { id: 'general', label: 'Général' },
          { id: 'sections', label: 'Sections' },
          { id: 'faq', label: 'FAQ' },
          { id: 'highlight', label: 'À retenir' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'border-[#A5BBA8] text-[#2A312C] font-medium' 
                : 'border-transparent text-[#5C6A61] hover:text-[#2A312C]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Informations générales</h3>
            
            <div>
              <Label>Titre de la page</Label>
              <input
                type="text"
                value={content.page_title || ''}
                onChange={(e) => updateField('page_title', e.target.value)}
                className="input-default"
              />
            </div>
            
            <div>
              <Label>Sous-titre</Label>
              <input
                type="text"
                value={content.page_subtitle || ''}
                onChange={(e) => updateField('page_subtitle', e.target.value)}
                className="input-default"
              />
            </div>
            
            <div>
              <Label>Numéro SAP</Label>
              <input
                type="text"
                value={content.sap_number || ''}
                onChange={(e) => updateField('sap_number', e.target.value)}
                className="input-default"
                placeholder="SAP102115565"
              />
            </div>
            
            <div>
              <Label>Texte de déclaration SAP</Label>
              <input
                type="text"
                value={content.sap_declaration_text || ''}
                onChange={(e) => updateField('sap_declaration_text', e.target.value)}
                className="input-default"
              />
            </div>
            
            <div>
              <Label>Lien officiel URSSAF</Label>
              <input
                type="url"
                value={content.urssaf_link || ''}
                onChange={(e) => updateField('urssaf_link', e.target.value)}
                className="input-default"
                placeholder="https://www.urssaf.fr/..."
              />
            </div>
            
            <div>
              <Label>Texte du bouton URSSAF</Label>
              <input
                type="text"
                value={content.section5_button_text || ''}
                onChange={(e) => updateField('section5_button_text', e.target.value)}
                className="input-default"
              />
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'sections' && (
        <div className="space-y-6">
          {/* Section 1 */}
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Section 1 : Mitonner et le SAP</h3>
            <div>
              <Label>Titre</Label>
              <input
                type="text"
                value={content.section1_title || ''}
                onChange={(e) => updateField('section1_title', e.target.value)}
                className="input-default"
              />
            </div>
            <div>
              <Label>Texte</Label>
              <textarea
                value={content.section1_text || ''}
                onChange={(e) => updateField('section1_text', e.target.value)}
                className="input-default min-h-[100px]"
              />
            </div>
          </div>
          
          {/* Section 2 */}
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Section 2 : Crédit d'impôt</h3>
            <div>
              <Label>Titre</Label>
              <input
                type="text"
                value={content.section2_title || ''}
                onChange={(e) => updateField('section2_title', e.target.value)}
                className="input-default"
              />
            </div>
            <div>
              <Label>Texte explicatif</Label>
              <textarea
                value={content.section2_text || ''}
                onChange={(e) => updateField('section2_text', e.target.value)}
                className="input-default min-h-[100px]"
              />
            </div>
            <div>
              <Label>Exemple chiffré</Label>
              <input
                type="text"
                value={content.section2_example || ''}
                onChange={(e) => updateField('section2_example', e.target.value)}
                className="input-default"
              />
            </div>
            <div>
              <Label>Mention prudente (disclaimer)</Label>
              <textarea
                value={content.section2_disclaimer || ''}
                onChange={(e) => updateField('section2_disclaimer', e.target.value)}
                className="input-default min-h-[80px]"
              />
            </div>
          </div>
          
          {/* Section 3 */}
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Section 3 : Avance Immédiate</h3>
            <div>
              <Label>Titre</Label>
              <input
                type="text"
                value={content.section3_title || ''}
                onChange={(e) => updateField('section3_title', e.target.value)}
                className="input-default"
              />
            </div>
            <div>
              <Label>Texte explicatif</Label>
              <textarea
                value={content.section3_text || ''}
                onChange={(e) => updateField('section3_text', e.target.value)}
                className="input-default min-h-[100px]"
              />
            </div>
            <div>
              <Label>Exemple</Label>
              <input
                type="text"
                value={content.section3_example || ''}
                onChange={(e) => updateField('section3_example', e.target.value)}
                className="input-default"
              />
            </div>
          </div>
          
          {/* Section 4 - Steps */}
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Section 4 : Comment ça marche (étapes)</h3>
            <div>
              <Label>Titre</Label>
              <input
                type="text"
                value={content.section4_title || ''}
                onChange={(e) => updateField('section4_title', e.target.value)}
                className="input-default"
              />
            </div>
            <div className="space-y-3">
              {content.section4_steps?.map((step, idx) => (
                <div key={idx} className="p-4 border border-[#E3DED5] rounded-lg bg-[#F9F6F0]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-[#A5BBA8] text-white text-sm flex items-center justify-center">{step.step}</span>
                    <input
                      type="text"
                      value={step.title || ''}
                      onChange={(e) => updateStep(idx, 'title', e.target.value)}
                      className="input-default flex-1"
                      placeholder="Titre de l'étape"
                    />
                  </div>
                  <input
                    type="text"
                    value={step.text || ''}
                    onChange={(e) => updateStep(idx, 'text', e.target.value)}
                    className="input-default"
                    placeholder="Description de l'étape"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'faq' && (
        <div className="space-y-6">
          <div className="card-default space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-[#2A312C]">Questions fréquentes</h3>
              <button onClick={addFAQ} className="btn-outline text-sm">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter une question
              </button>
            </div>
            
            <div>
              <Label>Titre de la section FAQ</Label>
              <input
                type="text"
                value={content.section6_title || ''}
                onChange={(e) => updateField('section6_title', e.target.value)}
                className="input-default"
              />
            </div>
            
            <div className="space-y-4">
              {content.faq_items?.map((item, idx) => (
                <div key={idx} className="p-4 border border-[#E3DED5] rounded-lg bg-[#F9F6F0]">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-[#5C6A61]">Question {idx + 1}</span>
                    <button 
                      onClick={() => removeFAQ(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={item.question || ''}
                      onChange={(e) => updateFAQ(idx, 'question', e.target.value)}
                      className="input-default"
                      placeholder="Question"
                    />
                    <textarea
                      value={item.answer || ''}
                      onChange={(e) => updateFAQ(idx, 'answer', e.target.value)}
                      className="input-default min-h-[80px]"
                      placeholder="Réponse"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'highlight' && (
        <div className="space-y-6">
          <div className="card-default space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-[#2A312C]">Encadré "À retenir"</h3>
              <button onClick={addHighlightItem} className="btn-outline text-sm">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un élément
              </button>
            </div>
            
            <div>
              <Label>Titre de l'encadré</Label>
              <input
                type="text"
                value={content.highlight_title || ''}
                onChange={(e) => updateField('highlight_title', e.target.value)}
                className="input-default"
              />
            </div>
            
            <div className="space-y-2">
              {content.highlight_items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#A5BBA8] flex-shrink-0" />
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateHighlightItem(idx, e.target.value)}
                    className="input-default flex-1"
                  />
                  <button 
                    onClick={() => removeHighlightItem(idx)}
                    className="p-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SEASONS MANAGEMENT - Menu Automation by Service Date
// =============================================================================

const SEASON_STATUS = {
  brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
  programme: { label: 'Programmé', color: 'bg-blue-100 text-blue-800' },
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  archive: { label: 'Archivé', color: 'bg-orange-100 text-orange-800' }
};

function SeasonsManagement() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSeasons();
      setSeasons(res.data || []);
    } catch (error) {
      console.error('Error loading seasons:', error);
      toast.error('Erreur lors du chargement des saisons');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (seasonId, data) => {
    try {
      await adminApi.updatePermanentSeason(seasonId, data);
      toast.success('Dates de la saison mises à jour !');
      setEditing(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="space-y-6" data-testid="seasons-management">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
          Saisons
        </h2>
        <p className="text-sm text-[#5C6A61]">
          4 saisons permanentes • dates en jour/mois, réutilisées automatiquement chaque année
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Fonctionnement
        </h3>
        <p className="text-sm text-blue-700 mt-2">
          Les recettes sont rattachées aux saisons dans leur formulaire. La saison courante est
          déterminée automatiquement à partir de la date du jour (jour/mois), sans année.
          L'hiver traverse correctement décembre → mars.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          Chargement...
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {seasons.map((season) => (
            <div key={season.season_id} className="card-default" data-testid={`season-card-${season.season_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Sun className="w-5 h-5 text-[#A5BBA8]" />
                  <h3 className="font-semibold text-[#2A312C] text-lg">{season.name}</h3>
                </div>
                <button
                  onClick={() => setEditing(season)}
                  className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                  title="Modifier les dates"
                  data-testid={`edit-season-${season.season_id}`}
                >
                  <Pencil className="w-4 h-4 text-[#5C6A61]" />
                </button>
              </div>
              <div className="mt-2 text-sm text-[#5C6A61]">
                <p data-testid={`season-period-${season.season_id}`}>📅 {season.period_label}</p>
                <p className="mt-1">{season.recipe_count || 0} recette(s) rattachée(s)</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Dates de la saison {editing?.name}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <PermanentSeasonForm
              season={editing}
              onSave={(data) => handleSave(editing.season_id, data)}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MONTH_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Éditeur des dates (jour/mois) d'une saison permanente
function PermanentSeasonForm({ season, onSave, onCancel }) {
  const [data, setData] = useState({
    start_day: season.start_day || 1,
    start_month: season.start_month || 1,
    end_day: season.end_day || 1,
    end_month: season.end_month || 1,
  });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setData(prev => ({ ...prev, [k]: parseInt(v) || 0 }));

  const submit = async () => {
    setSaving(true);
    try { await onSave(data); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="font-semibold">Début (jour / mois)</Label>
        <div className="flex gap-2 mt-1">
          <input type="number" min="1" max="31" value={data.start_day}
            onChange={(e) => upd('start_day', e.target.value)}
            className="input-default w-20" data-testid="input-start_day" />
          <select value={data.start_month} onChange={(e) => upd('start_month', e.target.value)}
            className="input-default flex-1" data-testid="input-start_month">
            {MONTH_LABELS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="font-semibold">Fin (jour / mois)</Label>
        <div className="flex gap-2 mt-1">
          <input type="number" min="1" max="31" value={data.end_day}
            onChange={(e) => upd('end_day', e.target.value)}
            className="input-default w-20" data-testid="input-end_day" />
          <select value={data.end_month} onChange={(e) => upd('end_month', e.target.value)}
            className="input-default flex-1" data-testid="input-end_month">
            {MONTH_LABELS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>
      <p className="text-xs text-[#5C6A61]">
        Astuce : pour l'Hiver, la fin (mars) peut être antérieure au début (décembre) — le système gère automatiquement le passage d'année.
      </p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[#E3DED5] text-[#5C6A61] hover:bg-[#F4F1EA] transition-colors" data-testid="cancel-season-btn">
          Annuler
        </button>
        <button onClick={submit} disabled={saving} className="btn-primary" data-testid="save-season-btn">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
          Valider
        </button>
      </div>
    </div>
  );
}

// Recipes Management
// SIMPLIFIED Recipe Categories
const RECIPE_CATEGORIES = {
  plat_mijote: 'Plat mijoté',
  plat_four: 'Plat au four',
  poisson: 'Poisson',
  soupe: 'Soupe',
  tarte: 'Tarte / Pizza',
  pates_gratin: 'Pâtes / Gratin',
  sauce: 'Sauce',
  dessert: 'Dessert',
  autre: 'Autre'
};

// SEASONS is imported from utils.js

function HomePreparationsManagement() {
  const [preps, setPreps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { try { const r = await adminApi.listHomePreparations(); setPreps(r.data || []); } catch (e) { console.error(e); } };
  useEffect(() => { load(); }, []);
  const handleSave = async (data) => {
    if (editing) await adminApi.updateHomePreparation(editing.preparation_id, data);
    else await adminApi.createHomePreparation(data);
    toast.success('Préparation enregistrée'); setDialogOpen(false); setEditing(null); load();
  };
  const handleDelete = async (p) => { if (!window.confirm(`Supprimer "${p.name}" ?`)) return; await adminApi.deleteHomePreparation(p.preparation_id); toast.success('Supprimée'); load(); };
  return (
    <div className="space-y-6" data-testid="home-preparations-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">Préparations maison</h2>
          <p className="text-sm text-[#5C6A61]">Préparations de base (ex. Béchamel) décomposées en ingrédients côté client.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" onClick={() => setEditing(null)} data-testid="add-home-prep-btn">
              <Plus className="w-5 h-5 mr-2" /> Nouvelle préparation
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Modifier' : 'Nouvelle'} préparation maison</DialogTitle></DialogHeader>
            <HomePreparationForm prep={editing} onSave={handleSave} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3">
        {preps.map(p => (
          <div key={p.preparation_id} className="card-default flex justify-between items-start" data-testid={`home-prep-${p.preparation_id}`}>
            <div>
              <h3 className="font-semibold text-[#2A312C]">{p.name} — référence {p.reference_quantity} {p.unit}</h3>
              <p className="text-sm text-[#5C6A61]">{(p.components || []).map(c => `${c.name} ${c.quantity} ${c.unit}`).join(' + ') || 'Aucun composant'}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-outline text-sm" onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-home-prep-${p.preparation_id}`}>Modifier</button>
              <button className="text-red-500 text-sm" onClick={() => handleDelete(p)} data-testid={`delete-home-prep-${p.preparation_id}`}>Supprimer</button>
            </div>
          </div>
        ))}
        {preps.length === 0 && <p className="text-sm text-[#5C6A61]">Aucune préparation maison. Créez par exemple la Béchamel (120 g = 10 g beurre + 10 g farine + 100 ml lait).</p>}
      </div>
    </div>
  );
}

function HomePreparationForm({ prep, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: prep?.name || '',
    reference_quantity: prep?.reference_quantity || 120,
    unit: prep?.unit || 'g',
    is_active: prep?.is_active !== false,
    components: prep?.components || [{ name: '', quantity: 0, unit: 'g' }]
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onSave({ ...formData, components: formData.components.filter(c => c.name.trim()) }); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div>
        <Label>Nom</Label>
        <input className="input-default" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Béchamel" data-testid="home-prep-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Quantité de référence</Label>
          <input type="number" className="input-default" value={formData.reference_quantity} onChange={(e) => setFormData({ ...formData, reference_quantity: parseFloat(e.target.value) || 0 })} data-testid="home-prep-refqty" /></div>
        <div><Label>Unité</Label>
          <input className="input-default" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} data-testid="home-prep-unit" /></div>
      </div>
      <div>
        <Label>Composants</Label>
        <div className="space-y-2">
          {formData.components.map((c, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <input className="input-default col-span-6 text-sm" placeholder="Ingrédient" value={c.name} onChange={(e) => { const l = [...formData.components]; l[idx] = { ...l[idx], name: e.target.value }; setFormData({ ...formData, components: l }); }} />
              <input className="input-default col-span-3 text-sm" placeholder="Qté" type="number" value={c.quantity} onChange={(e) => { const l = [...formData.components]; l[idx] = { ...l[idx], quantity: parseFloat(e.target.value) || 0 }; setFormData({ ...formData, components: l }); }} />
              <input className="input-default col-span-2 text-sm" placeholder="Unité" value={c.unit} onChange={(e) => { const l = [...formData.components]; l[idx] = { ...l[idx], unit: e.target.value }; setFormData({ ...formData, components: l }); }} />
              <button type="button" className="col-span-1 text-red-500" onClick={() => setFormData({ ...formData, components: formData.components.filter((_, i) => i !== idx) })}>✕</button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-outline mt-2 text-sm" onClick={() => setFormData({ ...formData, components: [...formData.components, { name: '', quantity: 0, unit: 'g' }] })} data-testid="add-component-btn">+ Composant</button>
      </div>
      <div className="flex justify-end gap-3">
        <button className="btn-outline" onClick={onCancel} disabled={saving}>Annuler</button>
        <button className="btn-primary" onClick={submit} disabled={saving} data-testid="save-home-prep-btn">{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
      </div>
    </div>
  );
}

function RecipesManagement() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [filter, setFilter] = useState({ category: '', status: '', season: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [seasonsList, setSeasonsList] = useState([]);

  useEffect(() => {
    loadData();
  }, [filter]);

  useEffect(() => {
    adminApi.getSeasons()
      .then(res => setSeasonsList(res.data || []))
      .catch(err => console.error('Error loading seasons:', err));
  }, []);

  const loadData = async () => {
    try {
      const response = await adminApi.getRecipes(filter);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    let rid = editingRecipe?.recipe_id;
    if (editingRecipe) {
      await adminApi.updateRecipe(editingRecipe.recipe_id, data);
      toast.success('Recette mise à jour !');
    } else {
      const res = await adminApi.createRecipe(data);
      rid = res.data?.recipe_id;
      toast.success('Recette créée !');
    }
    // Phase 1: persiste la structure d'ingrédients + base de portion via l'endpoint dédié
    if (rid) {
      await adminApi.updateRecipeStructure(rid, {
        reference_servings: data.reference_servings || 1,
        ingredients_struct: data.ingredients_struct || [],
        is_migrated: !!data.is_migrated,
      });
    }
    setDialogOpen(false);
    setEditingRecipe(null);
    loadData();
  };

  const handleDelete = async (recipe) => {
    if (!window.confirm(`Supprimer "${recipe.name}" ?`)) return;
    try {
      await adminApi.deleteRecipe(recipe.recipe_id);
      toast.success('Recette supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (recipe) => {
    try {
      await adminApi.duplicateRecipe(recipe.recipe_id);
      toast.success('Recette dupliquée !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleToggleStatus = async (recipe) => {
    const newStatus = recipe.status === 'actif' ? 'inactif' : 'actif';
    try {
      await adminApi.updateRecipe(recipe.recipe_id, { ...recipe, status: newStatus });
      toast.success(`Recette ${newStatus === 'actif' ? 'activée' : 'désactivée'}`);
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Filter recipes by search term and season
  const filteredRecipes = recipes.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeason = !filter.season || filter.season === '' ||
      r.season_ids?.includes(filter.season);
    return matchesSearch && matchesSeason;
  });

  // Count by status
  const activeCount = recipes.filter(r => r.status === 'actif').length;
  const inactiveCount = recipes.filter(r => r.status !== 'actif').length;

  return (
    <div className="space-y-6" data-testid="recipes-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Recettes
          </h2>
          <p className="text-sm text-[#5C6A61]">
            {activeCount} active(s) • {inactiveCount} inactive(s)
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button 
              className="btn-primary"
              onClick={() => setEditingRecipe(null)}
              data-testid="add-recipe-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle recette
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {editingRecipe ? 'Modifier la recette' : 'Nouvelle recette'}
              </DialogTitle>
            </DialogHeader>
            <SimpleRecipeForm 
              recipe={editingRecipe} 
              onSave={handleSave} 
              onCancel={() => setDialogOpen(false)} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Rechercher une recette..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-default"
            data-testid="recipes-search-input"
          />
        </div>
        <select
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          className="input-default w-auto"
          data-testid="recipes-filter-category"
        >
          <option value="">Toutes catégories</option>
          {Object.entries(RECIPE_CATEGORIES).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filter.season}
          onChange={(e) => setFilter({ ...filter, season: e.target.value })}
          className="input-default w-auto"
          data-testid="recipes-filter-season"
        >
          <option value="">Toutes saisons</option>
          {seasonsList.map((s) => (
            <option key={s.season_id} value={s.season_id}>{s.name}</option>
          ))}
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="input-default w-auto"
        >
          <option value="">Tous statuts</option>
          <option value="actif">Actif</option>
          <option value="inactif">Inactif</option>
        </select>
      </div>

      {/* Recipe List */}
      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : filteredRecipes.length === 0 ? (
        <div className="card-default text-center py-12">
          <ChefHat className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucune recette trouvée</p>
          <p className="text-sm text-[#5C6A61] mt-2">Cliquez sur "Nouvelle recette" pour en créer une</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecipes.map((recipe) => (
            <div key={recipe.recipe_id} className="card-default" data-testid={`recipe-card-${recipe.recipe_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-[#2A312C] text-lg">{recipe.name}</h3>
                    <Badge className={recipe.status === 'actif' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {recipe.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#5C6A61]">
                    <span>{RECIPE_CATEGORIES[recipe.category] || recipe.category}</span>
                    {recipe.servings && <span>• {recipe.servings} pers.</span>}
                    {(recipe.prep_time > 0 || recipe.cook_time > 0) && (
                      <span>• {recipe.prep_time + recipe.cook_time} min</span>
                    )}
                  </div>
                  {recipe.ingredients_text && (
                    <p className="text-xs text-[#5C6A61] mt-1 line-clamp-1">
                      {recipe.ingredients_text.split('\n').slice(0, 3).join(', ')}...
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleStatus(recipe)}
                    className={`p-2 rounded-lg transition-colors ${
                      recipe.status === 'actif' 
                        ? 'hover:bg-red-100 text-red-600' 
                        : 'hover:bg-green-100 text-green-600'
                    }`}
                    title={recipe.status === 'actif' ? 'Désactiver' : 'Activer'}
                  >
                    {recipe.status === 'actif' ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDuplicate(recipe)}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    title="Dupliquer"
                  >
                    <Copy className="w-4 h-4 text-[#5C6A61]" />
                  </button>
                  <button
                    onClick={() => { setEditingRecipe(recipe); setDialogOpen(true); }}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4 text-[#5C6A61]" />
                  </button>
                  <button
                    onClick={() => handleDelete(recipe)}
                    className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4 text-[#C86B51]" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// SIMPLIFIED Recipe Form - Now with dynamic seasons!
// Phase 1: assistant de conversion texte -> lignes structurées (pré-remplissage uniquement)
const CONVERT_KNOWN_UNITS = ['g','kg','mg','ml','cl','l','unite','unites','oeuf','oeufs','gousse','gousses','branche','branches','pincee','pincees','tranche','tranches','feuille','feuilles','filet','filets','cac','cas','cs','cc','sachet','sachets','pot','pots','verre','verres','boite','boites'];
const _normUnit = (s) => (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
function parseIngredientLineToStruct(raw) {
  const line = (raw || '').trim();
  if (!line) return null;
  // Format A : "Nom : 120 g" (séparateur : – - suivi d'un nombre)
  let m = line.match(/^(.+?)\s*[:–-]\s*(\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (m) {
    return { line_type: 'ingredient', name: m[1].trim(), quantity: parseFloat(m[2].replace(',', '.')), unit: (m[3] || '').trim() || null, scaling: 'proportional' };
  }
  // Format B : "120 g Nom" (quantité en tête)
  m = line.match(/^(\d+(?:[.,]\d+)?)\s+(\S+)?\s*(.*)$/);
  if (m) {
    const qty = parseFloat(m[1].replace(',', '.'));
    const tok = (m[2] || '').trim();
    const rest = (m[3] || '').trim();
    if (tok && CONVERT_KNOWN_UNITS.includes(_normUnit(tok))) {
      return { line_type: 'ingredient', name: rest || tok, quantity: qty, unit: tok, scaling: 'proportional' };
    }
    return { line_type: 'ingredient', name: [tok, rest].filter(Boolean).join(' ').trim(), quantity: qty, unit: null, scaling: 'proportional' };
  }
  // Séparateur sans nombre : "Poivre : selon goût"
  m = line.match(/^(.+?)\s*[:–-]\s*(.+)$/);
  if (m && !/^\d/.test(m[2].trim())) {
    return { line_type: 'ingredient', name: m[1].trim(), quantity: null, unit: null, scaling: 'proportional', qualitative_note: m[2].trim() };
  }
  // Aucun chiffre : qualitatif (Poivre, titres de section à reclasser…)
  if (!/\d/.test(line)) {
    return { line_type: 'ingredient', name: line, quantity: null, unit: null, scaling: 'proportional' };
  }
  // Contient un chiffre mais format non reconnu : à vérifier, sans quantité inventée
  return { line_type: 'ingredient', name: line, quantity: null, unit: null, scaling: 'proportional', qualitative_note: 'à vérifier' };
}
function parseIngredientsTextToStruct(text) {
  return (text || '').split('\n').map(parseIngredientLineToStruct).filter(Boolean);
}

function SimpleRecipeForm({ recipe, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: recipe?.name || '',
    ingredients_text: recipe?.ingredients_text || '',
    method: recipe?.method || '',
    category: recipe?.category || 'plat_mijote',
    season: recipe?.season || [],  // Legacy field (inactif ; season_ids fait foi)
    season_ids: recipe?.season_ids || [],   // New: dynamic season IDs
    servings: recipe?.servings || 4,
    prep_time: recipe?.prep_time || 0,
    cook_time: recipe?.cook_time || 0,
    status: recipe?.status || 'actif',
    image_url: recipe?.image_url || '',
    reference_servings: recipe?.reference_servings || 1,
    ingredients_struct: recipe?.ingredients_struct || [],
    is_migrated: recipe?.is_migrated || false
  });
  const [homePreps, setHomePreps] = useState([]);
  useEffect(() => {
    adminApi.listHomePreparations().then(r => setHomePreps(r.data || [])).catch(() => {});
  }, []);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Load available seasons on mount
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        const res = await adminApi.getAvailableSeasons();
        setAvailableSeasons(res.data || []);
      } catch (error) {
        console.error('Error loading seasons:', error);
      } finally {
        setLoadingSeasons(false);
      }
    };
    loadSeasons();
  }, []);

  const toggleSeasonId = (seasonId) => {
    let newSeasonIds = [...(formData.season_ids || [])];
    if (newSeasonIds.includes(seasonId)) {
      newSeasonIds = newSeasonIds.filter(id => id !== seasonId);
    } else {
      newSeasonIds = [...newSeasonIds, seasonId];
    }
    setFormData({ ...formData, season_ids: newSeasonIds });
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (!formData.name.trim()) {
      setSubmitError('Le nom de la recette est requis.');
      return;
    }
    // Warning if no season selected
    if (formData.season_ids.length === 0 && availableSeasons.length > 0) {
      if (!window.confirm('Attention : cette recette n\'est associée à aucune saison et ne sera pas visible par les clients. Continuer ?')) {
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI validation errors (422): show the concerned fields
        message = detail.map(d => {
          const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : '';
          return field ? `${field} : ${d.msg}` : d.msg;
        }).join(' • ');
      } else {
        message = "Échec de l'enregistrement. Vérifiez les champs et réessayez.";
      }
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Nom - PRINCIPAL */}
      <div>
        <Label className="text-base font-semibold">Nom de la recette *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default text-lg"
          placeholder="Ex: Poulet citron"
          autoFocus
          data-testid="recipe-name-input"
        />
      </div>

      {/* Catégorie */}
      <div>
        <Label className="text-base font-semibold">Catégorie</Label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="input-default"
        >
          {Object.entries(RECIPE_CATEGORIES).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>
      
      {/* Saisons dynamiques */}
      <div>
        <Label className="text-base font-semibold">Saison(s) *</Label>
        <p className="text-xs text-[#5C6A61] mb-2">
          Sélectionnez les saisons où cette recette sera disponible.
          Une recette sans saison ne sera jamais visible par les clients.
        </p>
        {loadingSeasons ? (
          <div className="flex items-center gap-2 text-sm text-[#5C6A61]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Chargement des saisons...
          </div>
        ) : availableSeasons.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Aucune saison n'a été créée. Créez d'abord des saisons dans le menu "Saisons & Menus".
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableSeasons.map((season) => (
              <button
                key={season.season_id}
                type="button"
                onClick={() => toggleSeasonId(season.season_id)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  formData.season_ids?.includes(season.season_id)
                    ? 'border-[#A5BBA8] bg-[#A5BBA8] text-white'
                    : 'border-[#E3DED5] bg-white text-[#5C6A61] hover:border-[#A5BBA8]'
                }`}
                data-testid={`season-toggle-${season.season_id}`}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium">{season.name}</span>
                  <span className="text-xs opacity-75">
                    {season.period_label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
        {formData.season_ids?.length === 0 && availableSeasons.length > 0 && (
          <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Cette recette ne sera visible dans aucune saison
          </p>
        )}
      </div>

      {/* Ingrédients - PRINCIPAL */}
      <div>
        <Label className="text-base font-semibold">Ingrédients</Label>
        <p className="text-xs text-[#5C6A61] mb-2">Un ingrédient par ligne (ex: 4 filets de poulet)</p>
        <textarea
          value={formData.ingredients_text}
          onChange={(e) => setFormData({ ...formData, ingredients_text: e.target.value })}
          className="input-default min-h-[150px] font-mono text-sm"
          placeholder="4 filets de poulet
2 citrons
1 oignon
20 cl crème fraîche
sel, poivre"
          data-testid="recipe-ingredients-input"
        />
        <button
          type="button"
          className="btn-outline mt-2 text-sm"
          data-testid="convert-ingredients-btn"
          onClick={() => {
            const parsed = parseIngredientsTextToStruct(formData.ingredients_text);
            if (!parsed.length) { toast.error('Aucun ingrédient à convertir.'); return; }
            if ((formData.ingredients_struct || []).length > 0 &&
                !window.confirm('Des lignes structurées existent déjà. Les remplacer par la proposition issue du texte ?')) {
              return;
            }
            setFormData({ ...formData, ingredients_struct: parsed });
            toast.success(`${parsed.length} ligne(s) pré-remplie(s). Vérifiez puis cochez « Recette migrée et validée ».`);
          }}
        >
          Convertir en ingrédients structurés
        </button>
      </div>

      {/* Méthode - PRINCIPAL */}
      <div>
        <Label className="text-base font-semibold">Préparation</Label>
        <textarea
          value={formData.method}
          onChange={(e) => setFormData({ ...formData, method: e.target.value })}
          className="input-default min-h-[120px]"
          placeholder="Faire revenir l'oignon émincé dans un peu d'huile. Ajouter les filets de poulet et les faire dorer. Ajouter le jus des citrons et la crème..."
          data-testid="recipe-method-input"
        />
      </div>

      {/* Phase 1: Ingrédients structurés (moteur unique) */}
      <div className="border border-[#E3DED5] rounded-lg p-4 bg-white" data-testid="recipe-struct-editor">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-base font-semibold">Ingrédients structurés (moteur de calcul)</Label>
          {!formData.is_migrated && (
            <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700" data-testid="recipe-a-migrer-badge">À migrer</span>
          )}
        </div>
        <p className="text-xs text-[#5C6A61] mb-3">Base de portion + type de chaque ligne. Utilisé uniquement après bascule du moteur. Laissez la quantité vide pour un ingrédient qualitatif (sel, poivre…).</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-sm">Portions de référence (capacité d'un lot)</Label>
            <input type="number" min={1} value={formData.reference_servings}
              onChange={(e) => setFormData({ ...formData, reference_servings: parseInt(e.target.value) || 1 })}
              className="input-default" data-testid="recipe-reference-servings" />
          </div>
          <label className="flex items-center gap-2 sm:mt-7 cursor-pointer">
            <input type="checkbox" checked={formData.is_migrated}
              onChange={(e) => setFormData({ ...formData, is_migrated: e.target.checked })}
              data-testid="recipe-is-migrated" />
            <span className="text-sm">Recette migrée et validée</span>
          </label>
        </div>
        <div className="space-y-2">
          {(formData.ingredients_struct || []).map((line, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <select className="input-default col-span-2 text-xs" value={line.line_type}
                onChange={(e) => { const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], line_type: e.target.value }; setFormData({ ...formData, ingredients_struct: l }); }}>
                <option value="ingredient">Ingrédient</option>
                <option value="preparation">Préparation</option>
                <option value="section">Titre section</option>
              </select>
              {line.line_type === 'preparation' ? (
                <select className="input-default col-span-3 text-xs" value={line.preparation_id || ''}
                  onChange={(e) => { const p = homePreps.find(h => h.preparation_id === e.target.value); const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], preparation_id: e.target.value, name: p ? p.name : l[idx].name }; setFormData({ ...formData, ingredients_struct: l }); }}>
                  <option value="">— préparation —</option>
                  {homePreps.map(h => <option key={h.preparation_id} value={h.preparation_id}>{h.name}</option>)}
                </select>
              ) : (
                <input className="input-default col-span-3 text-xs" placeholder="Nom" value={line.name || ''}
                  onChange={(e) => { const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], name: e.target.value }; setFormData({ ...formData, ingredients_struct: l }); }} />
              )}
              {line.line_type !== 'section' && (<>
                <input className="input-default col-span-1 text-xs" placeholder="Qté" value={line.quantity ?? ''}
                  onChange={(e) => { const v = e.target.value; const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], quantity: v === '' ? null : parseFloat(v.replace(',', '.')) }; setFormData({ ...formData, ingredients_struct: l }); }} />
                <input className="input-default col-span-1 text-xs" placeholder="Unité" value={line.unit || ''}
                  onChange={(e) => { const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], unit: e.target.value }; setFormData({ ...formData, ingredients_struct: l }); }} />
                <select className="input-default col-span-2 text-xs" value={line.scaling || 'proportional'}
                  onChange={(e) => { const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], scaling: e.target.value }; setFormData({ ...formData, ingredients_struct: l }); }}>
                  <option value="proportional">Proportionnel</option>
                  <option value="per_batch">Par lot</option>
                </select>
                <input className="input-default col-span-2 text-xs" placeholder="Note (qualitatif)" value={line.qualitative_note || ''}
                  onChange={(e) => { const l = [...formData.ingredients_struct]; l[idx] = { ...l[idx], qualitative_note: e.target.value }; setFormData({ ...formData, ingredients_struct: l }); }} />
              </>)}
              <button type="button" className="col-span-1 text-red-500 text-sm"
                onClick={() => { const l = formData.ingredients_struct.filter((_, i) => i !== idx); setFormData({ ...formData, ingredients_struct: l }); }}>✕</button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-outline mt-3 text-sm" data-testid="add-struct-line-btn"
          onClick={() => setFormData({ ...formData, ingredients_struct: [...(formData.ingredients_struct || []), { line_type: 'ingredient', name: '', quantity: null, unit: 'g', scaling: 'proportional' }] })}>
          + Ajouter une ligne
        </button>
      </div>

      {/* Options secondaires - Collapsible */}
      <details className="bg-[#F9F6F0] rounded-lg p-4">
        <summary className="cursor-pointer text-[#5C6A61] font-medium">
          Options supplémentaires
        </summary>
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Portions</Label>
            <input
              type="number"
              value={formData.servings}
              onChange={(e) => setFormData({ ...formData, servings: parseInt(e.target.value) || 4 })}
              className="input-default"
              min={1}
              max={20}
            />
          </div>
          <div>
            <Label>Préparation (min)</Label>
            <input
              type="number"
              value={formData.prep_time}
              onChange={(e) => setFormData({ ...formData, prep_time: parseInt(e.target.value) || 0 })}
              className="input-default"
              min={0}
            />
          </div>
          <div>
            <Label>Cuisson (min)</Label>
            <input
              type="number"
              value={formData.cook_time}
              onChange={(e) => setFormData({ ...formData, cook_time: parseInt(e.target.value) || 0 })}
              className="input-default"
              min={0}
            />
          </div>
        </div>
        <div className="mt-4">
          <Label>Statut</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={formData.status === 'actif'}
                onChange={() => setFormData({ ...formData, status: 'actif' })}
                className="text-[#A5BBA8]"
              />
              <span className="text-sm">Actif</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={formData.status === 'inactif'}
                onChange={() => setFormData({ ...formData, status: 'inactif' })}
              />
              <span className="text-sm">Inactif</span>
            </label>
          </div>
        </div>
      </details>

      {/* Error banner - keeps data & window open, allows retry */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="recipe-form-error">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{submitError}</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline" disabled={submitting}>
          Annuler
        </button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          disabled={submitting}
          data-testid="save-recipe-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Check className="w-5 h-5 mr-2" />
              Enregistrer
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Menus Management with Categories
// TODO: Implement MenusManagement component

// =============================================================================
// INGREDIENTS MANAGEMENT (Legacy - kept for reference)
// =============================================================================

function IngredientsManagement() {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  
  useEffect(() => {
    loadIngredients();
  }, []);
  
  const loadIngredients = async () => {
    try {
      const response = await adminApi.getIngredients();
      setIngredients(response.data);
    } catch (error) {
      console.error('Error loading ingredients:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async (data) => {
    try {
      if (editingIngredient) {
        await adminApi.updateIngredient(editingIngredient.ingredient_id, data);
        toast.success('Ingrédient mis à jour');
      } else {
        await adminApi.createIngredient(data);
        toast.success('Ingrédient créé');
      }
      setDialogOpen(false);
      setEditingIngredient(null);
      loadIngredients();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (ingredient) => {
    if (!window.confirm('Supprimer cet ingrédient ?')) return;
    try {
      await adminApi.deleteIngredient(ingredient.ingredient_id);
      toast.success('Ingrédient supprimé');
      loadIngredients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="ingredients-management">
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button 
              className="btn-primary"
              onClick={() => setEditingIngredient(null)}
              data-testid="add-ingredient-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel ingrédient
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingIngredient ? 'Modifier l\'ingrédient' : 'Nouvel ingrédient'}</DialogTitle>
            </DialogHeader>
            <IngredientForm 
              ingredient={editingIngredient}
              onSave={handleSave} 
              onCancel={() => setDialogOpen(false)} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : (
        <div className="card-default overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E3DED5]">
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Nom</th>
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Catégorie</th>
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Qté/pers</th>
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Unité</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ingredient) => (
                <tr key={ingredient.ingredient_id} className="border-b border-[#E3DED5] last:border-0">
                  <td className="py-3 px-4 text-[#2A312C]">{ingredient.name}</td>
                  <td className="py-3 px-4 text-[#5C6A61]">{INGREDIENT_CATEGORIES[ingredient.category] || ingredient.category}</td>
                  <td className="py-3 px-4 text-[#5C6A61]">{ingredient.quantity_per_person}</td>
                  <td className="py-3 px-4 text-[#5C6A61]">{ingredient.unit}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => { setEditingIngredient(ingredient); setDialogOpen(true); }}
                      className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors mr-1"
                    >
                      <Pencil className="w-4 h-4 text-[#5C6A61]" />
                    </button>
                    <button
                      onClick={() => handleDelete(ingredient)}
                      className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-[#C86B51]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IngredientForm({ ingredient, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: ingredient?.name || '',
    category: ingredient?.category || 'legumes',
    unit: ingredient?.unit || 'g',
    quantity_per_person: ingredient?.quantity_per_person || 0,
    is_fresh: ingredient?.is_fresh ?? true,
    is_frozen: ingredient?.is_frozen ?? false,
    is_canned: ingredient?.is_canned ?? false,
    notes: ingredient?.notes || ''
  });

  return (
    <div className="space-y-4">
      <div>
        <Label>Nom *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          data-testid="ingredient-name-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Catégorie</Label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="input-default"
          >
            {Object.entries(INGREDIENT_CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Unité</Label>
          <select
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="input-default"
          >
            {Object.entries(UNITS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label>Quantité par personne</Label>
        <input
          type="number"
          value={formData.quantity_per_person}
          onChange={(e) => setFormData({ ...formData, quantity_per_person: parseFloat(e.target.value) || 0 })}
          className="input-default"
        />
      </div>
      <div>
        <Label>Notes</Label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="input-default min-h-[80px]"
          placeholder="Conseils d'achat, préférences..."
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={() => onSave(formData)} 
          className="btn-primary"
          data-testid="save-ingredient-btn"
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// Placeholder components for other management pages
function MenusManagement() {
  const [menus, setMenus] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [menusRes, recipesRes] = await Promise.all([
        adminApi.getMenus(),
        adminApi.getRecipes({})
      ]);
      setMenus(menusRes.data);
      setRecipes(recipesRes.data.filter(r => r.status === 'actif'));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingMenu) {
        await adminApi.updateMenu(editingMenu.menu_id, data);
        toast.success('Menu mis à jour !');
      } else {
        await adminApi.createMenu(data);
        toast.success('Menu créé !');
      }
      setDialogOpen(false);
      setEditingMenu(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDuplicate = async (menu) => {
    try {
      await adminApi.duplicateMenu(menu.menu_id);
      toast.success('Menu dupliqué');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (menu) => {
    if (!window.confirm(`Supprimer le menu "${menu.name}" ?`)) return;
    try {
      await adminApi.deleteMenu(menu.menu_id);
      toast.success('Menu supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'actif':
        return <Badge className="bg-green-100 text-green-800">Actif</Badge>;
      case 'inactif':
        return <Badge className="bg-gray-100 text-gray-800">Inactif</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">Brouillon</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="menus-management">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Menus de saison
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Un menu = un nom, une saison, une description et un statut. La composition est automatique.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button 
              className="btn-primary" 
              onClick={() => setEditingMenu(null)}
              data-testid="add-menu-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouveau menu
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMenu ? 'Modifier le menu' : 'Nouveau menu'}</DialogTitle>
            </DialogHeader>
            <MenuFormWithCategories 
              menu={editingMenu} 
              recipes={recipes}
              onSave={handleSave} 
              onCancel={() => setDialogOpen(false)} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : menus.length === 0 ? (
        <div className="card-default text-center py-12">
          <Utensils className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun menu créé</p>
          <p className="text-sm text-[#5C6A61] mt-2">
            Les menus regroupent des recettes par catégories avec des choix multiples
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {menus.map((menu) => (
            <div key={menu.menu_id} className="card-default" data-testid={`menu-card-${menu.menu_id}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  {getStatusBadge(menu.status)}
                  {menu.season && (
                    <Badge className="bg-blue-100 text-blue-800">
                      {SEASONS[menu.season] || menu.season}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditingMenu(menu); setDialogOpen(true); }}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4 text-[#5C6A61]" />
                  </button>
                  <button
                    onClick={() => handleDuplicate(menu)}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    title="Dupliquer"
                  >
                    <Copy className="w-4 h-4 text-[#5C6A61]" />
                  </button>
                  <button
                    onClick={() => handleDelete(menu)}
                    className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4 text-[#C86B51]" />
                  </button>
                </div>
              </div>
              
              <h3 className="font-semibold text-[#2A312C] text-lg mb-1">{menu.name}</h3>
              {menu.description && (
                <p className="text-sm text-[#5C6A61] mb-3">{menu.description}</p>
              )}

              <p className="text-xs text-[#5C6A61] mt-3">
                Composition automatique via la structure des repas et les recettes de la saison.
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Simplified Menu Form: only name, season, description, status.
// Legacy menu.categories are preserved (passed through) but no longer editable here,
// as composition is now driven by the permanent meal structure.
function MenuFormWithCategories({ menu, recipes, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: menu?.name || '',
    description: menu?.description || '',
    categories: menu?.categories || [],  // legacy conservé, non éditable
    status: menu?.status || 'brouillon',
    season: menu?.season || ''
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du menu est requis');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-6">
      {/* Basic info */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold">Nom du menu *</Label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input-default"
            placeholder="Ex: Menu été, Menu automne..."
            autoFocus
            data-testid="menu-name-input"
          />
        </div>
        <div>
          <Label>Saison</Label>
          <select
            value={formData.season}
            onChange={(e) => setFormData({ ...formData, season: e.target.value })}
            className="input-default"
            data-testid="menu-season-select"
          >
            <option value="">Toutes saisons</option>
            {Object.entries(SEASONS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-default min-h-[60px]"
          placeholder="Description optionnelle du menu..."
          data-testid="menu-description-input"
        />
      </div>

      <div className="bg-[#F9F6F0] border border-[#E3DED5] rounded-lg p-4 text-sm text-[#5C6A61]">
        La composition du menu (catégories et recettes proposées au client) est désormais
        gérée automatiquement à partir de la <span className="font-medium">Structure des repas</span> et
        des recettes actives rattachées à la saison. Il n'y a plus rien à configurer ici.
      </div>

      {/* Status */}
      <div>
        <Label className="text-base font-semibold mb-2 block">Statut</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={formData.status === 'brouillon'}
              onChange={() => setFormData({ ...formData, status: 'brouillon' })}
            />
            <span className="text-sm">Brouillon</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={formData.status === 'actif'}
              onChange={() => setFormData({ ...formData, status: 'actif' })}
            />
            <span className="text-sm">Actif</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={formData.status === 'inactif'}
              onChange={() => setFormData({ ...formData, status: 'inactif' })}
            />
            <span className="text-sm">Inactif</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-[#E3DED5]">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          data-testid="save-menu-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// Category Form (nested in Menu Form)
function CategoryForm({ category, recipes, recipesByCategory, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    category_id: category?.category_id || '',
    name: category?.name || '',
    recipe_ids: category?.recipe_ids || [],  // legacy conservé (non édité)
    recipe_categories: category?.recipe_categories || [],
    max_choices: category?.max_choices || 1,
    category_type: category?.category_type || 'other'
  });

  const toggleRecipeCategory = (key) => {
    const cur = formData.recipe_categories || [];
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    setFormData({ ...formData, recipe_categories: next });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }
    if ((formData.recipe_categories || []).length === 0) {
      toast.error('Sélectionnez au moins une famille de recettes');
      return;
    }
    onSave(formData);
  };

  // Suggested category names based on recipe categories
  const suggestedNames = [
    'Plat mijoté', 'Plat au four', 'Poisson', 'Soupe', 
    'Tarte / Pizza', 'Pâtes / Gratin', 'Sauce', 'Accompagnement'
  ];

  return (
    <div className="space-y-5">
      {/* Category name */}
      <div>
        <Label className="text-base font-semibold">Nom de la catégorie *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          placeholder="Ex: Plat mijoté, Soupe..."
          autoFocus
          data-testid="category-name-input"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {suggestedNames.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => setFormData({ ...formData, name })}
              className="text-xs px-2 py-1 rounded bg-[#F9F6F0] text-[#5C6A61] hover:bg-[#A5BBA8]/20"
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Category technical type (stable, independent of label) */}
      <div>
        <Label className="text-base font-semibold">Type technique</Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Utilisé par le tunnel (règle « sans poisson »). Indépendant du libellé affiché.
        </p>
        <select
          value={formData.category_type}
          onChange={(e) => setFormData({ ...formData, category_type: e.target.value })}
          className="input-default"
          data-testid="category-type-select"
        >
          <option value="other">Autre</option>
          <option value="fish">Poisson</option>
          <option value="simmered">Plat mijoté</option>
          <option value="oven">Plat au four</option>
        </select>
        <p className="text-xs text-[#5C6A61] mt-1">
          Si « Je ne souhaite pas de poisson » est choisi, le remplacement se fait uniquement parmi les catégories « Plat mijoté » ou « Plat au four ».
        </p>
      </div>

      {/* Max choices */}
      <div>
        <Label className="text-base font-semibold">Nombre de choix client</Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Combien de recettes le client peut-il choisir dans cette catégorie ?
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={formData.max_choices}
            onChange={(e) => setFormData({ ...formData, max_choices: Math.max(1, parseInt(e.target.value) || 1) })}
            className="input-default w-24 text-center"
            min={1}
            max={10}
            data-testid="max-choices-input"
          />
          <span className="text-sm text-[#5C6A61]">
            recette(s) par famille configurée
          </span>
        </div>
      </div>

      {/* Familles de recettes (règle technique stable, indépendante du libellé) */}
      <div>
        <Label className="text-base font-semibold">
          Familles de recettes ({(formData.recipe_categories || []).length} sélectionnée(s))
        </Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Les recettes actives de ces familles, rattachées à la saison en cours, apparaîtront
          automatiquement. Plus besoin de sélectionner les recettes une par une, ni de modifier
          le menu à chaque nouvelle recette.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {Object.entries(RECIPE_CATEGORIES).map(([key, label]) => (
            <label
              key={key}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                (formData.recipe_categories || []).includes(key)
                  ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                  : 'border-[#E3DED5] hover:border-[#A5BBA8]'
              }`}
              data-testid={`recipe-category-${key}`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                (formData.recipe_categories || []).includes(key)
                  ? 'bg-[#A5BBA8] border-[#A5BBA8]'
                  : 'border-[#E3DED5]'
              }`}>
                {(formData.recipe_categories || []).includes(key) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-sm text-[#2A312C]">{label}</span>
              <input
                type="checkbox"
                checked={(formData.recipe_categories || []).includes(key)}
                onChange={() => toggleRecipeCategory(key)}
                className="sr-only"
              />
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          data-testid="save-category-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Valider
        </button>
      </div>
    </div>
  );
}

// Keep old MenuForm for backward compatibility (hidden)
function MenuForm({ menu, recipes, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: menu?.name || '',
    description: menu?.description || '',
    recipe_ids: menu?.recipe_ids || [],
    is_active: menu?.is_active ?? true
  });

  const toggleRecipe = (recipeId) => {
    const newRecipes = formData.recipe_ids.includes(recipeId)
      ? formData.recipe_ids.filter(id => id !== recipeId)
      : [...formData.recipe_ids, recipeId];
    setFormData({ ...formData, recipe_ids: newRecipes });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du menu est requis');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-base font-semibold">Nom du menu *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          placeholder="Ex: Menu découverte, Menu végétarien..."
          autoFocus
          data-testid="menu-name-input"
        />
      </div>

      <div>
        <Label>Description</Label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-default min-h-[80px]"
          placeholder="Description du menu..."
        />
      </div>

      <div>
        <Label className="text-base font-semibold">Recettes ({formData.recipe_ids.length} sélectionnée(s))</Label>
        {recipes.length === 0 ? (
          <p className="text-sm text-[#C86B51] mt-2">
            Aucune recette active. Créez d'abord des recettes.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-[300px] overflow-y-auto">
            {recipes.map(recipe => (
              <label 
                key={recipe.recipe_id} 
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.recipe_ids.includes(recipe.recipe_id)
                    ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                    : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  formData.recipe_ids.includes(recipe.recipe_id)
                    ? 'bg-[#A5BBA8] border-[#A5BBA8]'
                    : 'border-[#E3DED5]'
                }`}>
                  {formData.recipe_ids.includes(recipe.recipe_id) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-sm text-[#2A312C]">{recipe.name}</span>
                <input
                  type="checkbox"
                  checked={formData.recipe_ids.includes(recipe.recipe_id)}
                  onChange={() => toggleRecipe(recipe.recipe_id)}
                  className="sr-only"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox 
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
          id="menu-active"
        />
        <Label htmlFor="menu-active">Menu actif</Label>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          data-testid="save-menu-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Structure permanente des repas ─────────────────────────────────────────
const MEAL_CATEGORY_TYPES = {
  other: 'Autre',
  fish: 'Poisson',
  simmered: 'Plat mijoté',
  oven: 'Plat au four',
};

function MealStructureManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await adminApi.getMealStructure();
      const cats = (res.data?.categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
    } catch (error) {
      console.error('Error loading meal structure:', error);
      toast.error('Erreur lors du chargement de la structure');
    } finally {
      setLoading(false);
    }
  };

  const persist = async (cats) => {
    setSaving(true);
    try {
      const payload = cats.map((c, i) => ({
        category_id: c.category_id || undefined,
        name: c.name,
        recipe_categories: c.recipe_categories || [],
        category_type: c.category_type || 'other',
        max_choices: c.category_type === 'fish' ? 1 : (c.max_choices || 1),
        order: i,
        is_active: c.is_active !== false,
      }));
      const res = await adminApi.updateMealStructure(payload);
      const saved = (res.data?.categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(saved);
      toast.success('Structure enregistrée');
    } catch (error) {
      console.error('Error saving meal structure:', error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setEditingIndex(-1);
    setDialogOpen(true);
  };

  const handleEdit = (cat, index) => {
    setEditing(cat);
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSaveCategory = (data) => {
    const next = [...categories];
    if (editingIndex >= 0) {
      next[editingIndex] = { ...next[editingIndex], ...data };
    } else {
      next.push(data);
    }
    setDialogOpen(false);
    persist(next);
  };

  const handleToggleActive = (index) => {
    const next = categories.map((c, i) => i === index ? { ...c, is_active: !(c.is_active !== false) } : c);
    persist(next);
  };

  const handleDelete = (index) => {
    if (!window.confirm('Supprimer définitivement ce type de plat de la structure ?')) return;
    const next = categories.filter((_, i) => i !== index);
    persist(next);
  };

  const move = (index, direction) => {
    const next = [...categories];
    const ni = index + direction;
    if (ni < 0 || ni >= next.length) return;
    [next[index], next[ni]] = [next[ni], next[index]];
    persist(next);
  };

  return (
    <div className="space-y-6" data-testid="meal-structure-management">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Structure des repas
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Définit une seule fois les types de plats proposés au client (ex : « Plat mijoté », « Poisson »).
            Cette structure s'applique à toutes les saisons. Les recettes actives de la saison remplissent
            automatiquement chaque type.
          </p>
        </div>
        <button className="btn-primary self-start" onClick={handleAdd} data-testid="add-meal-type-btn">
          <Plus className="w-5 h-5 mr-2" />
          Ajouter un type de plat
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : categories.length === 0 ? (
        <div className="card-default text-center py-12">
          <Utensils className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun type de plat défini</p>
          <p className="text-sm text-[#5C6A61] mt-2">
            Ajoutez les types de plats que le client verra dans son menu.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((cat, index) => (
            <div
              key={cat.category_id || index}
              className={`card-default ${cat.is_active === false ? 'opacity-60' : ''}`}
              data-testid={`meal-type-card-${index}`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-[#2A312C] text-lg">{cat.name}</h3>
                    <Badge className="bg-blue-100 text-blue-800">
                      {MEAL_CATEGORY_TYPES[cat.category_type] || cat.category_type}
                    </Badge>
                    {cat.is_active === false ? (
                      <Badge className="bg-gray-100 text-gray-800">Inactif</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">Actif</Badge>
                    )}
                  </div>
                  <p className="text-sm text-[#5C6A61]">
                    Le client choisit <span className="font-medium">{cat.max_choices || 1}</span> recette(s).
                  </p>
                  <p className="text-sm text-[#5C6A61] mt-1">
                    Familles de recettes :{' '}
                    <span className="font-medium">
                      {(cat.recipe_categories || []).map(k => RECIPE_CATEGORIES[k] || k).join(', ') || '—'}
                    </span>
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || saving}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors disabled:opacity-30"
                    title="Monter"
                    data-testid={`meal-type-up-${index}`}
                  >
                    <ChevronRight className="w-4 h-4 text-[#5C6A61] -rotate-90" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === categories.length - 1 || saving}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors disabled:opacity-30"
                    title="Descendre"
                    data-testid={`meal-type-down-${index}`}
                  >
                    <ChevronRight className="w-4 h-4 text-[#5C6A61] rotate-90" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(index)}
                    disabled={saving}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors text-xs text-[#5C6A61]"
                    title={cat.is_active === false ? 'Activer' : 'Désactiver'}
                    data-testid={`meal-type-toggle-${index}`}
                  >
                    {cat.is_active === false ? 'Activer' : 'Désactiver'}
                  </button>
                  <button
                    onClick={() => handleEdit(cat, index)}
                    disabled={saving}
                    className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    title="Modifier"
                    data-testid={`meal-type-edit-${index}`}
                  >
                    <Pencil className="w-4 h-4 text-[#5C6A61]" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    disabled={saving}
                    className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    title="Supprimer"
                    data-testid={`meal-type-delete-${index}`}
                  >
                    <Trash2 className="w-4 h-4 text-[#C86B51]" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le type de plat' : 'Nouveau type de plat'}</DialogTitle>
          </DialogHeader>
          <MealTypeForm
            category={editing}
            onSave={handleSaveCategory}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MealTypeForm({ category, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    category_id: category?.category_id || '',  // clé technique stable, jamais modifiée par l'admin
    name: category?.name || '',
    recipe_categories: category?.recipe_categories || [],
    category_type: category?.category_type || 'other',
    max_choices: category?.max_choices || 1,
    is_active: category?.is_active !== false,
  });

  const isFish = formData.category_type === 'fish';

  const toggleRecipeCategory = (key) => {
    const cur = formData.recipe_categories || [];
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    setFormData({ ...formData, recipe_categories: next });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom visible est requis');
      return;
    }
    if ((formData.recipe_categories || []).length === 0) {
      toast.error('Sélectionnez au moins une famille de recettes');
      return;
    }
    onSave({
      ...formData,
      max_choices: isFish ? 1 : Math.max(1, parseInt(formData.max_choices) || 1),
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-base font-semibold">Nom visible par le client *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          placeholder="Ex : Plat mijoté, Poisson du jour..."
          autoFocus
          data-testid="meal-type-name-input"
        />
        <p className="text-xs text-[#5C6A61] mt-1">
          Vous pouvez renommer ce type quand vous voulez, cela ne perturbe pas le classement interne.
        </p>
      </div>

      <div>
        <Label className="text-base font-semibold">Type technique</Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Sert à la règle « sans poisson » du tunnel client. Indépendant du nom affiché.
        </p>
        <select
          value={formData.category_type}
          onChange={(e) => {
            const ct = e.target.value;
            setFormData({ ...formData, category_type: ct, max_choices: ct === 'fish' ? 1 : formData.max_choices });
          }}
          className="input-default"
          data-testid="meal-type-type-select"
        >
          {Object.entries(MEAL_CATEGORY_TYPES).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <Label className="text-base font-semibold">Nombre de choix client</Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Combien de recettes le client peut-il choisir pour ce type ?
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={isFish ? 1 : formData.max_choices}
            onChange={(e) => setFormData({ ...formData, max_choices: Math.max(1, parseInt(e.target.value) || 1) })}
            className="input-default w-24 text-center"
            min={1}
            max={10}
            disabled={isFish}
            data-testid="meal-type-max-choices-input"
          />
          <span className="text-sm text-[#5C6A61]">recette(s)</span>
        </div>
        {isFish && (
          <p className="text-xs text-[#C86B51] mt-1">
            Pour un type « Poisson », le nombre de choix est fixé à 1.
          </p>
        )}
      </div>

      <div>
        <Label className="text-base font-semibold">
          Familles de recettes ({(formData.recipe_categories || []).length} sélectionnée(s))
        </Label>
        <p className="text-sm text-[#5C6A61] mb-2">
          Les recettes actives de ces familles, rattachées à la saison affichée, apparaîtront automatiquement.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {Object.entries(RECIPE_CATEGORIES).map(([key, label]) => (
            <label
              key={key}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                (formData.recipe_categories || []).includes(key)
                  ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                  : 'border-[#E3DED5] hover:border-[#A5BBA8]'
              }`}
              data-testid={`meal-type-family-${key}`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                (formData.recipe_categories || []).includes(key)
                  ? 'bg-[#A5BBA8] border-[#A5BBA8]'
                  : 'border-[#E3DED5]'
              }`}>
                {(formData.recipe_categories || []).includes(key) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-sm text-[#2A312C]">{label}</span>
              <input
                type="checkbox"
                checked={(formData.recipe_categories || []).includes(key)}
                onChange={() => toggleRecipeCategory(key)}
                className="sr-only"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="meal-type-active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          data-testid="meal-type-active-checkbox"
        />
        <label htmlFor="meal-type-active" className="text-sm text-[#2A312C] cursor-pointer">
          Actif (visible dans les menus clients)
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="button" onClick={handleSubmit} className="btn-primary" data-testid="save-meal-type-btn">
          <Check className="w-5 h-5 mr-2" />
          Valider
        </button>
      </div>
    </div>
  );
}


function SectorsManagement() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);

  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    try {
      const response = await adminApi.getSectors();
      setSectors(response.data);
    } catch (error) {
      console.error('Error loading sectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingSector) {
        await adminApi.updateSector(editingSector.sector_id, data);
        toast.success('Secteur mis à jour');
      } else {
        await adminApi.createSector(data);
        toast.success('Secteur créé');
      }
      setDialogOpen(false);
      setEditingSector(null);
      loadSectors();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="space-y-6" data-testid="sectors-management">
      {(() => {
        const seen = {};
        (sectors || []).forEach((s) => {
          if (s.is_active === false) return;
          (s.postal_codes || []).forEach((cp) => { (seen[cp] = seen[cp] || []).push(s.name); });
        });
        const dupes = Object.entries(seen).filter(([, names]) => names.length > 1);
        if (dupes.length === 0) return null;
        return (
          <div className="p-4 border border-orange-300 bg-orange-50 rounded-xl" data-testid="sector-duplicates-warning">
            <p className="font-semibold text-orange-800 mb-1">Codes postaux en doublon entre secteurs</p>
            <p className="text-sm text-orange-700 mb-2">Ces codes postaux appartiennent à plusieurs secteurs actifs. Le secteur le plus prioritaire (numéro de priorité le plus petit, 1 = la plus prioritaire) est retenu. Corrigez si besoin.</p>
            <ul className="text-sm text-orange-800 list-disc pl-5">
              {dupes.map(([cp, names]) => (
                <li key={cp}><strong>{cp}</strong> : {names.join(', ')}</li>
              ))}
            </ul>
          </div>
        );
      })()}
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" onClick={() => setEditingSector(null)} data-testid="add-sector-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nouveau secteur
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSector ? 'Modifier le secteur' : 'Nouveau secteur'}</DialogTitle>
            </DialogHeader>
            <SectorForm sector={editingSector} onSave={handleSave} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : sectors.length === 0 ? (
        <div className="card-default text-center py-12">
          <MapPin className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun secteur configuré</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.map((sector) => (
            <div key={sector.sector_id} className="card-default">
              <div className="flex justify-between items-start mb-3">
                <Badge className={sector.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {sector.is_active ? 'Actif' : 'Inactif'}
                </Badge>
                <button
                  onClick={() => { setEditingSector(sector); setDialogOpen(true); }}
                  className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                >
                  <Pencil className="w-4 h-4 text-[#5C6A61]" />
                </button>
              </div>
              <h3 className="font-semibold text-[#2A312C] mb-2">{sector.name}</h3>
              <div className="flex flex-wrap gap-1.5 mb-2">
                <span className="px-2 py-0.5 rounded-full text-xs bg-[#C86B51]/10 text-[#C86B51]" data-testid={`sector-surcharge-${sector.sector_id}`}>
                  Supplément : {(sector.travel_surcharge || 0).toFixed(0)} €
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-[#A5BBA8]/20 text-[#2A312C]">Priorité {sector.priority ?? 100}</span>
                {sector.bookable === false && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800">Non réservable</span>
                )}
              </div>
              <p className="text-sm text-[#5C6A61]">{sector.postal_codes?.length || 0} codes postaux</p>
              <p className="text-xs text-[#5C6A61] mt-1">{sector.postal_codes?.slice(0, 5).join(', ')}{sector.postal_codes?.length > 5 ? '...' : ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectorForm({ sector, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: sector?.name || '',
    cities: sector?.cities || [],
    postal_codes: sector?.postal_codes || [],
    is_active: sector?.is_active ?? true,
    travel_surcharge: sector?.travel_surcharge ?? 0,
    priority: sector?.priority ?? 100,
    bookable: sector?.bookable ?? true
  });
  const [postalCodeInput, setPostalCodeInput] = useState('');

  const addPostalCode = () => {
    if (postalCodeInput && postalCodeInput.length === 5 && !formData.postal_codes.includes(postalCodeInput)) {
      setFormData({ ...formData, postal_codes: [...formData.postal_codes, postalCodeInput] });
      setPostalCodeInput('');
    }
  };

  const removePostalCode = (code) => {
    setFormData({ ...formData, postal_codes: formData.postal_codes.filter(c => c !== code) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Nom du secteur *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          data-testid="sector-name-input"
        />
      </div>
      <div>
        <Label>Codes postaux</Label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={postalCodeInput}
            onChange={(e) => setPostalCodeInput(e.target.value.slice(0, 5))}
            className="input-default flex-1"
            placeholder="75001"
            maxLength={5}
          />
          <button type="button" onClick={addPostalCode} className="btn-outline">Ajouter</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.postal_codes.map((code) => (
            <span key={code} className="px-2 py-1 bg-[#A5BBA8]/20 text-[#2A312C] rounded-lg text-sm flex items-center gap-1">
              {code}
              <button type="button" onClick={() => removePostalCode(code)}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Supplément déplacement (€)</Label>
          <input
            type="number" min={0} step={1}
            value={formData.travel_surcharge}
            onChange={(e) => setFormData({ ...formData, travel_surcharge: Math.max(0, parseFloat(e.target.value) || 0) })}
            className="input-default"
            data-testid="sector-surcharge-input"
          />
          <p className="text-xs text-[#5C6A61] mt-1">0 € = zone principale (aucun supplément)</p>
        </div>
        <div>
          <Label>Priorité (1 = la plus prioritaire)</Label>
          <input
            type="number" min={1} step={1}
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: Math.max(1, parseInt(e.target.value) || 1) })}
            className="input-default"
            data-testid="sector-priority-input"
          />
          <p className="text-xs text-[#5C6A61] mt-1">1 = priorité la plus forte. Si un code postal est dans plusieurs secteurs, celui dont le numéro est le plus petit est retenu.</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          checked={formData.bookable}
          onCheckedChange={(checked) => setFormData({ ...formData, bookable: checked })}
          id="sector-bookable"
        />
        <Label htmlFor="sector-bookable">Zone réservable (décocher = consultable mais non réservable)</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
          id="sector-active"
        />
        <Label htmlFor="sector-active">Secteur actif</Label>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="button" onClick={() => onSave(formData)} className="btn-primary" data-testid="save-sector-btn">
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// Waiting List Management - Out of Sector Requests
function WaitingListManagement() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        adminApi.getOutOfSectorRequests(),
        adminApi.getOutOfSectorStats()
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading waiting list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, status) => {
    try {
      await adminApi.updateOutOfSectorStatus(requestId, status);
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>;
  }

  const STATUS_LABELS = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
    contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-800' },
    converted: { label: 'Converti', color: 'bg-green-100 text-green-800' }
  };

  return (
    <div className="space-y-6" data-testid="waiting-list-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Liste d'attente hors secteur
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Personnes intéressées par des secteurs non encore desservis
          </p>
        </div>
        {requests.length > 0 && (
          <a
            href={adminApi.exportOutOfSectorCsv()}
            download
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </a>
        )}
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Demandes totales</p>
          <p className="text-2xl font-semibold text-[#2A312C]">{stats?.total_requests || 0}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Avec coordonnées</p>
          <p className="text-2xl font-semibold text-[#A5BBA8]">{stats?.with_contact_info || 0}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Codes postaux distincts</p>
          <p className="text-2xl font-semibold text-[#C86B51]">{stats?.by_postal_code?.length || 0}</p>
        </div>
      </div>

      {/* Top requested postal codes */}
      {stats?.by_postal_code?.length > 0 && (
        <div className="card-default">
          <h3 className="font-semibold text-[#2A312C] mb-4">Codes postaux les plus demandés</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.by_postal_code.slice(0, 8).map((item) => (
              <div key={item.postal_code} className="flex items-center justify-between p-3 bg-[#F9F6F0] rounded-lg">
                <span className="font-mono text-[#2A312C]">{item.postal_code}</span>
                <Badge className="bg-[#C86B51]/10 text-[#C86B51]">{item.count} demandes</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests list */}
      <div className="card-default">
        <h3 className="font-semibold text-[#2A312C] mb-4">Demandes récentes</h3>
        {requests.length === 0 ? (
          <p className="text-[#5C6A61] text-center py-8">Aucune demande en attente</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3DED5]">
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Contact</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Code postal</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Ville</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Statut</th>
                  <th className="text-right py-3 px-4 text-[#5C6A61] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.slice(0, 50).map((req) => (
                  <tr key={req.request_id} className="border-b border-[#E3DED5] last:border-0">
                    <td className="py-3 px-4">
                      <p className="text-[#2A312C]">{req.name || 'Non renseigné'}</p>
                      <p className="text-xs text-[#5C6A61]">{req.email || req.phone || '-'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono bg-[#F9F6F0] px-2 py-1 rounded text-[#2A312C]">
                        {req.postal_code}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#5C6A61]">{req.city || '-'}</td>
                    <td className="py-3 px-4">
                      <Badge className={STATUS_LABELS[req.status]?.color || 'bg-gray-100'}>
                        {STATUS_LABELS[req.status]?.label || req.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <select
                        value={req.status}
                        onChange={(e) => handleStatusUpdate(req.request_id, e.target.value)}
                        className="input-default w-auto py-1 text-sm"
                      >
                        <option value="pending">En attente</option>
                        <option value="contacted">Contacté</option>
                        <option value="converted">Converti</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EmployeesManagement() {
  const [employees, setEmployees] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesRes, sectorsRes] = await Promise.all([
        adminApi.getEmployees(),
        adminApi.getSectors()
      ]);
      setEmployees(employeesRes.data);
      setSectors(sectorsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingEmployee) {
        await adminApi.updateEmployee(editingEmployee.employee_id, data);
        toast.success('Employé mis à jour !');
      } else {
        await adminApi.createEmployee(data);
        toast.success('Employé créé !');
      }
      setDialogOpen(false);
      setEditingEmployee(null);
      loadData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Retirer ${employee.name} des employés ?`)) return;
    try {
      await adminApi.deleteEmployee(employee.employee_id);
      toast.success('Employé retiré');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-management">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Employés
          </h2>
          <p className="text-sm text-[#5C6A61]">{employees.length} employé(s) configuré(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button 
              className="btn-primary"
              onClick={() => setEditingEmployee(null)}
              data-testid="add-employee-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel employé
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}
              </DialogTitle>
            </DialogHeader>
            <EmployeeForm 
              employee={editingEmployee}
              sectors={sectors}
              onSave={handleSave} 
              onCancel={() => setDialogOpen(false)} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : employees.length === 0 ? (
        <div className="card-default text-center py-12">
          <Users className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun employé configuré</p>
          <p className="text-sm text-[#5C6A61] mt-2">Ajoutez un employé pour pouvoir créer des créneaux</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => {
            const employeeSectors = sectors.filter(s => employee.sector_ids?.includes(s.sector_id));
            return (
              <div key={employee.employee_id} className="card-default" data-testid={`employee-card-${employee.employee_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={employee.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {employee.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                    {employee.user_id && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">Connecté</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingEmployee(employee); setDialogOpen(true); }}
                      className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4 text-[#5C6A61]" />
                    </button>
                    <button
                      onClick={() => handleDelete(employee)}
                      className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-[#C86B51]" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-[#2A312C] text-lg">{employee.name}</h3>
                <p className="text-sm text-[#5C6A61] flex items-center gap-1 mb-1">
                  <Mail className="w-3 h-3" /> {employee.email}
                </p>
                {employee.phone && (
                  <p className="text-sm text-[#5C6A61] flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {employee.phone}
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-[#E3DED5]">
                  <p className="text-xs text-[#5C6A61] mb-1">Secteurs :</p>
                  {employeeSectors.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {employeeSectors.map(s => (
                        <Badge key={s.sector_id} className="bg-[#A5BBA8]/20 text-[#2A312C] text-xs">
                          {s.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[#C86B51]">Aucun secteur assigné</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmployeeForm({ employee, sectors, onSave, onCancel }) {
  const DAYS = [
    { id: 0, name: 'Lundi' },
    { id: 1, name: 'Mardi' },
    { id: 2, name: 'Mercredi' },
    { id: 3, name: 'Jeudi' },
    { id: 4, name: 'Vendredi' }
  ];

  const [formData, setFormData] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    sector_ids: employee?.sector_ids || [],
    working_days: employee?.working_days ?? [0, 1, 2, 3, 4], // Default Mon-Fri
    is_active: employee?.is_active ?? true
  });

  const toggleSector = (sectorId) => {
    const newSectors = formData.sector_ids.includes(sectorId)
      ? formData.sector_ids.filter(id => id !== sectorId)
      : [...formData.sector_ids, sectorId];
    setFormData({ ...formData, sector_ids: newSectors });
  };

  const toggleDay = (dayId) => {
    const newDays = formData.working_days.includes(dayId)
      ? formData.working_days.filter(id => id !== dayId)
      : [...formData.working_days, dayId].sort();
    setFormData({ ...formData, working_days: newDays });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('L\'email est requis');
      return;
    }
    if (formData.sector_ids.length === 0) {
      toast.error('Sélectionnez au moins un secteur');
      return;
    }
    if (formData.working_days.length === 0) {
      toast.error('Sélectionnez au moins un jour travaillé');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-5">
      {/* Nom */}
      <div>
        <Label className="text-base font-semibold">Nom de l'employé *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default"
          placeholder="Jean Dupont"
          autoFocus
          data-testid="employee-name-input"
        />
      </div>

      {/* Email et Téléphone */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold">Email *</Label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="input-default"
            placeholder="jean.dupont@email.fr"
            data-testid="employee-email-input"
          />
          <p className="text-xs text-[#5C6A61] mt-1">
            L'employé utilisera cet email pour se connecter
          </p>
        </div>
        <div>
          <Label>Téléphone</Label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="input-default"
            placeholder="06 12 34 56 78"
            data-testid="employee-phone-input"
          />
        </div>
      </div>

      {/* Jours travaillés */}
      <div>
        <Label className="text-base font-semibold">Jours travaillés *</Label>
        <p className="text-xs text-[#5C6A61] mb-2">Sélectionnez les jours où cet employé peut intervenir</p>
        <div className="flex flex-wrap gap-2">
          {DAYS.map(day => (
            <button
              key={day.id}
              type="button"
              onClick={() => toggleDay(day.id)}
              className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                formData.working_days.includes(day.id)
                  ? 'border-[#A5BBA8] bg-[#A5BBA8] text-white'
                  : 'border-[#E3DED5] bg-white text-[#5C6A61] hover:border-[#A5BBA8]'
              }`}
            >
              {day.name}
            </button>
          ))}
        </div>
      </div>

      {/* Secteurs */}
      <div>
        <Label className="text-base font-semibold">Secteurs d'intervention *</Label>
        {sectors.length === 0 ? (
          <div className="mt-2 p-4 bg-[#E8B25C]/10 rounded-lg border border-[#E8B25C]/30">
            <p className="text-sm text-[#E8B25C] font-medium">Aucun secteur créé</p>
            <p className="text-xs text-[#5C6A61]">
              Créez d'abord des secteurs dans le menu "Secteurs"
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {sectors.map(sector => (
              <label 
                key={sector.sector_id} 
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.sector_ids.includes(sector.sector_id)
                    ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                    : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  formData.sector_ids.includes(sector.sector_id)
                    ? 'bg-[#A5BBA8] border-[#A5BBA8]'
                    : 'border-[#E3DED5]'
                }`}>
                  {formData.sector_ids.includes(sector.sector_id) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#2A312C] block">{sector.name}</span>
                  {sector.postal_codes?.length > 0 && (
                    <span className="text-xs text-[#5C6A61]">
                      {sector.postal_codes.slice(0, 3).join(', ')}
                      {sector.postal_codes.length > 3 && ` +${sector.postal_codes.length - 3}`}
                    </span>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={formData.sector_ids.includes(sector.sector_id)}
                  onChange={() => toggleSector(sector.sector_id)}
                  className="sr-only"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Statut */}
      <div className="flex items-center gap-2">
        <Checkbox 
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
          id="employee-active"
        />
        <Label htmlFor="employee-active">Employé actif</Label>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">
          Annuler
        </button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          disabled={sectors.length === 0}
          data-testid="save-employee-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
}

function TimeSlotsManagement() {
  const [planning, setPlanning] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [weekSchedule, setWeekSchedule] = useState([]);
  // Reschedule (réutilise l'endpoint déjà développé)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleStart, setRescheduleStart] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [exceptionData, setExceptionData] = useState({ 
    date: '', 
    is_active: false, 
    start_time: '08:00',
    end_time: '17:30',
    reason: '' 
  });
  const [applyType, setApplyType] = useState('recurring'); // 'recurring' or 'one_time'
  const [weekStartDate, setWeekStartDate] = useState('');
  const [exceptions, setExceptions] = useState([]);

  const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const TIME_OPTIONS = [];
  for (let h = 6; h <= 20; h++) {
    TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:00`);
    TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:30`);
  }

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadData();
    }
  }, [selectedEmployee]);

  const loadEmployees = async () => {
    try {
      const res = await adminApi.getEmployees();
      setEmployees(res.data.filter(e => e.is_active));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadData = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const [planningRes, scheduleRes, exceptionsRes] = await Promise.all([
        adminApi.getEmployeePlanning(selectedEmployee, { days: 56 }),
        adminApi.getEmployeeSchedule(selectedEmployee),
        adminApi.getEmployeeExceptions(selectedEmployee, { 
          start_date: new Date().toISOString().split('T')[0] 
        })
      ]);
      setPlanning(planningRes.data);
      
      // Set week schedule with all 7 days
      const schedule = scheduleRes.data.days || [];
      const fullSchedule = DAYS_FR.map((_, i) => {
        const existing = schedule.find(d => d.day_of_week === i);
        return existing || {
          day_of_week: i,
          is_active: i < 5,
          start_time: '08:00',
          end_time: '17:30'
        };
      });
      setWeekSchedule(fullSchedule);
      setExceptions(exceptionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openReschedule = (prestation) => {
    setRescheduleTarget(prestation);
    setRescheduleStart(prestation.start_time || '');
    setRescheduleDialogOpen(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget) return;
    if (!/^\d{2}:\d{2}$/.test(rescheduleStart)) { toast.error('Heure invalide (format HH:MM)'); return; }
    setRescheduling(true);
    try {
      await adminApi.rescheduleReservation(rescheduleTarget.reservation_id, rescheduleStart);
      toast.success('Horaire modifié');
      setRescheduleDialogOpen(false);
      setRescheduleTarget(null);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de déplacer la prestation");
    } finally {
      setRescheduling(false);
    }
  };

  const addMinutesToTime = (hhmm, minutes) => {
    if (!/^\d{2}:\d{2}$/.test(hhmm || '')) return '—';
    const [h, m] = hhmm.split(':').map(Number);
    const total = h * 60 + m + (minutes || 0);
    const nh = Math.floor(total / 60), nm = total % 60;
    return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
  };

  const handleSaveSchedule = async () => {
    try {
      const data = {
        employee_id: selectedEmployee,
        days: weekSchedule,
        apply_type: applyType,
        week_start_date: applyType === 'one_time' ? weekStartDate : null
      };
      await adminApi.bulkUpdateSchedule(selectedEmployee, data);
      toast.success(applyType === 'recurring' 
        ? 'Planning récurrent mis à jour !' 
        : 'Exceptions créées pour cette semaine !');
      setScheduleDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSaveException = async () => {
    if (!selectedEmployee || !exceptionData.date) {
      toast.error('Sélectionnez un employé et une date');
      return;
    }
    try {
      await adminApi.createEmployeeException(selectedEmployee, {
        employee_id: selectedEmployee,
        date: exceptionData.date,
        is_active: exceptionData.is_active,
        start_time: exceptionData.is_active ? exceptionData.start_time : null,
        end_time: exceptionData.is_active ? exceptionData.end_time : null,
        reason: exceptionData.reason
      });
      toast.success('Exception enregistrée');
      setExceptionDialogOpen(false);
      setExceptionData({ date: '', is_active: false, start_time: '08:00', end_time: '17:30', reason: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteException = async (date) => {
    if (!window.confirm('Supprimer cette exception ?')) return;
    try {
      await adminApi.deleteEmployeeException(selectedEmployee, date);
      toast.success('Exception supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateDaySchedule = (dayIndex, field, value) => {
    const newSchedule = [...weekSchedule];
    newSchedule[dayIndex] = { ...newSchedule[dayIndex], [field]: value };
    setWeekSchedule(newSchedule);
  };

  const today = new Date().toISOString().split('T')[0];
  const selectedEmp = employees.find(e => e.employee_id === selectedEmployee);
  const workingDays = planning.filter(d => d.is_active);
  const totalPrestations = planning.reduce((sum, d) => sum + (d.count || 0), 0);

  // Get next Monday for one_time week start
  const getNextMonday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  };

  return (
    <div className="space-y-6" data-testid="timeslots-management">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Gestion des disponibilités
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Planning réel de l'employé : amplitude de travail effective et prestations positionnées.
            Les créneaux clients sont calculés automatiquement selon la durée réelle des prestations.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="input-default w-auto"
            data-testid="employee-select"
          >
            <option value="">Sélectionner un employé</option>
            {employees.map(e => (
              <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedEmployee && (
        <div className="flex flex-wrap gap-2">
          {/* Schedule Management Button */}
          <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary" data-testid="edit-schedule-btn">
                <Clock className="w-5 h-5 mr-2" />
                Modifier les horaires
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Horaires de travail - {selectedEmp?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Apply type selection */}
                <div className="p-4 bg-[#F9F6F0] rounded-xl">
                  <Label className="font-semibold mb-3 block">Type de modification</Label>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[#E3DED5] hover:border-[#A5BBA8] transition-colors">
                      <input
                        type="radio"
                        checked={applyType === 'recurring'}
                        onChange={() => setApplyType('recurring')}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-[#2A312C]">Récurrent</p>
                        <p className="text-sm text-[#5C6A61]">
                          Appliqué chaque semaine jusqu'à modification
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[#E3DED5] hover:border-[#A5BBA8] transition-colors">
                      <input
                        type="radio"
                        checked={applyType === 'one_time'}
                        onChange={() => { setApplyType('one_time'); setWeekStartDate(getNextMonday()); }}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-[#2A312C]">Ponctuel (une semaine)</p>
                        <p className="text-sm text-[#5C6A61]">
                          Valable uniquement pour la semaine sélectionnée
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  {applyType === 'one_time' && (
                    <div className="mt-4">
                      <Label>Semaine du</Label>
                      <input
                        type="date"
                        value={weekStartDate}
                        onChange={(e) => setWeekStartDate(e.target.value)}
                        className="input-default"
                        min={today}
                      />
                    </div>
                  )}
                </div>

                {/* Week schedule */}
                <div className="space-y-3">
                  <Label className="font-semibold">Horaires par jour</Label>
                  {weekSchedule.map((day, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-xl border transition-colors ${
                        day.is_active 
                          ? 'border-[#A5BBA8] bg-[#A5BBA8]/5' 
                          : 'border-[#E3DED5] bg-[#F9F6F0]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={day.is_active}
                            onCheckedChange={(checked) => updateDaySchedule(idx, 'is_active', checked)}
                            id={`day-${idx}`}
                          />
                          <Label 
                            htmlFor={`day-${idx}`} 
                            className={`font-medium cursor-pointer ${day.is_active ? 'text-[#2A312C]' : 'text-[#5C6A61]'}`}
                          >
                            {DAYS_FR[idx]}
                          </Label>
                        </div>
                        {day.is_active && (
                          <Badge className="bg-green-100 text-green-800">Actif</Badge>
                        )}
                      </div>
                      
                      {day.is_active && (
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <Label className="text-xs text-[#5C6A61]">Début</Label>
                            <select
                              value={day.start_time}
                              onChange={(e) => updateDaySchedule(idx, 'start_time', e.target.value)}
                              className="input-default"
                            >
                              {TIME_OPTIONS.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-[#5C6A61]">Fin</Label>
                            <select
                              value={day.end_time}
                              onChange={(e) => updateDaySchedule(idx, 'end_time', e.target.value)}
                              className="input-default"
                            >
                              {TIME_OPTIONS.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Example use case */}
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">💡 Exemples d'utilisation :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Le lundi, je termine à 16h pour récupérer ma fille → Récurrent + Lundi fin à 16:00</li>
                    <li>Cette semaine, je ne travaille pas mercredi → Ponctuel + Mercredi inactif</li>
                  </ul>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button onClick={() => setScheduleDialogOpen(false)} className="btn-outline">
                    Annuler
                  </button>
                  <button onClick={handleSaveSchedule} className="btn-primary" data-testid="save-schedule-btn">
                    <Check className="w-5 h-5 mr-2" />
                    Enregistrer
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Quick Exception Button */}
          <Dialog open={exceptionDialogOpen} onOpenChange={setExceptionDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-outline" data-testid="add-exception-btn">
                <Calendar className="w-5 h-5 mr-2" />
                Exception ponctuelle
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Ajouter une exception</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Date</Label>
                  <input
                    type="date"
                    value={exceptionData.date}
                    onChange={(e) => setExceptionData({...exceptionData, date: e.target.value})}
                    className="input-default"
                    min={today}
                  />
                </div>
                
                <div>
                  <Label>Ce jour-là :</Label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#E3DED5] hover:border-[#A5BBA8]">
                      <input
                        type="radio"
                        checked={!exceptionData.is_active}
                        onChange={() => setExceptionData({...exceptionData, is_active: false})}
                      />
                      <div>
                        <p className="font-medium text-[#2A312C]">Ne travaille pas</p>
                        <p className="text-xs text-[#5C6A61]">Aucun créneau ce jour</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#E3DED5] hover:border-[#A5BBA8]">
                      <input
                        type="radio"
                        checked={exceptionData.is_active}
                        onChange={() => setExceptionData({...exceptionData, is_active: true})}
                      />
                      <div>
                        <p className="font-medium text-[#2A312C]">Travaille (horaires spéciaux)</p>
                        <p className="text-xs text-[#5C6A61]">Avec des horaires différents</p>
                      </div>
                    </label>
                  </div>
                </div>

                {exceptionData.is_active && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Début</Label>
                      <select
                        value={exceptionData.start_time}
                        onChange={(e) => setExceptionData({...exceptionData, start_time: e.target.value})}
                        className="input-default"
                      >
                        {TIME_OPTIONS.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Fin</Label>
                      <select
                        value={exceptionData.end_time}
                        onChange={(e) => setExceptionData({...exceptionData, end_time: e.target.value})}
                        className="input-default"
                      >
                        {TIME_OPTIONS.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Raison (optionnel)</Label>
                  <input
                    type="text"
                    value={exceptionData.reason}
                    onChange={(e) => setExceptionData({...exceptionData, reason: e.target.value})}
                    className="input-default"
                    placeholder="Ex: Rendez-vous médical, école..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setExceptionDialogOpen(false)} className="btn-outline">Annuler</button>
                  <button onClick={handleSaveException} className="btn-primary" data-testid="save-exception-btn">Confirmer</button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Current Schedule Summary */}
      {selectedEmp && weekSchedule.length > 0 && (
        <div className="card-default bg-[#F9F6F0]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-semibold text-[#2A312C]">{selectedEmp.name}</p>
              <p className="text-sm text-[#5C6A61]">Planning récurrent actuel</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#A5BBA8]">{totalPrestations}</p>
              <p className="text-xs text-[#5C6A61]">prestation(s) positionnée(s)</p>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {weekSchedule.map((day, idx) => (
              <div 
                key={idx} 
                className={`p-2 rounded text-xs ${
                  day.is_active ? 'bg-[#A5BBA8]/20 text-[#2A312C]' : 'bg-[#E3DED5]/50 text-[#5C6A61]'
                }`}
              >
                <p className="font-medium">{DAYS_FR[idx].slice(0, 3)}</p>
                {day.is_active ? (
                  <p className="mt-1">{day.start_time?.slice(0, 5)}-{day.end_time?.slice(0, 5)}</p>
                ) : (
                  <p className="mt-1 text-[#C86B51]">—</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exceptions List */}
      {exceptions.length > 0 && (
        <div className="card-default">
          <h3 className="font-semibold text-[#2A312C] mb-3">Exceptions à venir</h3>
          <div className="space-y-2">
            {exceptions.map((exc) => (
              <div 
                key={exc.exception_id || exc.date} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  exc.is_active ? 'bg-[#A5BBA8]/10' : 'bg-[#C86B51]/10'
                }`}
              >
                <div>
                  <p className="font-medium text-[#2A312C]">
                    {new Date(exc.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  <p className="text-sm text-[#5C6A61]">
                    {exc.is_active 
                      ? `Travaille ${exc.start_time}-${exc.end_time}` 
                      : 'Ne travaille pas'}
                    {exc.reason && ` — ${exc.reason}`}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteException(exc.date)}
                  className="p-2 hover:bg-[#C86B51]/20 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 text-[#C86B51]" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {employees.length === 0 && (
        <div className="card-default bg-[#E8B25C]/10 border-[#E8B25C]/30">
          <p className="text-[#E8B25C] font-semibold">Aucun employé actif</p>
          <p className="text-sm text-[#5C6A61]">Créez d'abord des employés dans le menu "Employés".</p>
        </div>
      )}

      {!selectedEmployee && employees.length > 0 && (
        <div className="card-default text-center py-12">
          <Users className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Sélectionnez un employé pour gérer ses disponibilités</p>
        </div>
      )}

      {/* Real intelligent planning (source: effective schedule + positioned reservations) */}
      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : selectedEmployee && workingDays.length === 0 ? (
        <div className="card-default text-center py-12">
          <Calendar className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun jour travaillé sur les prochaines semaines</p>
          <p className="text-sm text-[#5C6A61] mt-2">
            Définissez les horaires de travail via « Modifier les horaires ».
          </p>
        </div>
      ) : selectedEmployee && (
        <div className="space-y-3" data-testid="admin-planning-list">
          <h3 className="font-semibold text-[#2A312C]">Planning des prochaines semaines</h3>
          {workingDays.map(day => {
            const dateObj = new Date(day.date);
            const formattedDate = dateObj.toLocaleDateString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long'
            });
            const full = day.count >= day.max_prestations;
            return (
              <div key={day.date} className="card-default" data-testid={`admin-planning-day-${day.date}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-[#2A312C] capitalize">{formattedDate}</h4>
                    <p className="text-sm text-[#5C6A61]" data-testid={`admin-planning-amplitude-${day.date}`}>
                      Disponibilité : {day.start_time?.slice(0, 5)}–{day.end_time?.slice(0, 5)}
                      {day.is_exception && (
                        <span className="ml-2 text-[#E8B25C]">
                          (exception{day.reason ? ` — ${day.reason}` : ''})
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge
                    className={full ? 'bg-[#C86B51]/15 text-[#C86B51]' : 'bg-[#A5BBA8]/20 text-[#5C6A61]'}
                    data-testid={`admin-planning-count-${day.date}`}
                  >
                    {day.count}/{day.max_prestations} prestation(s)
                  </Badge>
                </div>

                {day.prestations.length === 0 ? (
                  <p className="text-sm text-[#5C6A61]">Aucune prestation positionnée ce jour.</p>
                ) : (
                  <div className="space-y-2">
                    {day.prestations.map(p => {
                      const canReschedule = p.reservation_id && p.duration_minutes &&
                        p.status !== 'annulee' && p.status !== 'terminee';
                      return (
                        <div
                          key={p.reservation_id}
                          className="flex items-center justify-between p-3 rounded-xl border-2 border-[#A5BBA8] bg-[#A5BBA8]/5"
                          data-testid={`admin-planning-prestation-${p.reservation_id}`}
                        >
                          <div>
                            <p className="font-medium text-[#2A312C]">
                              {p.start_time} → {p.end_time}
                            </p>
                            <p className="text-sm text-[#5C6A61]">
                              {p.client_name || `#${(p.reservation_id || '').slice(-8)}`}
                              {p.city ? ` • ${p.city}` : ''}
                              {p.formula ? ` • ${p.formula}` : ''}
                            </p>
                          </div>
                          {canReschedule && (
                            <button
                              onClick={() => openReschedule(p)}
                              className="p-2 text-[#5C6A61] hover:bg-[#A5BBA8]/20 rounded"
                              title="Modifier l'horaire"
                              data-testid={`admin-planning-reschedule-${p.reservation_id}`}
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule Dialog (réutilise PUT /admin/reservations/{id}/reschedule) */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l'horaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5C6A61]">
              Seule l'heure de début est modifiable. La fin est recalculée automatiquement
              (durée : {rescheduleTarget?.duration_minutes || '—'} min) et toutes les règles
              (amplitude, 60 min, max 2/jour) sont vérifiées.
            </p>
            <div>
              <Label>Nouvelle heure de début (HH:MM)</Label>
              <input
                type="time"
                value={rescheduleStart}
                onChange={(e) => setRescheduleStart(e.target.value)}
                className="input-default"
                data-testid="admin-planning-reschedule-input"
              />
            </div>
            <p className="text-sm text-[#2A312C]">
              Nouvelle heure de fin : <strong>{addMinutesToTime(rescheduleStart, rescheduleTarget?.duration_minutes)}</strong>
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRescheduleDialogOpen(false)} className="btn-outline">Annuler</button>
              <button
                onClick={handleReschedule}
                disabled={rescheduling}
                className="btn-primary"
                data-testid="admin-planning-reschedule-confirm"
              >
                {rescheduling ? 'Traitement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReservationsManagement() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', is_test: null });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [rescheduleStart, setRescheduleStart] = useState('');

  useEffect(() => {
    loadReservations();
  }, [filter]);

  const loadReservations = async () => {
    try {
      const response = await adminApi.getReservations(filter);
      setReservations(response.data);
    } catch (error) {
      console.error('Error loading reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const computeEndPreview = (start, duration) => {
    if (!/^\d{2}:\d{2}$/.test(start || '') || !duration) return '—';
    const [h, m] = start.split(':').map(Number);
    const total = h * 60 + m + duration;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const handleReschedule = async () => {
    if (!selectedReservation) return;
    if (!/^\d{2}:\d{2}$/.test(rescheduleStart)) { toast.error('Heure invalide (format HH:MM)'); return; }
    setProcessing(true);
    try {
      const res = await adminApi.rescheduleReservation(selectedReservation.reservation_id, rescheduleStart);
      toast.success(`Prestation déplacée : ${res.data.start_time} – ${res.data.end_time}${res.data.client_notified ? ' (client notifié)' : ''}`);
      setShowRescheduleDialog(false);
      setSelectedReservation(null);
      loadReservations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Déplacement impossible.");
    } finally {
      setProcessing(false);
    }
  };

  const handleAdminCancel = async () => {
    if (!selectedReservation) return;
    setProcessing(true);
    try {
      await adminApi.adminCancelReservation(selectedReservation.reservation_id, cancelReason);
      toast.success('Réservation annulée');
      setShowCancelDialog(false);
      setCancelReason('');
      loadReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setProcessing(false);
    }
  };

  const handleForceRefund = async () => {
    if (!selectedReservation) return;
    setProcessing(true);
    try {
      const response = await adminApi.forceRefund(selectedReservation.reservation_id, refundReason);
      if (response.data.stripe_refund_success) {
        toast.success('Remboursement effectué via Stripe');
      } else {
        toast.success('Remboursement exceptionnel enregistré (à traiter manuellement)');
      }
      setShowRefundDialog(false);
      setRefundReason('');
      loadReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du remboursement');
    } finally {
      setProcessing(false);
    }
  };

  const getCancellationBadge = (reservation) => {
    if (reservation.status !== 'annulee') return null;
    const refundStatus = reservation.refund_status;
    if (refundStatus === 'refunded') return <Badge className="bg-green-100 text-green-800">Remboursé</Badge>;
    if (refundStatus === 'exceptional_refund') return <Badge className="bg-purple-100 text-purple-800">Remb. exceptionnel</Badge>;
    if (refundStatus === 'not_eligible') return <Badge className="bg-red-100 text-red-800">Non remboursable</Badge>;
    if (refundStatus === 'eligible_pending') return <Badge className="bg-yellow-100 text-yellow-800">Remb. en attente</Badge>;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="reservations-management">
      <div className="flex flex-wrap gap-2">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="input-default w-auto"
        >
          <option value="">Tous statuts</option>
          <option value="en_attente">En attente</option>
          <option value="confirmee">Confirmée</option>
          <option value="en_cours">En cours</option>
          <option value="terminee">Terminée</option>
          <option value="annulee">Annulée</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : reservations.length === 0 ? (
        <div className="card-default text-center py-12">
          <Calendar className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucune réservation</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => {
            const statusInfo = RESERVATION_STATUS[reservation.status];
            const paymentInfo = PAYMENT_STATUS[reservation.payment_status];
            const canCancel = reservation.status !== 'annulee' && reservation.status !== 'terminee';
            const canForceRefund = reservation.status === 'annulee' && 
              reservation.payment_status === 'paye' && 
              reservation.refund_status === 'not_eligible';
            
            return (
              <div key={reservation.reservation_id} className="card-default">
                <div className="flex flex-col lg:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-semibold text-[#2A312C]">#{reservation.reservation_id.slice(-8)}</h3>
                      <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
                      <Badge className={paymentInfo?.color}>{paymentInfo?.label}</Badge>
                      {reservation.is_test && <Badge className="bg-purple-100 text-purple-800">Test</Badge>}
                      {getCancellationBadge(reservation)}
                    </div>
                    <p className="text-sm text-[#5C6A61]">
                      {formatDate(reservation.date)} • {reservation.household_size} pers. • {reservation.formula}
                    </p>
                    <p className="text-sm text-[#5C6A61]" data-testid={`admin-reservation-client-${reservation.reservation_id}`}>
                      Client : {reservation.client_name || '—'}
                      {reservation.employee_name ? ` • Employé : ${reservation.employee_name}` : ' • Employé : non affecté'}
                      {reservation.sector_name ? ` • Secteur : ${reservation.sector_name}` : (reservation.client_city ? ` • ${reservation.client_city}` : '')}
                    </p>
                    {reservation.start_time && (
                      <p className="text-sm text-[#5C6A61]" data-testid={`admin-reservation-horaire-${reservation.reservation_id}`}>
                        Horaire : {reservation.start_time}{reservation.end_time ? ` – ${reservation.end_time}` : ''}
                      </p>
                    )}
                    {reservation.no_fish && (
                      <p className="text-xs text-[#C86B51] mt-1 font-medium" data-testid="admin-reservation-no-fish">
                        Sans poisson — remplacé par 1 plat mijoté / au four
                      </p>
                    )}
                    {reservation.status === 'annulee' && reservation.cancellation_reason && (
                      <p className="text-xs text-[#5C6A61] mt-1">
                        Raison: {reservation.cancellation_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xl font-semibold text-[#2A312C]">{formatPrice(reservation.total_amount)}</p>
                      {reservation.refund_amount > 0 && (
                        <p className="text-xs text-[#5C6A61]">Remboursé: {formatPrice(reservation.refund_amount)}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {reservation.employee_id && reservation.start_time && reservation.duration_minutes &&
                       reservation.status !== 'annulee' && reservation.status !== 'terminee' && (
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setRescheduleStart(reservation.start_time);
                            setShowRescheduleDialog(true);
                          }}
                          className="p-2 text-[#5C6A61] hover:bg-[#A5BBA8]/20 rounded"
                          title="Modifier l'horaire"
                          data-testid={`admin-reschedule-btn-${reservation.reservation_id}`}
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      {canCancel && (
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowCancelDialog(true);
                          }}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-lg"
                          title="Annuler la prestation"
                          data-testid={`admin-cancel-btn-${reservation.reservation_id}`}
                        >
                          <X className="w-4 h-4" />
                          Annuler la prestation
                        </button>
                      )}
                      {canForceRefund && (
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowRefundDialog(true);
                          }}
                          className="p-2 text-purple-500 hover:bg-purple-50 rounded"
                          title="Remboursement exceptionnel"
                        >
                          <Euro className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'horaire de la prestation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C6A61]">
              La durée ({selectedReservation?.duration_minutes} min) reste inchangée. La nouvelle heure
              de fin est recalculée automatiquement. Les règles (amplitude effective de l'employé,
              exceptions, 60 min entre prestations, max 2/jour) sont vérifiées ; en cas d'impossibilité,
              un message explique pourquoi.
            </p>
            <div>
              <Label className="text-sm mb-1 block">Nouvelle heure de début</Label>
              <input
                type="time"
                value={rescheduleStart}
                onChange={(e) => setRescheduleStart(e.target.value)}
                className="w-full border border-[#E3DED5] rounded-lg p-2"
                data-testid="reschedule-start-input"
              />
            </div>
            <p className="text-sm text-[#2A312C]" data-testid="reschedule-end-preview">
              Nouvelle heure de fin : <strong>{computeEndPreview(rescheduleStart, selectedReservation?.duration_minutes)}</strong>
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>Annuler</Button>
              <Button onClick={handleReschedule} disabled={processing} data-testid="reschedule-confirm-btn">
                {processing ? 'Traitement…' : 'Valider'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler la prestation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C6A61]">
              Êtes-vous sûr de vouloir annuler cette prestation ? Le créneau sera immédiatement libéré,
              la réservation reste conservée dans l'historique et le client sera informé par e-mail.
              Aucun remboursement n'est déclenché automatiquement.
            </p>
            <div>
              <Label className="text-sm mb-1 block">Raison</Label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="input-field"
                rows={2}
                placeholder="Raison de l'annulation..."
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCancelDialog(false)} className="btn-outline flex-1">
                Retour
              </button>
              <button
                onClick={handleAdminCancel}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="admin-cancel-confirm-btn"
              >
                {processing ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remboursement exceptionnel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800">
                Cette réservation a été annulée tardivement. Le remboursement n'est normalement pas prévu.
              </p>
              <p className="text-sm text-purple-700 mt-1">
                Montant à rembourser: <strong>{selectedReservation && formatPrice(selectedReservation.amount_paid)}</strong>
              </p>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Raison du remboursement exceptionnel</Label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="input-field"
                rows={2}
                placeholder="Justification du remboursement..."
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowRefundDialog(false)} className="btn-outline flex-1">
                Retour
              </button>
              <button
                onClick={handleForceRefund}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {processing ? 'Traitement...' : 'Confirmer le remboursement'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatisticsView() {
  return (
    <div className="card-default text-center py-12" data-testid="statistics-view">
      <BarChart3 className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
      <p className="text-[#5C6A61]">Statistiques détaillées</p>
      <p className="text-sm text-[#5C6A61]">Analyses et rapports à venir</p>
    </div>
  );
}

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await adminApi.updateUserRole(userId, role);
      toast.success('Rôle mis à jour');
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-management">
      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : (
        <div className="card-default overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E3DED5]">
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Nom</th>
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Email</th>
                <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Rôle</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-[#E3DED5] last:border-0">
                  <td className="py-3 px-4 text-[#2A312C]">{u.name}</td>
                  <td className="py-3 px-4 text-[#5C6A61]">{u.email}</td>
                  <td className="py-3 px-4">
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                      className="input-default w-auto py-1"
                    >
                      <option value="client">Client</option>
                      <option value="employee">Employé</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Badge className={u.role === 'admin' ? 'bg-purple-100 text-purple-800' : u.role === 'employee' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                      {u.role}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// Pricing Management
function PricingManagement() {
  const [formulas, setFormulas] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formulaDialogOpen, setFormulaDialogOpen] = useState(false);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [formulaFilter, setFormulaFilter] = useState({ status: 'all', type: 'all' });
  const [optionFilter, setOptionFilter] = useState({ status: 'all', type: 'all' });

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      const [formulasRes, optionsRes] = await Promise.all([
        adminApi.getFormulaPrices(),
        adminApi.getOptionPrices()
      ]);
      setFormulas(formulasRes.data);
      setOptions(optionsRes.data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFormula = async (data) => {
    try {
      if (editingFormula) {
        await adminApi.updateFormulaPrice(editingFormula.formula_id, data);
        toast.success('Formule mise à jour');
      } else {
        await adminApi.createFormulaPrice(data);
        toast.success('Formule créée');
      }
      setFormulaDialogOpen(false);
      setEditingFormula(null);
      loadPricing();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteFormula = async (formula) => {
    const usageCount = formula.usage_count || 0;
    let confirmMsg = 'Supprimer cette formule ?';
    if (usageCount > 0) {
      confirmMsg = `Cette formule est utilisée dans ${usageCount} réservation(s). Voulez-vous l'archiver (elle restera visible dans l'historique) ?`;
    }
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await adminApi.deleteFormulaPrice(formula.formula_id, usageCount > 0);
      toast.success(usageCount > 0 ? 'Formule archivée' : 'Formule supprimée');
      loadPricing();
    } catch (error) {
      console.error('Delete formula error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleSaveOption = async (data) => {
    try {
      if (editingOption) {
        await adminApi.updateOptionPrice(editingOption.option_id, data);
        toast.success('Option mise à jour');
      } else {
        await adminApi.createOptionPrice(data);
        toast.success('Option créée');
      }
      setOptionDialogOpen(false);
      setEditingOption(null);
      loadPricing();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteOption = async (option) => {
    const usageCount = option.usage_count || 0;
    let confirmMsg = 'Supprimer cette option ?';
    if (usageCount > 0) {
      confirmMsg = `Cette option est utilisée dans ${usageCount} réservation(s). Voulez-vous l'archiver ?`;
    }
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await adminApi.deleteOptionPrice(option.option_id, usageCount > 0);
      toast.success(usageCount > 0 ? 'Option archivée' : 'Option supprimée');
      loadPricing();
    } catch (error) {
      console.error('Delete option error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Apply filters
  const filteredFormulas = formulas.filter(f => {
    if (formulaFilter.status !== 'all') {
      if (formulaFilter.status === 'active' && !f.is_active) return false;
      if (formulaFilter.status === 'inactive' && f.is_active) return false;
    }
    if (formulaFilter.type !== 'all') {
      if (f.service_type !== formulaFilter.type) return false;
    }
    return true;
  });

  const filteredOptions = options.filter(o => {
    if (o.key === 'drive') return false;  // Option Drive supprimée : masquée de l'admin (historique conservé en base)
    if (optionFilter.status !== 'all') {
      if (optionFilter.status === 'active' && !o.is_active) return false;
      if (optionFilter.status === 'inactive' && o.is_active) return false;
    }
    if (optionFilter.type !== 'all') {
      if (o.service_type !== optionFilter.type) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>;
  }

  return (
    <div className="space-y-8" data-testid="pricing-management">
      {/* Formulas Section */}
      <div>
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">Formules</h2>
            <p className="text-sm text-[#5C6A61]">Tarifs selon la taille du foyer (attribués automatiquement)</p>
          </div>
          <div className="flex gap-2 items-center">
            <select 
              value={formulaFilter.status} 
              onChange={(e) => setFormulaFilter({...formulaFilter, status: e.target.value})}
              className="input-default text-sm py-2"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
            <select 
              value={formulaFilter.type} 
              onChange={(e) => setFormulaFilter({...formulaFilter, type: e.target.value})}
              className="input-default text-sm py-2"
            >
              <option value="all">Tous les types</option>
              <option value="SAP">SAP</option>
              <option value="NON_SAP">NON SAP</option>
            </select>
            <Dialog open={formulaDialogOpen} onOpenChange={setFormulaDialogOpen}>
              <DialogTrigger asChild>
                <button 
                  className="btn-primary"
                  onClick={() => setEditingFormula(null)}
                  data-testid="add-formula-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nouvelle formule
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{editingFormula ? 'Modifier la formule' : 'Nouvelle formule'}</DialogTitle>
                </DialogHeader>
                <FormulaForm 
                  formula={editingFormula}
                  onSave={handleSaveFormula} 
                  onCancel={() => setFormulaDialogOpen(false)} 
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {filteredFormulas.length === 0 ? (
          <div className="card-default text-center py-8">
            <Euro className="w-10 h-10 text-[#E3DED5] mx-auto mb-3" />
            <p className="text-[#5C6A61]">Aucune formule {formulaFilter.status !== 'all' || formulaFilter.type !== 'all' ? 'correspondant aux filtres' : 'configurée'}</p>
            <p className="text-sm text-[#5C6A61]">{formulas.length === 0 ? 'Les tarifs par défaut seront utilisés' : 'Modifiez les filtres pour voir les formules'}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFormulas.map((formula) => (
              <div key={formula.formula_id} className="card-default" data-testid={`formula-card-${formula.formula_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-2">
                    <Badge className={formula.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {formula.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                    <Badge className={formula.service_type === 'SAP' ? 'bg-[#A5BBA8]/20 text-[#2A312C]' : 'bg-[#C86B51]/20 text-[#C86B51]'}>
                      {formula.service_type || 'SAP'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingFormula(formula); setFormulaDialogOpen(true); }}
                      className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4 text-[#5C6A61]" />
                    </button>
                    <button
                      onClick={() => handleDeleteFormula(formula)}
                      className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-[#C86B51]" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-[#2A312C] mb-1">{formula.name}</h3>
                <p className="text-sm text-[#5C6A61] mb-2">{formula.description}</p>
                <p className="text-xs text-[#5C6A61] mb-1">{formula.min_persons}-{formula.max_persons} personnes · {formula.duration_hours || 4}h</p>
                {formula.usage_count > 0 && (
                  <p className="text-xs text-[#A5BBA8] mb-2">{formula.usage_count} réservation(s)</p>
                )}
                <p className="text-2xl font-semibold text-[#C86B51]">{formatPrice(formula.price)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Options Section */}
      <div>
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">Options</h2>
            <p className="text-sm text-[#5C6A61]">Services supplémentaires au choix du client</p>
          </div>
          <div className="flex gap-2 items-center">
            <select 
              value={optionFilter.status} 
              onChange={(e) => setOptionFilter({...optionFilter, status: e.target.value})}
              className="input-default text-sm py-2"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
            <select 
              value={optionFilter.type} 
              onChange={(e) => setOptionFilter({...optionFilter, type: e.target.value})}
              className="input-default text-sm py-2"
            >
              <option value="all">Tous les types</option>
              <option value="SAP">SAP</option>
              <option value="NON_SAP">NON SAP</option>
            </select>
            <Dialog open={optionDialogOpen} onOpenChange={setOptionDialogOpen}>
              <DialogTrigger asChild>
                <button 
                  className="btn-primary"
                  onClick={() => setEditingOption(null)}
                  data-testid="add-option-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nouvelle option
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingOption ? 'Modifier l\'option' : 'Nouvelle option'}</DialogTitle>
                </DialogHeader>
                <OptionForm 
                  option={editingOption}
                  onSave={handleSaveOption} 
                  onCancel={() => setOptionDialogOpen(false)} 
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {filteredOptions.length === 0 ? (
          <div className="card-default text-center py-8">
            <Settings className="w-10 h-10 text-[#E3DED5] mx-auto mb-3" />
            <p className="text-[#5C6A61]">Aucune option {optionFilter.status !== 'all' || optionFilter.type !== 'all' ? 'correspondant aux filtres' : 'configurée'}</p>
            <p className="text-sm text-[#5C6A61]">{options.length === 0 ? 'Les options par défaut seront utilisées' : 'Modifiez les filtres pour voir les options'}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredOptions.map((option) => (
              <div key={option.option_id} className="card-default" data-testid={`option-card-${option.option_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex flex-wrap gap-1">
                    <Badge className={option.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {option.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                    <Badge className={option.service_type === 'SAP' ? 'bg-[#A5BBA8]/20 text-[#2A312C]' : 'bg-[#C86B51]/20 text-[#C86B51]'}>
                      {option.service_type || 'SAP'}
                    </Badge>
                    {option.has_formats && (
                      <Badge className="bg-[#C86B51]/15 text-[#C86B51]">Structurée · {(option.formats || []).length} format(s)</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingOption(option); setOptionDialogOpen(true); }}
                      className="p-2 hover:bg-[#A5BBA8]/20 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4 text-[#5C6A61]" />
                    </button>
                    <button
                      onClick={() => handleDeleteOption(option)}
                      className="p-2 hover:bg-[#C86B51]/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-[#C86B51]" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-[#2A312C] mb-1">{option.name}</h3>
                <p className="text-sm text-[#5C6A61] mb-2">{option.description}</p>
                {option.usage_count > 0 && (
                  <p className="text-xs text-[#A5BBA8] mb-2">{option.usage_count} réservation(s)</p>
                )}
                {option.has_formats ? (
                  <p className="text-sm font-semibold text-[#A5BBA8]">
                    {(option.formats || []).filter(f => f.is_active !== false).map(f => `${f.name} +${formatPrice(f.price)}`).join(' · ') || 'Aucun format'}
                  </p>
                ) : (
                  <p className="text-xl font-semibold text-[#A5BBA8]">+{formatPrice(option.price)}</p>
                )}
                {option.extra_duration_minutes > 0 && (
                  <p className="text-xs text-[#5C6A61] mt-1">+{option.extra_duration_minutes} min de prestation</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FormulaForm({ formula, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    key: formula?.key || '',
    name: formula?.name || '',
    description: formula?.description || '',
    min_persons: formula?.min_persons || 1,
    max_persons: formula?.max_persons || 2,
    price: formula?.price || 0,
    duration_hours: formula?.duration_hours || 4.0,
    is_active: formula?.is_active ?? true,
    service_type: formula?.service_type || 'SAP',
    note_html: formula?.note_html || ''
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom de la formule est requis');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error('Le prix est requis');
      return;
    }
    // Auto-generate key from name if not set
    const data = {
      ...formData,
      key: formData.key || formData.name.toLowerCase().replace(/[^a-z0-9]/g, '_')
    };
    onSave(data);
  };

  return (
    <div className="space-y-5">
      {/* Nom - Champ principal large */}
      <div>
        <Label className="text-base font-semibold">Nom de la formule *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default text-lg"
          placeholder="Ex: Petit foyer, Famille nombreuse..."
          autoFocus
          data-testid="formula-name-input"
        />
      </div>

      {/* Description */}
      <div>
        <Label>Description</Label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-default"
          placeholder="Ex: Idéal pour les couples ou personnes seules"
        />
      </div>

      {/* Note tarifaire (rich-text avec terracotta) */}
      <div>
        <Label className="text-base font-semibold">Note tarifaire (affichée sur la page d'accueil)</Label>
        <p className="text-xs text-[#5C6A61] mb-2">
          Ex : « Tarif : 190 € — Avec l'Avance immédiate : <strong>95 € à payer</strong> ». Sélectionnez le montant à payer et appliquez le terracotta Mitonner pour le faire ressortir.
        </p>
        <RichTextEditor
          value={formData.note_html}
          onChange={(html) => setFormData({ ...formData, note_html: html })}
          placeholder="Note tarifaire optionnelle…"
        />
      </div>

      {/* Type SAP / NON SAP */}
      <div>
        <Label className="text-base font-semibold">Type de prestation *</Label>
        <div className="flex gap-4 mt-2">
          <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.service_type === 'SAP' ? 'border-[#A5BBA8] bg-[#A5BBA8]/10' : 'border-[#E3DED5]'}`}>
            <input
              type="radio"
              checked={formData.service_type === 'SAP'}
              onChange={() => setFormData({ ...formData, service_type: 'SAP' })}
              className="sr-only"
            />
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.service_type === 'SAP' ? 'border-[#A5BBA8]' : 'border-[#5C6A61]'}`}>
              {formData.service_type === 'SAP' && <div className="w-2.5 h-2.5 rounded-full bg-[#A5BBA8]" />}
            </div>
            <div>
              <p className="font-semibold text-[#2A312C]">SAP</p>
              <p className="text-xs text-[#5C6A61]">Service à la Personne - Crédit d'impôt 50%</p>
            </div>
          </label>
          <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.service_type === 'NON_SAP' ? 'border-[#C86B51] bg-[#C86B51]/10' : 'border-[#E3DED5]'}`}>
            <input
              type="radio"
              checked={formData.service_type === 'NON_SAP'}
              onChange={() => setFormData({ ...formData, service_type: 'NON_SAP' })}
              className="sr-only"
            />
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.service_type === 'NON_SAP' ? 'border-[#C86B51]' : 'border-[#5C6A61]'}`}>
              {formData.service_type === 'NON_SAP' && <div className="w-2.5 h-2.5 rounded-full bg-[#C86B51]" />}
            </div>
            <div>
              <p className="font-semibold text-[#2A312C]">NON SAP</p>
              <p className="text-xs text-[#5C6A61]">Prestation complémentaire - Pas de crédit d'impôt</p>
            </div>
          </label>
        </div>
      </div>

      {/* Prix et Personnes sur la même ligne */}
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <Label className="text-base font-semibold">Prix (€) *</Label>
          <div className="relative">
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              className="input-default text-xl font-semibold pr-8"
              min={0}
              step={1}
              data-testid="formula-price-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6A61]">€</span>
          </div>
        </div>
        <div>
          <Label>Durée (h)</Label>
          <input
            type="number"
            value={formData.duration_hours}
            onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) || 4 })}
            className="input-default"
            min={1}
            max={8}
            step={0.5}
          />
        </div>
        <div>
          <Label>Min personnes</Label>
          <input
            type="number"
            value={formData.min_persons}
            onChange={(e) => setFormData({ ...formData, min_persons: parseInt(e.target.value) || 1 })}
            className="input-default"
            min={1}
            max={10}
          />
        </div>
        <div>
          <Label>Max personnes</Label>
          <input
            type="number"
            value={formData.max_persons}
            onChange={(e) => setFormData({ ...formData, max_persons: parseInt(e.target.value) || 2 })}
            className="input-default"
            min={1}
            max={10}
          />
        </div>
      </div>

      {/* Clé technique (optionnel, caché dans details) */}
      <details className="text-sm">
        <summary className="cursor-pointer text-[#5C6A61]">Options avancées</summary>
        <div className="mt-3 space-y-3">
          <div>
            <Label>Clé technique (auto-générée si vide)</Label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              className="input-default font-mono text-sm"
              placeholder="petit_foyer"
              data-testid="formula-key-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              id="formula-active"
            />
            <Label htmlFor="formula-active">Formule active (visible par les clients)</Label>
          </div>
        </div>
      </details>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          data-testid="save-formula-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
}

function OptionForm({ option, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    key: option?.key || '',
    name: option?.name || '',
    description: option?.description || '',
    price: option?.price || 0,
    extra_duration_minutes: option?.extra_duration_minutes ?? 0,
    is_active: option?.is_active ?? true,
    service_type: option?.service_type || 'SAP',
    has_formats: option?.has_formats ?? false,
    formats: option?.formats || []
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Le nom de l\'option est requis');
      return;
    }
    if (formData.has_formats) {
      if (!formData.formats.length) { toast.error('Ajoutez au moins un format'); return; }
      for (const f of formData.formats) {
        if (!f.name.trim()) { toast.error('Chaque format doit avoir un nom'); return; }
        for (const g of (f.groups || [])) {
          if (!g.name.trim()) { toast.error(`Un groupe du format « ${f.name} » n'a pas de nom`); return; }
          if (g.min_select > g.max_select) { toast.error(`Groupe « ${g.name} » : min > max`); return; }
          for (const c of (g.choices || [])) {
            if (!c.name.trim()) { toast.error(`Un choix du groupe « ${g.name} » n'a pas de nom`); return; }
          }
        }
      }
    }
    // Auto-generate key from name if not set
    const data = {
      ...formData,
      key: formData.key || formData.name.toLowerCase().replace(/[^a-z0-9]/g, '_')
    };
    onSave(data);
  };

  return (
    <div className="space-y-5">
      {/* Étape 1 — Informations générales */}
      <div className="rounded-xl bg-[#A5BBA8]/10 border border-[#A5BBA8]/30 p-3">
        <p className="text-sm font-semibold text-[#2A312C]">Étape 1 — Informations générales</p>
        <p className="text-xs text-[#5C6A61] mt-0.5">Nom, description, type de prestation (SAP / NON SAP) et activation de l&apos;option.</p>
      </div>

      {/* Nom - Champ principal large */}
      <div>
        <Label className="text-base font-semibold">Nom de l'option *</Label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-default text-lg"
          placeholder="Ex: Lunch box, Desserts, Pâtisserie..."
          autoFocus
          data-testid="option-name-input"
        />
      </div>

      {/* Description */}
      <div>
        <Label>Description</Label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-default"
          placeholder="Ex: 5 repas supplémentaires pour le midi"
        />
      </div>

      {/* Type SAP / NON SAP */}
      <div>
        <Label className="text-base font-semibold">Type de prestation *</Label>
        <p className="text-xs text-[#5C6A61] mb-2">Ex: Pâtisserie est généralement NON SAP</p>
        <div className="flex gap-4">
          <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all flex-1 ${formData.service_type === 'SAP' ? 'border-[#A5BBA8] bg-[#A5BBA8]/10' : 'border-[#E3DED5]'}`}>
            <input
              type="radio"
              checked={formData.service_type === 'SAP'}
              onChange={() => setFormData({ ...formData, service_type: 'SAP' })}
              className="sr-only"
            />
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.service_type === 'SAP' ? 'border-[#A5BBA8]' : 'border-[#5C6A61]'}`}>
              {formData.service_type === 'SAP' && <div className="w-2.5 h-2.5 rounded-full bg-[#A5BBA8]" />}
            </div>
            <div>
              <p className="font-semibold text-[#2A312C]">SAP</p>
              <p className="text-xs text-[#5C6A61]">Crédit 50%</p>
            </div>
          </label>
          <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all flex-1 ${formData.service_type === 'NON_SAP' ? 'border-[#C86B51] bg-[#C86B51]/10' : 'border-[#E3DED5]'}`}>
            <input
              type="radio"
              checked={formData.service_type === 'NON_SAP'}
              onChange={() => setFormData({ ...formData, service_type: 'NON_SAP' })}
              className="sr-only"
            />
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.service_type === 'NON_SAP' ? 'border-[#C86B51]' : 'border-[#5C6A61]'}`}>
              {formData.service_type === 'NON_SAP' && <div className="w-2.5 h-2.5 rounded-full bg-[#C86B51]" />}
            </div>
            <div>
              <p className="font-semibold text-[#2A312C]">NON SAP</p>
              <p className="text-xs text-[#5C6A61]">Pas de crédit</p>
            </div>
          </label>
        </div>
      </div>

      {/* Prix */}
      <div className="w-1/2">
        <Label className="text-base font-semibold">Prix (€)</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A61]">+</span>
          <input
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
            className="input-default text-xl font-semibold pl-8 pr-8"
            min={0}
            step={1}
            data-testid="option-price-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6A61]">€</span>
        </div>
      </div>

      {/* Temps supplémentaire (minutes) */}
      <div className="w-1/2">
        <Label className="text-base font-semibold">Temps supplémentaire (minutes)</Label>
        <p className="text-xs text-[#5C6A61] mb-2">Durée ajoutée à la prestation quand cette option est choisie (0 = aucun supplément)</p>
        <input
          type="number"
          value={formData.extra_duration_minutes}
          onChange={(e) => setFormData({ ...formData, extra_duration_minutes: Math.max(0, parseInt(e.target.value) || 0) })}
          className="input-default text-xl font-semibold"
          min={0}
          step={5}
          data-testid="option-extra-duration-input"
        />
      </div>

      {/* Options avancées */}
      <details className="text-sm">
        <summary className="cursor-pointer text-[#5C6A61]">Options avancées</summary>
        <div className="mt-3 space-y-3">
          <div>
            <Label>Clé technique (auto-générée si vide)</Label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              className="input-default font-mono text-sm"
              placeholder="lunch_box"
              data-testid="option-key-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              id="option-active"
            />
            <Label htmlFor="option-active">Option active (visible par les clients)</Label>
          </div>
        </div>
      </details>

      {/* Options structurées (formats / groupes / choix / rendements) */}
      <div className="border-t border-[#E3DED5] pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Checkbox
            checked={formData.has_formats}
            onCheckedChange={(checked) => setFormData({ ...formData, has_formats: !!checked })}
            id="option-has-formats"
            data-testid="option-has-formats"
          />
          <Label htmlFor="option-has-formats" className="text-base font-semibold">Option structurée (formats Petit/Grand, choix de recettes, lots)</Label>
        </div>
        {formData.has_formats ? (
          <>
            <p className="text-xs text-[#5C6A61] mb-3">Interface guidée en étapes : <strong>Étape 2</strong> les formats, <strong>Étapes 3 &amp; 4</strong> les recettes et la règle de choix. Quand des formats sont définis, le prix et le temps proviennent du <strong>format choisi</strong> (le prix simple ci-dessus est ignoré).</p>
            <StructuredFormatsEditor
              formats={formData.formats}
              onChange={(formats) => setFormData({ ...formData, formats })}
            />
          </>
        ) : (
          <p className="text-xs text-[#5C6A61]">Option simple : utilise le prix et le temps supplémentaire ci-dessus.</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          className="btn-primary"
          data-testid="save-option-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Enregistrer
        </button>
      </div>
    </div>
  );
}


// Site Settings Management - Admin can modify EVERYTHING
function SiteSettingsManagement() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminApi.getSiteSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set defaults if no settings exist
      setSettings({
        contact_phone: "01 23 45 67 89",
        contact_email: "contact@mitonner-essentiel.fr",
        slogan: "Profitez de votre soirée, on s'occupe du dîner !",
        hero_subtitle: "Un cuisinier passionné vient à votre domicile préparer 10 repas savoureux et équilibrés.",
        hero_image_url: "",
        vegetables_image_url: "",
        about_image_url: "",
        logo_url: "",
        about_title: "Des repas faits maison, pour tous",
        about_text1: "Chez Mitonner, nous croyons que chaque famille mérite de manger des repas savoureux et équilibrés.",
        about_text2: "Nos cuisiniers sont passionnés et attentifs.",
        cta_title: "Prêt à simplifier vos repas ?",
        cta_subtitle: "Rejoignez les centaines de familles qui ont adopté Mitonner.",
        feature1_title: "Cuisinier à domicile",
        feature1_desc: "Un cuisinier passionné se déplace chez vous",
        feature2_title: "10 repas variés",
        feature2_desc: "Mijotés, plats au four, poissons, soupes, tartes...",
        feature3_title: "Vos courses, vos produits",
        feature3_desc: "Recevez votre liste personnalisée",
        feature4_title: "Réservation simple",
        feature4_desc: "Choisissez vos plats en quelques clics",
        step1_title: "Créez votre compte",
        step1_desc: "Indiquez votre adresse et la composition de votre foyer",
        step2_title: "Composez vos menus",
        step2_desc: "Sélectionnez vos 10 repas parmi notre carte de saison",
        step3_title: "Réservez votre créneau",
        step3_desc: "Choisissez la date et l'heure qui vous conviennent",
        step4_title: "Faites vos courses",
        step4_desc: "Achetez les ingrédients selon votre liste personnalisée",
        step5_title: "Profitez !",
        step5_desc: "Notre cuisinier vient chez vous préparer vos repas"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSiteSettings(settings);
      toast.success('Paramètres enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="site-settings-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Paramètres du site
          </h2>
          <p className="text-sm text-[#5C6A61]">Modifiez tous les contenus affichés sur le site</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          Enregistrer tout
        </button>
      </div>

      <div className="rounded-xl border border-[#E8B25C]/40 bg-[#E8B25C]/10 p-4 flex items-start gap-3" data-testid="settings-config-notice">
        <AlertTriangle className="w-5 h-5 text-[#E8B25C] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#2A312C]">
          <p className="font-semibold">Source unique de configuration : le Configuration Center</p>
          <p className="text-[#5C6A61] mt-1">
            Le logo, les images, le slogan, les coordonnées et le numéro SAP se gèrent désormais dans <strong>Configuration</strong> (menu de gauche) — c'est la référence utilisée sur le site, les emails et les PDF. Les champs correspondants ci-dessous sont conservés pour compatibilité mais peuvent être ignorés. Cet écran reste utile pour les réglages spécifiques (règles SAP, messages, questionnaire).
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-white border border-[#E3DED5] flex-wrap">
          <TabsTrigger value="contact" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            <Phone className="w-4 h-4 mr-2" />Contact
          </TabsTrigger>
          <TabsTrigger value="images" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            <Image className="w-4 h-4 mr-2" />Images
          </TabsTrigger>
          <TabsTrigger value="hero" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            Contenus éditoriaux
          </TabsTrigger>
          <TabsTrigger value="sap" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            <Euro className="w-4 h-4 mr-2" />SAP
          </TabsTrigger>
          <TabsTrigger value="zones" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            <MapPin className="w-4 h-4 mr-2" />Zones
          </TabsTrigger>
        </TabsList>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#A5BBA8]" />
              Informations de contact
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <input
                  type="text"
                  value={settings.contact_phone || ''}
                  onChange={(e) => updateField('contact_phone', e.target.value)}
                  className="input-default"
                  placeholder="01 23 45 67 89"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  className="input-default"
                  placeholder="contact@mitonner-essentiel.fr"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Images Tab */}
        <TabsContent value="images">
          <div className="card-default space-y-6">
            <h3 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <Image className="w-5 h-5 text-[#A5BBA8]" />
              Images du site
            </h3>
            <p className="text-sm text-[#5C6A61]">Collez les URLs des images (hébergées sur un service externe ou uploadées)</p>
            
            <div className="space-y-4">
              <div>
                <Label>Logo (URL)</Label>
                <input
                  type="url"
                  value={settings.logo_url || ''}
                  onChange={(e) => updateField('logo_url', e.target.value)}
                  className="input-default"
                  placeholder="https://..."
                />
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo preview" className="mt-2 h-16 object-contain" />
                )}
              </div>
              
              <div>
                <Label>Image Hero (bandeau principal)</Label>
                <input
                  type="url"
                  value={settings.hero_image_url || ''}
                  onChange={(e) => updateField('hero_image_url', e.target.value)}
                  className="input-default"
                  placeholder="https://..."
                  data-testid="hero-image-input"
                />
                {settings.hero_image_url && (
                  <img src={settings.hero_image_url} alt="Hero preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>
              
              <div>
                <Label>Image Légumes (section "Comment ça marche")</Label>
                <input
                  type="url"
                  value={settings.vegetables_image_url || ''}
                  onChange={(e) => updateField('vegetables_image_url', e.target.value)}
                  className="input-default"
                  placeholder="https://..."
                />
                {settings.vegetables_image_url && (
                  <img src={settings.vegetables_image_url} alt="Vegetables preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>
              
              <div>
                <Label>Image À propos (section "Notre mission")</Label>
                <input
                  type="url"
                  value={settings.about_image_url || ''}
                  onChange={(e) => updateField('about_image_url', e.target.value)}
                  className="input-default"
                  placeholder="https://..."
                />
                {settings.about_image_url && (
                  <img src={settings.about_image_url} alt="About preview" className="mt-2 h-32 object-cover rounded-lg" />
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Contenus éditoriaux déplacés vers le Centre de configuration (source unique) */}
        {['hero', 'features', 'steps', 'about'].map((tabKey) => (
          <TabsContent key={tabKey} value={tabKey}>
            <div className="card-default text-center py-10" data-testid={`settings-moved-${tabKey}`}>
              <Sliders className="w-8 h-8 text-[#A5BBA8] mx-auto mb-3" />
              <h3 className="font-semibold text-[#2A312C]">Contenu géré dans le Centre de configuration</h3>
              <p className="text-[#5C6A61] text-sm mt-2 max-w-xl mx-auto">
                Le slogan, les points forts, les étapes « Comment ça marche » et la section « À propos »
                se modifient désormais dans <strong>Configuration &gt; Contenu</strong>. C'est la source unique
                affichée sur le site public, pour éviter que deux endroits se contredisent.
              </p>
              <Link to="/admin/configuration" className="btn-primary inline-flex mt-4" data-testid="settings-goto-config">
                Ouvrir le Centre de configuration
              </Link>
            </div>
          </TabsContent>
        ))}

        {/* SAP Tab */}
        <TabsContent value="sap">
          <div className="space-y-6">
            {/* General SAP Settings */}
            <div className="card-default space-y-4">
              <h3 className="font-semibold text-[#2A312C] flex items-center gap-2">
                <Euro className="w-5 h-5 text-[#A5BBA8]" />
                Services à la Personne (SAP)
              </h3>
              
              <div className="p-4 bg-[#A5BBA8]/10 rounded-xl border border-[#A5BBA8]/20">
                <p className="text-sm text-[#5C6A61]">
                  <strong>Important :</strong> Utilisez le terme "déclaration" et non "agrément" dans vos textes légaux.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={settings.sap_enabled || false}
                  onCheckedChange={(checked) => updateField('sap_enabled', checked)}
                  id="sap-enabled"
                />
                <Label htmlFor="sap-enabled">Activer l'affichage SAP sur le site</Label>
              </div>

              <div>
                <Label>Numéro SAP</Label>
                <input
                  type="text"
                  value={settings.sap_number || ''}
                  onChange={(e) => updateField('sap_number', e.target.value)}
                  className="input-default"
                  placeholder="SAP102115565"
                  data-testid="sap-number-input"
                />
              </div>

              <div>
                <Label>Texte légal (avant le numéro)</Label>
                <input
                  type="text"
                  value={settings.sap_text || ''}
                  onChange={(e) => updateField('sap_text', e.target.value)}
                  className="input-default"
                  placeholder="Déclaration de services à la personne enregistrée sous le n°"
                />
                <p className="text-xs text-[#5C6A61] mt-1">Ce texte sera affiché suivi du numéro SAP</p>
              </div>

              <div>
                <Label>Texte marketing (bannière footer)</Label>
                <input
                  type="text"
                  value={settings.sap_marketing_text || ''}
                  onChange={(e) => updateField('sap_marketing_text', e.target.value)}
                  className="input-default"
                  placeholder="Bénéficiez de 50% de crédit d'impôt sur nos prestations"
                  data-testid="sap-marketing-input"
                />
              </div>
            </div>

            {/* Avance Immédiate */}
            <div className="card-default space-y-4">
              <h3 className="font-semibold text-[#2A312C]">Avance immédiate URSSAF</h3>
              
              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={settings.sap_avance_immediate_enabled || false}
                  onCheckedChange={(checked) => updateField('sap_avance_immediate_enabled', checked)}
                  id="sap-avance-enabled"
                />
                <Label htmlFor="sap-avance-enabled">L'avance immédiate est disponible</Label>
              </div>

              <div>
                <Label>Texte avance immédiate</Label>
                <input
                  type="text"
                  value={settings.sap_avance_immediate_text || ''}
                  onChange={(e) => updateField('sap_avance_immediate_text', e.target.value)}
                  className="input-default"
                  placeholder="Avance immédiate disponible"
                />
              </div>

              <div>
                <Label>Taux d'avance immédiate (%)</Label>
                <input
                  type="number"
                  value={settings.sap_avance_immediate_rate ?? 50}
                  onChange={(e) => updateField('sap_avance_immediate_rate', parseFloat(e.target.value) || 0)}
                  className="input-default w-32"
                  min={0}
                  max={100}
                  step={1}
                  data-testid="sap-avance-rate-input"
                />
                <p className="text-xs text-[#5C6A61] mt-1">
                  Part prise en charge par l'avance immédiate (défaut 50%). Le reste à payer est calculé automatiquement depuis le tarif administré.
                </p>
              </div>

              <div>
                <Label>Lien URSSAF</Label>
                <input
                  type="url"
                  value={settings.sap_urssaf_link || ''}
                  onChange={(e) => updateField('sap_urssaf_link', e.target.value)}
                  className="input-default"
                  placeholder="https://www.urssaf.fr/..."
                />
              </div>
            </div>

            {/* SAP Page Settings */}
            <div className="card-default space-y-4">
              <h3 className="font-semibold text-[#2A312C]">Page explicative SAP</h3>
              
              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={settings.sap_page_enabled || false}
                  onCheckedChange={(checked) => updateField('sap_page_enabled', checked)}
                  id="sap-page-enabled"
                />
                <Label htmlFor="sap-page-enabled">Activer la page /services-a-la-personne</Label>
              </div>

              <div>
                <Label>Titre de la page</Label>
                <input
                  type="text"
                  value={settings.sap_page_title || ''}
                  onChange={(e) => updateField('sap_page_title', e.target.value)}
                  className="input-default"
                  placeholder="Services à la personne & crédit d'impôt"
                  data-testid="sap-page-title-input"
                />
              </div>

              <div>
                <Label>Introduction</Label>
                <textarea
                  value={settings.sap_page_intro || ''}
                  onChange={(e) => updateField('sap_page_intro', e.target.value)}
                  className="input-default min-h-[80px]"
                  placeholder="Mitonner est déclaré en tant que Service à la Personne..."
                />
              </div>

              <div className="border-t border-[#E3DED5] pt-4">
                <p className="font-medium text-[#2A312C] mb-3">Section Crédit d'impôt</p>
                <div className="space-y-3">
                  <div>
                    <Label>Titre</Label>
                    <input
                      type="text"
                      value={settings.sap_page_credit_title || ''}
                      onChange={(e) => updateField('sap_page_credit_title', e.target.value)}
                      className="input-default"
                      placeholder="Crédit d'impôt de 50%"
                    />
                  </div>
                  <div>
                    <Label>Texte explicatif</Label>
                    <textarea
                      value={settings.sap_page_credit_text || ''}
                      onChange={(e) => updateField('sap_page_credit_text', e.target.value)}
                      className="input-default min-h-[80px]"
                    />
                  </div>
                  <div>
                    <Label>Exemple de calcul</Label>
                    <input
                      type="text"
                      value={settings.sap_page_credit_example || ''}
                      onChange={(e) => updateField('sap_page_credit_example', e.target.value)}
                      className="input-default"
                      placeholder="Une prestation à 200€ → 100€ après crédit d'impôt"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E3DED5] pt-4">
                <p className="font-medium text-[#2A312C] mb-3">Section Avance immédiate</p>
                <div className="space-y-3">
                  <div>
                    <Label>Titre</Label>
                    <input
                      type="text"
                      value={settings.sap_page_avance_title || ''}
                      onChange={(e) => updateField('sap_page_avance_title', e.target.value)}
                      className="input-default"
                      placeholder="L'avance immédiate"
                    />
                  </div>
                  <div>
                    <Label>Texte explicatif</Label>
                    <textarea
                      value={settings.sap_page_avance_text || ''}
                      onChange={(e) => updateField('sap_page_avance_text', e.target.value)}
                      className="input-default min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Link */}
            {settings.sap_page_enabled && (
              <div className="p-4 bg-[#F9F6F0] rounded-xl flex items-center justify-between">
                <span className="text-sm text-[#5C6A61]">Prévisualiser la page SAP</span>
                <a 
                  href="/services-a-la-personne" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm"
                >
                  Ouvrir la page
                </a>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Zones Tab */}
        <TabsContent value="zones">
          <div className="space-y-6">
            <div className="card-default space-y-4">
              <h3 className="font-semibold text-[#2A312C] flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C86B51]" />
                Vérification des zones
              </h3>
              
              <div className="p-4 bg-[#A5BBA8]/10 rounded-xl border border-[#A5BBA8]/20">
                <p className="text-sm text-[#5C6A61]">
                  Ces paramètres contrôlent la vérification des secteurs lors de la réservation. 
                  Un client hors zone ne pourra pas réserver mais pourra s'inscrire sur la liste d'attente.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={settings.sector_check_enabled !== false}
                  onCheckedChange={(checked) => updateField('sector_check_enabled', checked)}
                  id="sector-check-enabled"
                />
                <Label htmlFor="sector-check-enabled">Activer la vérification des secteurs</Label>
              </div>
            </div>

            <div className="card-default space-y-4">
              <h3 className="font-semibold text-[#2A312C]">Messages personnalisables</h3>
              
              <div>
                <Label>Message "Aucun cuisinier disponible"</Label>
                <textarea
                  value={settings.sector_no_employee_message || ''}
                  onChange={(e) => updateField('sector_no_employee_message', e.target.value)}
                  className="input-default min-h-[100px]"
                  placeholder="Aucun créneau n'est disponible dans votre secteur à cette date..."
                />
                <p className="text-xs text-[#5C6A61] mt-1">Affiché quand le client est dans une zone couverte mais aucun employé n'est disponible</p>
              </div>

              <div>
                <Label>Message "Zone non couverte"</Label>
                <textarea
                  value={settings.sector_out_of_zone_message || ''}
                  onChange={(e) => updateField('sector_out_of_zone_message', e.target.value)}
                  className="input-default min-h-[100px]"
                  placeholder="Nous n'intervenons pas encore dans votre zone..."
                />
                <p className="text-xs text-[#5C6A61] mt-1">Affiché quand le client n'est pas dans une zone couverte</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <button 
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          Enregistrer tous les paramètres
        </button>
      </div>
    </div>
  );
}

// Emails Management
function EmailsManagement() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [filters, setFilters] = useState({ status: '', email_type: '', search: '' });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [resending, setResending] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [cronLogs, setCronLogs] = useState([]);
  const [cronConfig, setCronConfig] = useState(null);
  const [showCronSetup, setShowCronSetup] = useState(false);
  const LIMIT = 20;

  useEffect(() => {
    loadData();
    loadCronData();
  }, [filters, page]);

  const loadCronData = async () => {
    try {
      const [logsRes, configRes] = await Promise.all([
        adminApi.getCronLogs(5),
        adminApi.getCronConfig()
      ]);
      setCronLogs(logsRes.data.logs || []);
      setCronConfig(configRes.data);
    } catch (error) {
      console.error('Error loading cron data:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * LIMIT,
        limit: LIMIT,
        ...filters
      };
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const [emailsRes, statsRes] = await Promise.all([
        adminApi.getEmailLogs(params),
        adminApi.getEmailStats()
      ]);
      setEmails(emailsRes.data.emails || []);
      setTotal(emailsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (emailLogId) => {
    setResending(emailLogId);
    try {
      await adminApi.resendEmail(emailLogId);
      toast.success('Email renvoyé !');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renvoi');
    } finally {
      setResending(null);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await adminApi.sendMenuReminders();
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    setSendingTest(true);
    try {
      const response = await adminApi.sendTestEmail(testEmail);
      toast.success(response.data.message);
      setTestEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      skipped: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      sent: 'Envoyé',
      failed: 'Échoué',
      pending: 'En attente',
      skipped: 'Ignoré'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      booking_confirmation: 'bg-blue-100 text-blue-800',
      welcome: 'bg-green-100 text-green-800',
      payment_failed: 'bg-red-100 text-red-800',
      payment_failure_warning: 'bg-orange-100 text-orange-800',
      slot_released: 'bg-gray-100 text-gray-800',
      payment_regularized: 'bg-teal-100 text-teal-800',
      menu_reminder_j5: 'bg-purple-100 text-purple-800',
      menu_reminder_j3: 'bg-purple-100 text-purple-800',
      recurring_confirmation: 'bg-indigo-100 text-indigo-800',
      admin_payment_alert: 'bg-red-100 text-red-800',
      test: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      booking_confirmation: 'Confirmation',
      welcome: 'Bienvenue',
      payment_failed: 'Échec paiement',
      payment_failure_warning: 'Avertissement',
      slot_released: 'Créneau libéré',
      payment_regularized: 'Régularisé',
      menu_reminder_j5: 'Rappel J-5',
      menu_reminder_j3: 'Rappel J-3',
      recurring_confirmation: 'Récurrent',
      admin_payment_alert: 'Alerte Admin',
      test: 'Test'
    };
    return (
      <Badge className={colors[type] || 'bg-gray-100'}>
        {labels[type] || type}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="emails-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Gestion des emails
          </h2>
          <p className="text-sm text-[#5C6A61]">Historique et statistiques des emails envoyés</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleSendReminders}
            disabled={sendingReminders}
            className="btn-secondary text-sm"
            data-testid="send-reminders-btn"
          >
            {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Envoyer rappels J-5/J-3
          </button>
          <button
            onClick={loadData}
            className="btn-secondary text-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="card-default">
            <p className="text-sm text-[#5C6A61] mb-1">Total envoyés</p>
            <p className="text-2xl font-semibold text-[#2A312C]">{stats.total || 0}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61] mb-1">Réussis</p>
            <p className="text-2xl font-semibold text-green-600">{stats.sent || 0}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61] mb-1">Échoués</p>
            <p className="text-2xl font-semibold text-red-600">{stats.failed || 0}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61] mb-1">Taux de succès</p>
            <p className="text-2xl font-semibold text-[#A5BBA8]">{stats.success_rate || 0}%</p>
          </div>
        </div>
      )}

      {/* Test Email */}
      <div className="card-default">
        <h3 className="font-semibold text-[#2A312C] mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#A5BBA8]" />
          Envoyer un email de test
        </h3>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="email@exemple.fr"
            className="input-default flex-1"
            data-testid="test-email-input"
          />
          <button
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmail}
            className="btn-primary"
            data-testid="send-test-btn"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
          </button>
        </div>
      </div>

      {/* Cron Configuration */}
      <div className="card-default" data-testid="cron-section">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-[#2A312C] flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#C86B51]" />
            Cron automatique (rappels J-5/J-3)
          </h3>
          <button
            onClick={() => setShowCronSetup(!showCronSetup)}
            className="btn-secondary text-sm"
          >
            {showCronSetup ? 'Masquer config' : 'Voir config'}
          </button>
        </div>

        {/* Cron setup instructions */}
        {showCronSetup && cronConfig && (
          <div className="mb-4 p-4 bg-[#F9F6F0] rounded-xl border border-[#E3DED5] space-y-4">
            <p className="text-sm text-[#5C6A61] font-semibold">
              Configuration cron-job.org (2 jobs à créer) :
            </p>
            
            {cronConfig.jobs ? cronConfig.jobs.map((job, idx) => (
              <div key={job.name} className="p-3 bg-white rounded-lg border border-[#E3DED5] space-y-2">
                <p className="text-sm font-medium text-[#2A312C]">
                  {idx + 1}. {job.name === 'daily_reminders' ? 'Rappels menus (J-5/J-3)' : 'Échecs paiement (retries & libération)'}
                </p>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-[#5C6A61]">URL :</span>
                    <code className="ml-2 px-2 py-1 bg-[#F9F6F0] rounded text-xs break-all">{job.endpoint}</code>
                  </div>
                  <div>
                    <span className="text-[#5C6A61]">Méthode :</span>
                    <code className="ml-2 px-2 py-1 bg-[#F9F6F0] rounded text-xs">{job.method}</code>
                  </div>
                  <div>
                    <span className="text-[#5C6A61]">Schedule :</span>
                    <code className="ml-2 px-2 py-1 bg-[#F9F6F0] rounded text-xs">{job.recommended_schedule}</code>
                    <span className="text-xs text-[#5C6A61] ml-2">
                      ({job.name === 'daily_reminders' ? 'tous les jours à 8h' : 'toutes les heures'})
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[#5C6A61]">{job.description}</p>
              </div>
            )) : (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-[#5C6A61]">URL :</span>
                  <code className="ml-2 px-2 py-1 bg-white rounded text-xs break-all">{cronConfig.endpoint}</code>
                </div>
                <div>
                  <span className="text-[#5C6A61]">Méthode :</span>
                  <code className="ml-2 px-2 py-1 bg-white rounded text-xs">{cronConfig.method}</code>
                </div>
              </div>
            )}
            
            <div className="pt-2 border-t border-[#E3DED5]">
              <p className="text-xs text-[#5C6A61]">
                <strong>Header requis pour les 2 jobs :</strong>
              </p>
              <code className="px-2 py-1 bg-white rounded text-xs">X-Cron-Secret: {cronConfig.secret_hint}</code>
            </div>
            
            <p className="text-xs text-[#5C6A61]">
              Créez un compte gratuit sur <a href="https://cron-job.org" target="_blank" rel="noopener noreferrer" className="text-[#C86B51] underline">cron-job.org</a> et configurez les 2 jobs avec ces paramètres.
            </p>
          </div>
        )}

        {/* Recent cron logs */}
        {cronLogs.length > 0 && (
          <div>
            <p className="text-sm text-[#5C6A61] mb-2">Dernières exécutions :</p>
            <div className="space-y-2">
              {cronLogs.map((log) => (
                <div key={log.cron_id} className="p-3 bg-[#F9F6F0] rounded-lg flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-[#2A312C]">
                      {formatDate(log.started_at)} - {log.job_type === 'payment_failures' ? 'Paiements' : 'Rappels menus'}
                    </p>
                    <p className="text-xs text-[#5C6A61]">
                      {log.job_type === 'payment_failures' ? (
                        <>Libérés: {log.results?.expired_released || 0} | Retries: {log.results?.retries_processed || 0} (✓ {log.results?.retry_successes || 0} / ✗ {log.results?.retry_failures || 0})</>
                      ) : (
                        <>J-5: {log.results?.j5?.sent || 0} envoyé(s), {log.results?.j5?.skipped_menu || 0} ignoré(s) | J-3: {log.results?.j3?.sent || 0} envoyé(s), {log.results?.j3?.skipped_menu || 0} ignoré(s)</>
                      )}
                    </p>
                  </div>
                  <Badge className={log.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {log.status === 'completed' ? 'OK' : 'Erreur'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {cronLogs.length === 0 && (
          <p className="text-sm text-[#5C6A61] text-center py-4">
            Aucune exécution cron enregistrée. Configurez le cron automatique ou utilisez le bouton "Envoyer rappels J-5/J-3" ci-dessus.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="card-default">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm">Rechercher</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A61]" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => { setFilters({...filters, search: e.target.value}); setPage(0); }}
                placeholder="Email, sujet..."
                className="input-default pl-10"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm">Statut</Label>
            <select
              value={filters.status}
              onChange={(e) => { setFilters({...filters, status: e.target.value}); setPage(0); }}
              className="input-default mt-1"
            >
              <option value="">Tous</option>
              <option value="sent">Envoyés</option>
              <option value="failed">Échoués</option>
              <option value="skipped">Ignorés</option>
            </select>
          </div>
          <div>
            <Label className="text-sm">Type</Label>
            <select
              value={filters.email_type}
              onChange={(e) => { setFilters({...filters, email_type: e.target.value}); setPage(0); }}
              className="input-default mt-1"
            >
              <option value="">Tous</option>
              <option value="booking_confirmation">Confirmation</option>
              <option value="welcome">Bienvenue</option>
              <option value="payment_failed">Échec paiement</option>
              <option value="menu_reminder_j5">Rappel J-5</option>
              <option value="menu_reminder_j3">Rappel J-3</option>
              <option value="recurring_confirmation">Récurrent</option>
              <option value="test">Test</option>
            </select>
          </div>
        </div>
      </div>

      {/* Emails List */}
      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12 text-[#5C6A61]">Aucun email trouvé</div>
      ) : (
        <>
          <div className="card-default overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3DED5]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Destinataire</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Sujet</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Statut</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#5C6A61]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.email_log_id} className="border-b border-[#E3DED5] hover:bg-[#F9F6F0]">
                    <td className="py-3 px-4 text-sm">{formatDate(email.created_at)}</td>
                    <td className="py-3 px-4 text-sm">{email.to}</td>
                    <td className="py-3 px-4">{getTypeBadge(email.email_type)}</td>
                    <td className="py-3 px-4 text-sm max-w-[200px] truncate" title={email.subject}>
                      {email.subject}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(email.status)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        {email.status === 'failed' && (
                          <button
                            onClick={() => handleResend(email.email_log_id)}
                            disabled={resending === email.email_log_id}
                            className="text-[#C86B51] hover:text-[#B55A42]"
                            title="Renvoyer"
                          >
                            {resending === email.email_log_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedEmail(email)}
                          className="text-[#5C6A61] hover:text-[#2A312C]"
                          title="Détails"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#5C6A61]">
              {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} sur {total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn-secondary text-sm"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * LIMIT >= total}
                className="btn-secondary text-sm"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}

      {/* Email Detail Dialog */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de l'email</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#5C6A61]">ID</p>
                  <p className="text-sm font-mono">{selectedEmail.email_log_id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C6A61]">Statut</p>
                  {getStatusBadge(selectedEmail.status)}
                </div>
                <div>
                  <p className="text-sm text-[#5C6A61]">Type</p>
                  {getTypeBadge(selectedEmail.email_type)}
                </div>
                <div>
                  <p className="text-sm text-[#5C6A61]">Date</p>
                  <p className="text-sm">{formatDate(selectedEmail.created_at)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-[#5C6A61]">Destinataire</p>
                <p className="text-sm">{selectedEmail.to}</p>
              </div>
              
              <div>
                <p className="text-sm text-[#5C6A61]">Sujet</p>
                <p className="text-sm">{selectedEmail.subject}</p>
              </div>
              
              {selectedEmail.error && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-[#5C6A61]">Erreur</p>
                  <p className="text-sm text-red-600">{selectedEmail.error}</p>
                </div>
              )}
              
              {selectedEmail.resend_id && (
                <div>
                  <p className="text-sm text-[#5C6A61]">ID Resend</p>
                  <p className="text-sm font-mono">{selectedEmail.resend_id}</p>
                </div>
              )}
              
              {selectedEmail.related_id && (
                <div>
                  <p className="text-sm text-[#5C6A61]">ID lié</p>
                  <p className="text-sm font-mono">{selectedEmail.related_id}</p>
                </div>
              )}
              
              {selectedEmail.status === 'failed' && (
                <button
                  onClick={() => { handleResend(selectedEmail.email_log_id); setSelectedEmail(null); }}
                  className="btn-primary w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Renvoyer cet email
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Kitchen Equipment Management
function KitchenEquipmentManagement() {
  const [equipment, setEquipment] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', icon: 'utensils', category: 'general',
    is_required: false, missing_message: '', display_order: 0, is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const CATEGORIES = [
    { value: 'cuisson', label: 'Cuisson' },
    { value: 'conservation', label: 'Conservation' },
    { value: 'preparation', label: 'Préparation' },
    { value: 'ustensiles', label: 'Ustensiles' },
    { value: 'general', label: 'Général' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [equipmentRes, settingsRes] = await Promise.all([
        adminApi.getKitchenEquipment(),
        adminApi.getKitchenSettings()
      ]);
      setEquipment(equipmentRes.data || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await adminApi.seedDefaultEquipment();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await adminApi.updateKitchenEquipment(editingItem.equipment_id, formData);
        toast.success('Équipement modifié');
      } else {
        await adminApi.createKitchenEquipment(formData);
        toast.success('Équipement créé');
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ name: '', description: '', icon: 'utensils', category: 'general', is_required: false, missing_message: '', display_order: 0, is_active: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.name}" ?`)) return;
    try {
      await adminApi.deleteKitchenEquipment(item.equipment_id);
      toast.success('Équipement supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      icon: item.icon || 'utensils',
      category: item.category || 'general',
      is_required: item.is_required || false,
      missing_message: item.missing_message || '',
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowForm(true);
  };

  const handleSaveSettings = async () => {
    try {
      await adminApi.updateKitchenSettings(settings);
      toast.success('Paramètres enregistrés');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const requiredCount = equipment.filter(e => e.is_required).length;
  const activeCount = equipment.filter(e => e.is_active).length;

  return (
    <div className="space-y-6" data-testid="kitchen-equipment-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Équipements cuisine
          </h2>
          <p className="text-sm text-[#5C6A61]">Questionnaire obligatoire pour les clients</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowSettings(true)} className="btn-secondary text-sm">
            <Settings className="w-4 h-4 mr-2" />Paramètres
          </button>
          <button onClick={() => { setEditingItem(null); setFormData({ name: '', description: '', icon: 'utensils', category: 'general', is_required: false, missing_message: '', display_order: 0, is_active: true }); setShowForm(true); }} className="btn-primary text-sm">
            <Plus className="w-4 h-4 mr-2" />Ajouter
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Total équipements</p>
          <p className="text-2xl font-semibold text-[#2A312C]">{equipment.length}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Obligatoires</p>
          <p className="text-2xl font-semibold text-[#C86B51]">{requiredCount}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Actifs</p>
          <p className="text-2xl font-semibold text-[#A5BBA8]">{activeCount}</p>
        </div>
      </div>

      {/* Seed button if empty */}
      {equipment.length === 0 && !loading && (
        <div className="card-default text-center py-8">
          <p className="text-[#5C6A61] mb-4">Aucun équipement configuré.</p>
          <button onClick={handleSeedDefaults} className="btn-primary">
            Initialiser avec les équipements par défaut
          </button>
        </div>
      )}

      {/* Equipment List */}
      {loading ? (
        <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
      ) : equipment.length > 0 && (
        <div className="card-default overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E3DED5]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Nom</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#5C6A61]">Catégorie</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-[#5C6A61]">Obligatoire</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-[#5C6A61]">Actif</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[#5C6A61]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((item) => (
                <tr key={item.equipment_id} className="border-b border-[#E3DED5] hover:bg-[#F9F6F0]">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-[#2A312C]">{item.name}</p>
                      {item.description && <p className="text-xs text-[#5C6A61]">{item.description}</p>}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-[#A5BBA8]/20 text-[#5C6A61]">
                      {CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item.is_required ? (
                      <Badge className="bg-red-100 text-red-800">Obligatoire</Badge>
                    ) : (
                      <span className="text-[#5C6A61]">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item.is_active ? (
                      <Check className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-red-500 mx-auto" />
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleEdit(item)} className="text-[#5C6A61] hover:text-[#2A312C]">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(item)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier l\'équipement' : 'Nouvel équipement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-default"
                placeholder="Ex: Four"
              />
            </div>
            <div>
              <Label>Description</Label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="input-default"
                placeholder="Description optionnelle"
              />
            </div>
            <div>
              <Label>Catégorie</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="input-default"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={formData.is_required}
                onCheckedChange={(checked) => setFormData({...formData, is_required: checked})}
                id="is-required"
              />
              <Label htmlFor="is-required">Équipement obligatoire</Label>
            </div>
            {formData.is_required && (
              <div>
                <Label>Message si absent</Label>
                <textarea
                  value={formData.missing_message}
                  onChange={(e) => setFormData({...formData, missing_message: e.target.value})}
                  className="input-default min-h-[80px]"
                  placeholder="Message affiché si cet équipement obligatoire est manquant"
                />
              </div>
            )}
            <div>
              <Label>Ordre d'affichage</Label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                className="input-default"
              />
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                id="is-active"
              />
              <Label htmlFor="is-active">Actif</Label>
            </div>
            <div className="flex gap-2 pt-4">
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Annuler</button>
              <button onClick={handleSubmit} disabled={saving} className="btn-primary flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingItem ? 'Modifier' : 'Créer')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Paramètres du questionnaire</DialogTitle>
          </DialogHeader>
          {settings && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={settings.questionnaire_enabled}
                  onCheckedChange={(checked) => setSettings({...settings, questionnaire_enabled: checked})}
                  id="questionnaire-enabled"
                />
                <Label htmlFor="questionnaire-enabled">Activer le questionnaire équipements</Label>
              </div>
              <div>
                <Label>Fréquence de mise à jour (mois)</Label>
                <input
                  type="number"
                  value={settings.update_frequency_months || 6}
                  onChange={(e) => setSettings({...settings, update_frequency_months: parseInt(e.target.value) || 6})}
                  className="input-default"
                  min={1}
                  max={24}
                />
                <p className="text-xs text-[#5C6A61] mt-1">Les clients doivent reconfirmer leur questionnaire tous les X mois</p>
              </div>
              <div>
                <Label>Jours d'avertissement avant expiration</Label>
                <input
                  type="number"
                  value={settings.expiry_warning_days || 14}
                  onChange={(e) => setSettings({...settings, expiry_warning_days: parseInt(e.target.value) || 14})}
                  className="input-default"
                  min={1}
                />
              </div>
              <div>
                <Label>Message global si équipement obligatoire manquant</Label>
                <textarea
                  value={settings.global_missing_message || ''}
                  onChange={(e) => setSettings({...settings, global_missing_message: e.target.value})}
                  className="input-default min-h-[100px]"
                />
              </div>
              <div>
                <Label>Message si changement d'adresse</Label>
                <textarea
                  value={settings.address_change_message || ''}
                  onChange={(e) => setSettings({...settings, address_change_message: e.target.value})}
                  className="input-default min-h-[100px]"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <button onClick={() => setShowSettings(false)} className="btn-secondary flex-1">Annuler</button>
                <button onClick={handleSaveSettings} className="btn-primary flex-1">Enregistrer</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Referrals Management
function ReferralsManagement() {
  const [referrals, setReferrals] = useState([]);
  const [uses, setUses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [referralsRes, usesRes] = await Promise.all([
        adminApi.getReferrals(),
        adminApi.getReferralUses()
      ]);
      setReferrals(referralsRes.data);
      setUses(usesRes.data);
    } catch (error) {
      console.error('Error loading referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>;
  }

  const totalUses = uses.length;
  const totalRewards = uses.filter(u => u.reward_credited).reduce((sum, u) => sum + (u.reward_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="referrals-management">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
          Gestion des parrainages
        </h2>
        <p className="text-sm text-[#5C6A61]">Suivez les parrainages et les récompenses</p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Codes actifs</p>
          <p className="text-2xl font-semibold text-[#2A312C]">{referrals.filter(r => r.is_active).length}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Utilisations totales</p>
          <p className="text-2xl font-semibold text-[#A5BBA8]">{totalUses}</p>
        </div>
        <div className="card-default">
          <p className="text-sm text-[#5C6A61]">Récompenses versées</p>
          <p className="text-2xl font-semibold text-[#C86B51]">{formatPrice(totalRewards)}</p>
        </div>
      </div>

      {/* Referral codes list */}
      <div className="card-default">
        <h3 className="font-semibold text-[#2A312C] mb-4">Codes de parrainage</h3>
        {referrals.length === 0 ? (
          <p className="text-[#5C6A61] text-center py-8">Aucun code de parrainage créé</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3DED5]">
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Parrain</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Code</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Utilisations</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Réduction</th>
                  <th className="text-left py-3 px-4 text-[#5C6A61] font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((ref) => (
                  <tr key={ref.referral_id} className="border-b border-[#E3DED5] last:border-0">
                    <td className="py-3 px-4">
                      <p className="text-[#2A312C]">{ref.referrer?.name || 'N/A'}</p>
                      <p className="text-xs text-[#5C6A61]">{ref.referrer?.email}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono bg-[#F9F6F0] px-2 py-1 rounded text-[#2A312C]">
                        {ref.referral_code}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#2A312C]">{ref.uses_count}</td>
                    <td className="py-3 px-4 text-[#5C6A61]">{ref.discount_percent}%</td>
                    <td className="py-3 px-4">
                      <Badge className={ref.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {ref.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent uses */}
      <div className="card-default">
        <h3 className="font-semibold text-[#2A312C] mb-4">Dernières utilisations</h3>
        {uses.length === 0 ? (
          <p className="text-[#5C6A61] text-center py-8">Aucune utilisation</p>
        ) : (
          <div className="space-y-3">
            {uses.slice(0, 10).map((use) => (
              <div key={use.use_id} className="flex items-center justify-between p-3 bg-[#F9F6F0] rounded-lg">
                <div>
                  <p className="text-[#2A312C]">Réservation #{use.reservation_id?.slice(-8)}</p>
                  <p className="text-xs text-[#5C6A61]">
                    Réduction appliquée: {formatPrice(use.discount_applied)}
                  </p>
                </div>
                <Badge className={use.reward_credited ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                  {use.reward_credited ? 'Récompensé' : 'En attente'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



// Contenu & SEO - éditeur simple des pages éditoriales + métadonnées (réutilise /admin/pages)
const SEO_PAGES = [
  { id: 'accueil', label: 'Accueil' },
  { id: 'comment-ca-marche', label: 'Comment ça marche' },
  { id: 'menus', label: 'Menus' },
  { id: 'tarifs', label: 'Tarifs' },
  { id: 'zone-intervention', label: "Zone d'intervention" },
  { id: 'a-propos', label: 'À propos' },
  { id: 'contact', label: 'Contact' },
  { id: 'contenants-conservation', label: 'Contenants & Conservation (SEO uniquement)' },
];

function SeoContentManagement() {
  const [pageId, setPageId] = useState('accueil');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isContainers = pageId === 'contenants-conservation';

  useEffect(() => {
    setLoading(true);
    adminApi.getPage(pageId)
      .then((res) => setData(res.data))
      .catch(() => { setData(null); toast.error('Erreur de chargement'); })
      .finally(() => setLoading(false));
  }, [pageId]);

  const setField = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

  const setSection = (idx, field, value) => setData((prev) => ({
    ...prev,
    sections: prev.sections.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
  }));

  const addSection = () => setData((prev) => ({
    ...prev,
    sections: [...(prev.sections || []), { id: `sec_${Date.now()}`, type: 'section', title: '', content: '', is_active: true }],
  }));

  const removeSection = (idx) => setData((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));

  const moveSection = (idx, dir) => setData((prev) => {
    const arr = [...prev.sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...prev, sections: arr };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updatePage(pageId, data);
      toast.success('Contenu enregistré !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seo-content-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">Contenu du site / SEO</h2>
        <div className="flex items-center gap-3">
          <select value={pageId} onChange={(e) => setPageId(e.target.value)} className="input-default py-2" data-testid="seo-page-select">
            {SEO_PAGES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <Button onClick={handleSave} disabled={saving || !data} data-testid="seo-save-btn">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-10 text-center text-[#5C6A61]">Chargement…</div>
      ) : (
        <>
          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Métadonnées (SEO)</h3>
            <div>
              <Label>Title SEO</Label>
              <input type="text" value={data.seo_title || ''} onChange={(e) => setField('seo_title', e.target.value)} className="input-default" data-testid="seo-title-input" />
            </div>
            <div>
              <Label>Meta description</Label>
              <textarea rows={2} value={data.meta_description || ''} onChange={(e) => setField('meta_description', e.target.value)} className="input-default" data-testid="seo-meta-input" />
            </div>
            {pageId === 'accueil' && (
              <div>
                <Label>Accroche au-dessus du titre (eyebrow)</Label>
                <input type="text" value={data.eyebrow || ''} onChange={(e) => setField('eyebrow', e.target.value)} className="input-default" data-testid="seo-eyebrow-input" />
              </div>
            )}
          </div>

          <div className="card-default space-y-4">
            <h3 className="font-semibold text-[#2A312C]">Contenu éditorial</h3>
            <div>
              <Label>Titre H1</Label>
              <input type="text" value={data.h1 || ''} onChange={(e) => setField('h1', e.target.value)} className="input-default" data-testid="seo-h1-input" />
            </div>
            <div>
              <Label>Introduction</Label>
              <textarea rows={3} value={data.intro || ''} onChange={(e) => setField('intro', e.target.value)} className="input-default" data-testid="seo-intro-input" />
            </div>
          </div>

          {isContainers ? (
            <div className="card-default text-sm text-[#5C6A61]">
              Les sections détaillées de cette page (tableaux de conservation, options…) se modifient dans <strong>Pages éditables</strong>. Ici, seules les métadonnées et l&apos;introduction sont gérées.
            </div>
          ) : (
            <div className="card-default space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#2A312C]">Sections (H2 + texte)</h3>
                <Button variant="outline" onClick={addSection} data-testid="seo-add-section">Ajouter une section</Button>
              </div>
              {(data.sections || []).map((s, idx) => (
                <div key={s.id || idx} className="border border-[#E3DED5] rounded-xl p-4 space-y-3" data-testid={`seo-section-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <input type="text" placeholder="Titre H2" value={s.title || ''} onChange={(e) => setSection(idx, 'title', e.target.value)} className="input-default flex-1" data-testid={`seo-section-title-${idx}`} />
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => moveSection(idx, -1)} className="p-1.5 hover:bg-[#A5BBA8]/15 rounded" title="Monter">↑</button>
                      <button type="button" onClick={() => moveSection(idx, 1)} className="p-1.5 hover:bg-[#A5BBA8]/15 rounded" title="Descendre">↓</button>
                      <label className="flex items-center gap-1 text-xs text-[#5C6A61] px-1">
                        <input type="checkbox" checked={s.is_active !== false} onChange={(e) => setSection(idx, 'is_active', e.target.checked)} /> Active
                      </label>
                      <button type="button" onClick={() => removeSection(idx)} className="p-1.5 hover:bg-[#C86B51]/15 rounded text-[#C86B51]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <textarea rows={3} placeholder="Texte de la section" value={s.content || ''} onChange={(e) => setSection(idx, 'content', e.target.value)} className="input-default" data-testid={`seo-section-content-${idx}`} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Pages Management - Admin can edit page content like Containers page
function PagesManagement() {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      const response = await adminApi.getPage('contenants-conservation');
      setPageContent(response.data);
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Erreur lors du chargement de la page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updatePage('contenants-conservation', pageContent);
      toast.success('Page enregistrée !');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (sectionId, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateHousehold = (sectionId, householdIndex, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.households) {
          const newHouseholds = [...section.households];
          newHouseholds[householdIndex] = { ...newHouseholds[householdIndex], [field]: value };
          return { ...section, households: newHouseholds };
        }
        return section;
      })
    }));
  };

  const updateHouseholdItem = (sectionId, householdIndex, itemIndex, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.households) {
          const newHouseholds = [...section.households];
          const newItems = [...newHouseholds[householdIndex].items];
          newItems[itemIndex] = value;
          newHouseholds[householdIndex] = { ...newHouseholds[householdIndex], items: newItems };
          return { ...section, households: newHouseholds };
        }
        return section;
      })
    }));
  };

  const addHouseholdItem = (sectionId, householdIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.households) {
          const newHouseholds = [...section.households];
          newHouseholds[householdIndex] = {
            ...newHouseholds[householdIndex],
            items: [...newHouseholds[householdIndex].items, 'Nouvel élément']
          };
          return { ...section, households: newHouseholds };
        }
        return section;
      })
    }));
  };

  const removeHouseholdItem = (sectionId, householdIndex, itemIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.households) {
          const newHouseholds = [...section.households];
          newHouseholds[householdIndex] = {
            ...newHouseholds[householdIndex],
            items: newHouseholds[householdIndex].items.filter((_, i) => i !== itemIndex)
          };
          return { ...section, households: newHouseholds };
        }
        return section;
      })
    }));
  };

  const updateConservationItem = (sectionId, itemIndex, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.items) {
          const newItems = [...section.items];
          newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
          return { ...section, items: newItems };
        }
        return section;
      })
    }));
  };

  const addConservationItem = (sectionId) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.items) {
          return {
            ...section,
            items: [...section.items, { name: 'Nouvel aliment', duration: '3 jours' }]
          };
        }
        return section;
      })
    }));
  };

  const removeConservationItem = (sectionId, itemIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.items) {
          return {
            ...section,
            items: section.items.filter((_, i) => i !== itemIndex)
          };
        }
        return section;
      })
    }));
  };

  const updateTip = (sectionId, tipIndex, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.tips) {
          const newTips = [...section.tips];
          newTips[tipIndex] = value;
          return { ...section, tips: newTips };
        }
        return section;
      })
    }));
  };

  const addTip = (sectionId) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.tips) {
          return { ...section, tips: [...section.tips, 'Nouveau conseil'] };
        }
        return section;
      })
    }));
  };

  const removeTip = (sectionId, tipIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.tips) {
          return { ...section, tips: section.tips.filter((_, i) => i !== tipIndex) };
        }
        return section;
      })
    }));
  };

  const updateOption = (sectionId, optionIndex, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.options) {
          const newOptions = [...section.options];
          newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
          return { ...section, options: newOptions };
        }
        return section;
      })
    }));
  };

  const updateOptionItem = (sectionId, optionIndex, itemIndex, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.options) {
          const newOptions = [...section.options];
          const newItems = [...newOptions[optionIndex].items];
          newItems[itemIndex] = value;
          newOptions[optionIndex] = { ...newOptions[optionIndex], items: newItems };
          return { ...section, options: newOptions };
        }
        return section;
      })
    }));
  };

  const addOptionItem = (sectionId, optionIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.options) {
          const newOptions = [...section.options];
          newOptions[optionIndex] = {
            ...newOptions[optionIndex],
            items: [...newOptions[optionIndex].items, 'Nouvel élément']
          };
          return { ...section, options: newOptions };
        }
        return section;
      })
    }));
  };

  const removeOptionItem = (sectionId, optionIndex, itemIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId && section.options) {
          const newOptions = [...section.options];
          newOptions[optionIndex] = {
            ...newOptions[optionIndex],
            items: newOptions[optionIndex].items.filter((_, i) => i !== itemIndex)
          };
          return { ...section, options: newOptions };
        }
        return section;
      })
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  if (!pageContent) {
    return (
      <div className="text-center py-12 text-[#5C6A61]">
        Page non trouvée
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pages-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
            Page Contenants et conservation
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Modifiez le contenu de la page d'information sur les contenants
          </p>
          <a 
            href="/contenants-conservation" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-[#A5BBA8] hover:underline inline-flex items-center gap-1 mt-1"
          >
            Voir la page <ChevronRight className="w-4 h-4" />
          </a>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-page-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          Enregistrer
        </button>
      </div>

      {/* Title */}
      <div className="card-default">
        <Label className="text-[#2A312C] font-medium mb-2 block">Titre de la page</Label>
        <input
          type="text"
          value={pageContent.title || ''}
          onChange={(e) => setPageContent({ ...pageContent, title: e.target.value })}
          className="input-field"
          data-testid="page-title-input"
        />
      </div>

      {/* Sections */}
      {pageContent.sections?.map((section) => (
        <div key={section.id} className="card-default">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-[#A5BBA8]/20 text-[#2A312C]">
              {section.type}
            </Badge>
            <h3 className="font-semibold text-[#2A312C]">{section.title}</h3>
          </div>

          {/* Section Title */}
          <div className="mb-4">
            <Label className="text-[#5C6A61] text-sm mb-1 block">Titre de la section</Label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => updateSection(section.id, 'title', e.target.value)}
              className="input-field"
            />
          </div>

          {/* Content field for intro and section types */}
          {(section.type === 'intro' || section.type === 'section') && (
            <div className="mb-4">
              <Label className="text-[#5C6A61] text-sm mb-1 block">
                Contenu <span className="text-xs">(utilisez **texte** pour le gras)</span>
              </Label>
              <textarea
                value={section.content || ''}
                onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                className="input-field min-h-[100px]"
                rows={4}
              />
            </div>
          )}

          {/* Households (containers section) */}
          {section.type === 'containers' && section.households && (
            <div className="space-y-4">
              <Label className="text-[#5C6A61] text-sm block">Contenants par taille de foyer</Label>
              {section.households.map((household, hIndex) => (
                <div key={hIndex} className="p-4 bg-[#F9F6F0] rounded-lg">
                  <div className="mb-3">
                    <Label className="text-xs text-[#5C6A61]">Taille du foyer</Label>
                    <input
                      type="text"
                      value={household.size || ''}
                      onChange={(e) => updateHousehold(section.id, hIndex, 'size', e.target.value)}
                      className="input-field"
                    />
                  </div>
                  <Label className="text-xs text-[#5C6A61] mb-2 block">
                    Éléments <span className="text-xs">(utilisez **texte** pour le gras)</span>
                  </Label>
                  <div className="space-y-2">
                    {household.items?.map((item, iIndex) => (
                      <div key={iIndex} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => updateHouseholdItem(section.id, hIndex, iIndex, e.target.value)}
                          className="input-field flex-1"
                        />
                        <button
                          onClick={() => removeHouseholdItem(section.id, hIndex, iIndex)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addHouseholdItem(section.id, hIndex)}
                      className="text-sm text-[#A5BBA8] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Ajouter un élément
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Conservation items */}
          {section.type === 'conservation' && (
            <>
              <Label className="text-[#5C6A61] text-sm mb-2 block">Durées de conservation</Label>
              <div className="space-y-2 mb-4">
                {section.items?.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => updateConservationItem(section.id, index, 'name', e.target.value)}
                      className="input-field flex-1"
                      placeholder="Aliment"
                    />
                    <input
                      type="text"
                      value={item.duration || ''}
                      onChange={(e) => updateConservationItem(section.id, index, 'duration', e.target.value)}
                      className="input-field w-40"
                      placeholder="Durée"
                    />
                    <button
                      onClick={() => removeConservationItem(section.id, index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addConservationItem(section.id)}
                  className="text-sm text-[#A5BBA8] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Ajouter un aliment
                </button>
              </div>

              {/* Conservation tips */}
              <Label className="text-[#5C6A61] text-sm mb-2 block">Conseils importants</Label>
              <div className="space-y-2">
                {section.tips?.map((tip, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={tip}
                      onChange={(e) => updateTip(section.id, index, e.target.value)}
                      className="input-field flex-1"
                    />
                    <button
                      onClick={() => removeTip(section.id, index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addTip(section.id)}
                  className="text-sm text-[#A5BBA8] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Ajouter un conseil
                </button>
              </div>
            </>
          )}

          {/* Options section */}
          {section.type === 'options' && section.options && (
            <div className="space-y-4">
              {section.options.map((option, oIndex) => (
                <div key={oIndex} className="p-4 bg-[#F9F6F0] rounded-lg">
                  <div className="mb-3">
                    <Label className="text-xs text-[#5C6A61]">Nom de l'option</Label>
                    <input
                      type="text"
                      value={option.name || ''}
                      onChange={(e) => updateOption(section.id, oIndex, 'name', e.target.value)}
                      className="input-field"
                    />
                  </div>
                  <Label className="text-xs text-[#5C6A61] mb-2 block">Détails</Label>
                  <div className="space-y-2">
                    {option.items?.map((item, iIndex) => (
                      <div key={iIndex} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => updateOptionItem(section.id, oIndex, iIndex, e.target.value)}
                          className="input-field flex-1"
                        />
                        <button
                          onClick={() => removeOptionItem(section.id, oIndex, iIndex)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addOptionItem(section.id, oIndex)}
                      className="text-sm text-[#A5BBA8] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Ajouter un détail
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tips section */}
          {section.type === 'tips' && section.tips && (
            <div className="space-y-2">
              {section.tips.map((tip, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={tip}
                    onChange={(e) => updateTip(section.id, index, e.target.value)}
                    className="input-field flex-1"
                  />
                  <button
                    onClick={() => removeTip(section.id, index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addTip(section.id)}
                className="text-sm text-[#A5BBA8] hover:underline flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Ajouter un conseil
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Payment Failures Management
function PaymentFailuresManagement() {
  const [failures, setFailures] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [processing, setProcessing] = useState(null);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [selectedFailure, setSelectedFailure] = useState(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const [failuresRes, statsRes] = await Promise.all([
        adminApi.getPaymentFailures(filter || null),
        adminApi.getPaymentFailuresStats()
      ]);
      setFailures(failuresRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading payment failures:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (failureId) => {
    setProcessing(failureId);
    try {
      const result = await adminApi.retryPayment(failureId);
      if (result.data.success) {
        toast.success('Paiement réussi !');
      } else {
        toast.error(`Échec: ${result.data.error}`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleForceRelease = async () => {
    if (!selectedFailure) return;
    setProcessing(selectedFailure.failure_id);
    try {
      await adminApi.forceReleaseSlot(selectedFailure.failure_id);
      toast.success('Créneau libéré');
      setShowReleaseDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkRegularized = async (failureId) => {
    setProcessing(failureId);
    try {
      await adminApi.markPaymentRegularized(failureId);
      toast.success('Marqué comme régularisé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      regularized: { label: 'Régularisé', color: 'bg-green-100 text-green-800' },
      slot_released: { label: 'Créneau libéré', color: 'bg-red-100 text-red-800' },
      expired: { label: 'Expiré', color: 'bg-gray-100 text-gray-800' },
    };
    const s = statuses[status] || statuses.pending;
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const getRemainingTime = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diff = deadlineDate - now;
    
    if (diff <= 0) return <span className="text-red-600">Expiré</span>;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return <span className={hours < 6 ? 'text-red-600' : 'text-[#E8B25C]'}>{hours}h {minutes}m</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-failures-management">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">En attente</p>
            <p className="text-2xl font-semibold text-yellow-600">{stats.pending}</p>
            <p className="text-xs text-[#5C6A61]">À risque: {formatPrice(stats.amount_at_risk)}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Régularisés</p>
            <p className="text-2xl font-semibold text-green-600">{stats.regularized}</p>
            <p className="text-xs text-[#5C6A61]">Récupéré: {formatPrice(stats.amount_recovered)}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Créneaux libérés</p>
            <p className="text-2xl font-semibold text-red-600">{stats.slot_released}</p>
            <p className="text-xs text-[#5C6A61]">Perdu: {formatPrice(stats.amount_lost)}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Total traités</p>
            <p className="text-2xl font-semibold text-[#2A312C]">{stats.regularized + stats.slot_released + stats.expired}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-default w-auto"
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="regularized">Régularisés</option>
          <option value="slot_released">Créneaux libérés</option>
        </select>
      </div>

      {/* Failures List */}
      {failures.length === 0 ? (
        <div className="card-default text-center py-12">
          <AlertTriangle className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun échec de paiement</p>
        </div>
      ) : (
        <div className="space-y-4">
          {failures.map((failure) => (
            <div key={failure.failure_id} className="card-default">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-[#2A312C]">
                      {failure.client?.name || 'Client inconnu'}
                    </h3>
                    {getStatusBadge(failure.status)}
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#5C6A61]">
                    <p>Email: {failure.client?.email}</p>
                    <p>Réservation: {failure.reservation?.date}</p>
                    <p>Tentatives: <strong>{failure.attempts_count}/3</strong></p>
                    {failure.status === 'pending' && (
                      <p>Temps restant: {getRemainingTime(failure.deadline)}</p>
                    )}
                  </div>
                  
                  {/* Attempts history */}
                  {failure.attempts?.length > 0 && (
                    <div className="mt-3 p-2 bg-[#F9F6F0] rounded text-xs">
                      <p className="font-medium mb-1">Historique des tentatives:</p>
                      {failure.attempts.map((attempt, idx) => (
                        <p key={idx} className="text-[#5C6A61]">
                          {idx + 1}. {new Date(attempt.executed_at || attempt.created_at).toLocaleString('fr-FR')} - 
                          <span className={attempt.status === 'failed' ? 'text-red-600' : 'text-green-600'}>
                            {' '}{attempt.status === 'failed' ? 'Échec' : 'Succès'}
                          </span>
                          {attempt.error_message && <span className="text-red-500"> ({attempt.error_message})</span>}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <p className="text-2xl font-semibold text-[#2A312C]">{formatPrice(failure.amount)}</p>
                  
                  {failure.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRetry(failure.failure_id)}
                        disabled={processing === failure.failure_id}
                        className="btn-outline text-sm px-3 py-1 inline-flex items-center gap-1"
                      >
                        {processing === failure.failure_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Relancer
                      </button>
                      <button
                        onClick={() => handleMarkRegularized(failure.failure_id)}
                        disabled={processing === failure.failure_id}
                        className="btn-outline text-sm px-3 py-1 text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedFailure(failure);
                          setShowReleaseDialog(true);
                        }}
                        className="btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Force Release Dialog */}
      <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Libérer le créneau</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Êtes-vous sûr de vouloir libérer ce créneau ? Cette action est irréversible.
              </p>
              <p className="text-sm text-red-700 mt-2">
                Client: {selectedFailure?.client?.name}<br/>
                Montant: {selectedFailure && formatPrice(selectedFailure.amount)}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowReleaseDialog(false)} className="btn-outline flex-1">
                Annuler
              </button>
              <button
                onClick={handleForceRelease}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? 'Traitement...' : 'Confirmer la libération'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Recurring Slots Management
function RecurringSlotsManagement() {
  const [slots, setSlots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');

  const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const [slotsRes, statsRes] = await Promise.all([
        adminApi.getRecurringSlots(filter),
        adminApi.getRecurringStats()
      ]);
      setSlots(slotsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading recurring slots:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedSlot) return;
    setProcessing(selectedSlot.recurring_id);
    try {
      await adminApi.adminDeactivateRecurring(selectedSlot.recurring_id, deactivateReason);
      toast.success('Créneau désactivé');
      setShowDeactivateDialog(false);
      setDeactivateReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleProcessSlots = async () => {
    setProcessing('process');
    try {
      const result = await adminApi.processRecurringSlots();
      toast.success(result.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-slots-management">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Créneaux actifs</p>
            <p className="text-2xl font-semibold text-green-600">{stats.active}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Créneaux inactifs</p>
            <p className="text-2xl font-semibold text-gray-600">{stats.inactive}</p>
          </div>
          <div className="card-default">
            <p className="text-sm text-[#5C6A61]">Réservations créées</p>
            <p className="text-2xl font-semibold text-[#A5BBA8]">{stats.total_reservations_created}</p>
          </div>
          <div className="card-default">
            <button
              onClick={handleProcessSlots}
              disabled={processing === 'process'}
              className="w-full h-full flex flex-col items-center justify-center gap-2 hover:bg-[#F9F6F0] transition-colors rounded-lg"
            >
              {processing === 'process' ? (
                <Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" />
              ) : (
                <RefreshCw className="w-6 h-6 text-[#A5BBA8]" />
              )}
              <span className="text-sm text-[#5C6A61]">Traiter maintenant</span>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filter}
            onChange={(e) => setFilter(e.target.checked)}
            className="w-4 h-4 accent-[#A5BBA8]"
          />
          <span className="text-sm text-[#5C6A61]">Actifs uniquement</span>
        </label>
      </div>

      {/* Slots List */}
      {slots.length === 0 ? (
        <div className="card-default text-center py-12">
          <RefreshCw className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
          <p className="text-[#5C6A61]">Aucun créneau récurrent</p>
        </div>
      ) : (
        <div className="space-y-4">
          {slots.map((slot) => (
            <div 
              key={slot.recurring_id} 
              className={`card-default ${!slot.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-[#2A312C]">
                      {slot.client?.name || 'Client inconnu'}
                    </h3>
                    <Badge className={slot.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {slot.is_active ? 'Actif' : 'Désactivé'}
                    </Badge>
                    <Badge className="bg-[#A5BBA8]/20 text-[#2A312C]">
                      {DAY_NAMES[slot.day_of_week]}
                    </Badge>
                  </div>
                  
                  <div className="grid sm:grid-cols-3 gap-2 text-sm text-[#5C6A61]">
                    <p>Email: {slot.client?.email}</p>
                    <p>Créneau: {slot.slot_type === 'matin' ? 'Matin' : 'Après-midi'}</p>
                    <p>Formule: {slot.formula} • {slot.household_size} pers.</p>
                    <p>Code postal: {slot.postal_code}</p>
                    <p>Réservations créées: {slot.reservations_created || 0}</p>
                    {slot.next_reservation_date && slot.is_active && (
                      <p className="text-[#A5BBA8]">
                        Prochaine: {new Date(slot.next_reservation_date).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {slot.is_active && (
                    <button
                      onClick={() => {
                        setSelectedSlot(slot);
                        setShowDeactivateDialog(true);
                      }}
                      className="btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Désactiver
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deactivate Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver le créneau récurrent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C6A61]">
              Client: {selectedSlot?.client?.name}<br/>
              Créneau: {selectedSlot && DAY_NAMES[selectedSlot.day_of_week]} - {selectedSlot?.slot_type}
            </p>
            <div>
              <Label className="text-sm mb-1 block">Raison (optionnel)</Label>
              <textarea
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                className="input-field"
                rows={2}
                placeholder="Raison de la désactivation..."
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowDeactivateDialog(false)} className="btn-outline flex-1">
                Annuler
              </button>
              <button
                onClick={handleDeactivate}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? 'Traitement...' : 'Désactiver'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Finances Dashboard
function FinancesDashboard() {
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({ status: '', payment_type: '', search: '' });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Accounting export state
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportQuarter, setExportQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [exportPeriodType, setExportPeriodType] = useState('current_month');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [accountingPreview, setAccountingPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [exportingAccounting, setExportingAccounting] = useState(false);
  const [pennylaneStatus, setPennylaneStatus] = useState(null);
  
  const LIMIT = 20;

  useEffect(() => {
    loadOverview();
  }, [period]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTransactions();
    }
    if (activeTab === 'accounting') {
      loadAccountingPreview();
      loadPennylaneStatus();
    }
  }, [activeTab, filters, page]);
  
  // Separate effect for accounting period changes
  useEffect(() => {
    if (activeTab === 'accounting') {
      loadAccountingPreview();
    }
  }, [exportPeriodType, exportMonth, exportYear, exportQuarter, exportStartDate, exportEndDate]);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getFinancialOverview(period);
      setOverview(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données financières');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await adminApi.getTransactions({
        ...filters,
        limit: LIMIT,
        skip: page * LIMIT
      });
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadAccountingPreview = async () => {
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        period_type: exportPeriodType
      });
      
      // Add parameters based on period type
      if (exportPeriodType === 'current_month' || exportPeriodType === 'previous_month') {
        // No extra params needed
      } else if (exportPeriodType === 'quarter') {
        params.append('year', exportYear);
        params.append('quarter', exportQuarter);
      } else if (exportPeriodType === 'year') {
        params.append('year', exportYear);
      } else if (exportPeriodType === 'custom') {
        if (exportStartDate) params.append('start_date', exportStartDate);
        if (exportEndDate) params.append('end_date', exportEndDate);
      } else {
        // Legacy month/year selection
        params.append('year', exportYear);
        params.append('month', exportMonth);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/finances/accounting-preview-v2?${params}`,
        {
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccountingPreview(data);
      } else {
        setAccountingPreview(null);
      }
    } catch (error) {
      console.error('Error loading accounting preview:', error);
      setAccountingPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const loadPennylaneStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/finances/pennylane-status`,
        {
          credentials: 'include'
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPennylaneStatus(data);
      }
    } catch (error) {
      console.error('Error loading Pennylane status:', error);
    }
  };

  const handleExport = async () => {
    try {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/finances/export?period=${period}`, '_blank');
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleAccountingExport = async () => {
    if (!accountingPreview?.can_export) {
      toast.error('Aucune donnée à exporter pour cette période');
      return;
    }
    
    setExportingAccounting(true);
    try {
      const params = new URLSearchParams({
        period_type: exportPeriodType,
        include_invoices: 'true',
        include_summary: 'true'
      });
      
      // Add parameters based on period type
      if (exportPeriodType === 'quarter') {
        params.append('year', exportYear);
        params.append('quarter', exportQuarter);
      } else if (exportPeriodType === 'year') {
        params.append('year', exportYear);
      } else if (exportPeriodType === 'custom') {
        if (exportStartDate) params.append('start_date', exportStartDate);
        if (exportEndDate) params.append('end_date', exportEndDate);
      }
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/finances/accounting-export-v2?${params}`;
      
      // Open in new tab to trigger download
      window.open(url, '_blank');
      toast.success('Export comptable en cours de téléchargement...');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportingAccounting(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedTransaction) return;
    setProcessing(true);
    try {
      await adminApi.processRefund(selectedTransaction.transaction_id, {
        amount: refundAmount ? parseFloat(refundAmount) : null,
        reason: refundReason
      });
      toast.success('Remboursement effectué');
      setShowRefundDialog(false);
      setSelectedTransaction(null);
      setRefundAmount('');
      setRefundReason('');
      loadOverview();
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du remboursement');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finances-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-['Cormorant_Garamond'] text-[#2A312C]">Tableau de bord financier</h2>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input-default text-sm"
          >
            <option value="week">7 derniers jours</option>
            <option value="month">30 derniers jours</option>
            <option value="quarter">3 derniers mois</option>
            <option value="year">12 derniers mois</option>
          </select>
          <button onClick={handleExport} className="btn-secondary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F9F6F0]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="transactions" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            Transactions
          </TabsTrigger>
          <TabsTrigger value="refunds" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            Remboursements
          </TabsTrigger>
          <TabsTrigger value="accounting" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
            Export comptable
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {overview && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="card-default">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <ArrowDownCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5C6A61]">Total encaissé</p>
                      <p className="text-2xl font-semibold text-green-600">{formatCurrency(overview.metrics.total_received)}</p>
                    </div>
                  </div>
                </div>

                <div className="card-default">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <ArrowUpCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5C6A61]">Total remboursé</p>
                      <p className="text-2xl font-semibold text-red-600">{formatCurrency(overview.metrics.total_refunded)}</p>
                    </div>
                  </div>
                </div>

                <div className="card-default">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#A5BBA8]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5C6A61]">Net encaissé</p>
                      <p className="text-2xl font-semibold text-[#2A312C]">{formatCurrency(overview.metrics.net_amount)}</p>
                    </div>
                  </div>
                </div>

                <div className="card-default">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#C86B51]/10 flex items-center justify-center">
                      <Euro className="w-6 h-6 text-[#C86B51]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5C6A61]">SAP éligible</p>
                      <p className="text-2xl font-semibold text-[#C86B51]">{formatCurrency(overview.breakdown.sap_eligible)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card-default">
                  <h3 className="font-semibold text-[#2A312C] mb-4">Statut des paiements</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Payés</span>
                      <Badge className="bg-green-100 text-green-800">{overview.metrics.transaction_count.paid}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Échoués</span>
                      <Badge className="bg-red-100 text-red-800">{overview.metrics.transaction_count.failed}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Remboursés</span>
                      <Badge className="bg-amber-100 text-amber-800">{overview.metrics.transaction_count.refunded}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">En attente</span>
                      <Badge className="bg-gray-100 text-gray-800">{overview.metrics.transaction_count.pending}</Badge>
                    </div>
                  </div>
                </div>

                <div className="card-default">
                  <h3 className="font-semibold text-[#2A312C] mb-4">Répartition par type</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Checkout (classique)</span>
                      <span className="font-medium text-[#2A312C]">{formatCurrency(overview.breakdown.checkout)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Récurrent/Automatique</span>
                      <span className="font-medium text-[#2A312C]">{formatCurrency(overview.breakdown.recurring)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6A61]">Régularisation</span>
                      <span className="font-medium text-[#2A312C]">{formatCurrency(overview.breakdown.regularization)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Chart */}
              {overview.daily_chart?.length > 0 && (
                <div className="card-default">
                  <h3 className="font-semibold text-[#2A312C] mb-4">Évolution journalière</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#E3DED5]">
                          <th className="text-left py-2 text-[#5C6A61]">Date</th>
                          <th className="text-right py-2 text-[#5C6A61]">Encaissé</th>
                          <th className="text-right py-2 text-[#5C6A61]">Remboursé</th>
                          <th className="text-right py-2 text-[#5C6A61]">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.daily_chart.slice(-10).map((day) => (
                          <tr key={day.date} className="border-b border-[#E3DED5]/50">
                            <td className="py-2">{formatDate(day.date)}</td>
                            <td className="py-2 text-right text-green-600">{formatCurrency(day.received)}</td>
                            <td className="py-2 text-right text-red-600">{formatCurrency(day.refunded)}</td>
                            <td className="py-2 text-right font-medium">{formatCurrency(day.received - day.refunded)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <div className="space-y-4">
            {/* Filters */}
            <div className="card-default">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-sm">Rechercher</Label>
                  <div className="relative mt-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A61]" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => { setFilters({...filters, search: e.target.value}); setPage(0); }}
                      placeholder="Client, email, référence..."
                      className="input-default pl-10 w-full"
                    />
                  </div>
                </div>
                <div className="w-40">
                  <Label className="text-sm">Statut</Label>
                  <select
                    value={filters.status}
                    onChange={(e) => { setFilters({...filters, status: e.target.value}); setPage(0); }}
                    className="input-default w-full mt-1"
                  >
                    <option value="">Tous</option>
                    <option value="paid">Payé</option>
                    <option value="failed">Échoué</option>
                    <option value="refunded">Remboursé</option>
                    <option value="pending">En attente</option>
                  </select>
                </div>
                <div className="w-40">
                  <Label className="text-sm">Type</Label>
                  <select
                    value={filters.payment_type}
                    onChange={(e) => { setFilters({...filters, payment_type: e.target.value}); setPage(0); }}
                    className="input-default w-full mt-1"
                  >
                    <option value="">Tous</option>
                    <option value="checkout">Checkout</option>
                    <option value="automatic">Automatique</option>
                    <option value="recurring">Récurrent</option>
                    <option value="regularization">Régularisation</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="card-default overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E3DED5]">
                    <th className="text-left py-3 text-[#5C6A61]">Référence</th>
                    <th className="text-left py-3 text-[#5C6A61]">Date</th>
                    <th className="text-left py-3 text-[#5C6A61]">Client</th>
                    <th className="text-left py-3 text-[#5C6A61]">Type</th>
                    <th className="text-right py-3 text-[#5C6A61]">Montant</th>
                    <th className="text-center py-3 text-[#5C6A61]">Statut</th>
                    <th className="text-center py-3 text-[#5C6A61]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.transaction_id} className="border-b border-[#E3DED5]/50 hover:bg-[#F9F6F0]">
                      <td className="py-3">
                        <span className="font-mono text-xs">{txn.invoice_reference || txn.transaction_id}</span>
                        {txn.is_sap_eligible && (
                          <Badge className="ml-2 bg-[#C86B51]/10 text-[#C86B51] text-xs">SAP</Badge>
                        )}
                      </td>
                      <td className="py-3 text-[#5C6A61]">{formatDate(txn.created_at)}</td>
                      <td className="py-3">
                        <p className="font-medium text-[#2A312C]">{txn.client_name || '-'}</p>
                        <p className="text-xs text-[#5C6A61]">{txn.client_email}</p>
                      </td>
                      <td className="py-3">
                        <Badge className="bg-[#A5BBA8]/20 text-[#2A312C] text-xs">
                          {txn.payment_type === 'checkout' ? 'Classique' : 
                           txn.payment_type === 'automatic' ? 'Auto' : 
                           txn.payment_type === 'recurring' ? 'Récurrent' : 
                           txn.payment_type === 'regularization' ? 'Régul.' : txn.payment_type}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        <span className="font-medium">{formatCurrency(txn.amount)}</span>
                        {txn.refunded_amount > 0 && (
                          <p className="text-xs text-red-600">-{formatCurrency(txn.refunded_amount)}</p>
                        )}
                      </td>
                      <td className="py-3 text-center">
                        <Badge className={
                          txn.status === 'paid' ? 'bg-green-100 text-green-800' :
                          txn.status === 'failed' ? 'bg-red-100 text-red-800' :
                          txn.status === 'refunded' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {txn.status === 'paid' ? 'Payé' :
                           txn.status === 'failed' ? 'Échoué' :
                           txn.status === 'refunded' ? 'Remboursé' :
                           txn.status === 'initiated' ? 'Initié' : txn.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-center">
                        {txn.status === 'paid' && txn.refunded_amount < txn.amount && (
                          <button
                            onClick={() => { setSelectedTransaction(txn); setShowRefundDialog(true); }}
                            className="text-[#C86B51] hover:underline text-sm"
                          >
                            Rembourser
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {transactions.length === 0 && (
                <p className="text-center py-8 text-[#5C6A61]">Aucune transaction trouvée</p>
              )}
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="px-4 py-2 text-sm text-[#5C6A61]">
                  Page {page + 1} / {Math.ceil(total / LIMIT)}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * LIMIT >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Refunds Tab */}
        <TabsContent value="refunds">
          <div className="card-default">
            <h3 className="font-semibold text-[#2A312C] mb-4">Remboursements récents</h3>
            <p className="text-sm text-[#5C6A61] mb-4">
              Les remboursements sont traités directement sur votre compte Stripe professionnel.
              Consultez le <a href="https://dashboard.stripe.com/refunds" target="_blank" rel="noopener noreferrer" className="text-[#C86B51] underline">Dashboard Stripe</a> pour plus de détails.
            </p>
            <div className="space-y-3">
              {transactions.filter(t => t.status === 'refunded' || t.refunded_amount > 0).slice(0, 10).map((txn) => (
                <div key={txn.transaction_id} className="p-4 bg-[#F9F6F0] rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-medium text-[#2A312C]">{txn.client_name}</p>
                    <p className="text-sm text-[#5C6A61]">{formatDate(txn.refunded_at || txn.created_at)} - {txn.invoice_reference}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">-{formatCurrency(txn.refunded_amount || txn.amount)}</p>
                    <p className="text-xs text-[#5C6A61]">sur {formatCurrency(txn.amount)}</p>
                  </div>
                </div>
              ))}
              {transactions.filter(t => t.status === 'refunded' || t.refunded_amount > 0).length === 0 && (
                <p className="text-center py-4 text-[#5C6A61]">Aucun remboursement</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Accounting Export Tab */}
        <TabsContent value="accounting">
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="card-default">
              <h3 className="font-semibold text-[#2A312C] mb-4">Export comptable</h3>
              <p className="text-sm text-[#5C6A61] mb-6">
                Générez un dossier complet pour votre comptable contenant toutes les factures PDF 
                et un récapitulatif détaillé des transactions sur la période choisie.
              </p>

              {/* Period type selector */}
              <div className="mb-6">
                <Label className="text-sm mb-2 block">Période</Label>
                <div className="flex flex-wrap gap-2" data-testid="export-period-selector">
                  {[
                    { value: 'current_month', label: 'Mois en cours' },
                    { value: 'previous_month', label: 'Mois précédent' },
                    { value: 'quarter', label: 'Trimestre' },
                    { value: 'year', label: 'Année' },
                    { value: 'custom', label: 'Personnalisée' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setExportPeriodType(opt.value)}
                      data-testid={`export-period-${opt.value}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        exportPeriodType === opt.value
                          ? 'bg-[#A5BBA8] text-white'
                          : 'bg-[#F9F6F0] text-[#5C6A61] hover:bg-[#E3DED5]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional fields */}
              <div className="flex flex-wrap gap-4 items-end">
                {exportPeriodType === 'quarter' && (
                  <>
                    <div>
                      <Label className="text-sm mb-1 block">Trimestre</Label>
                      <select
                        value={exportQuarter}
                        onChange={(e) => setExportQuarter(parseInt(e.target.value))}
                        className="input-default"
                        data-testid="export-quarter-select"
                      >
                        <option value={1}>T1 (Jan - Mar)</option>
                        <option value={2}>T2 (Avr - Juin)</option>
                        <option value={3}>T3 (Juil - Sep)</option>
                        <option value={4}>T4 (Oct - Déc)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Année</Label>
                      <select
                        value={exportYear}
                        onChange={(e) => setExportYear(parseInt(e.target.value))}
                        className="input-default"
                        data-testid="export-quarter-year-select"
                      >
                        {[2024, 2025, 2026, 2027].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {exportPeriodType === 'year' && (
                  <div>
                    <Label className="text-sm mb-1 block">Année</Label>
                    <select
                      value={exportYear}
                      onChange={(e) => setExportYear(parseInt(e.target.value))}
                      className="input-default"
                      data-testid="export-year-select"
                    >
                      {[2024, 2025, 2026, 2027].map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                )}

                {exportPeriodType === 'custom' && (
                  <>
                    <div>
                      <Label className="text-sm mb-1 block">Date de début</Label>
                      <input
                        type="date"
                        value={exportStartDate}
                        onChange={(e) => setExportStartDate(e.target.value)}
                        className="input-default"
                        data-testid="export-start-date"
                      />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Date de fin</Label>
                      <input
                        type="date"
                        value={exportEndDate}
                        onChange={(e) => setExportEndDate(e.target.value)}
                        className="input-default"
                        data-testid="export-end-date"
                      />
                    </div>
                  </>
                )}

                <button
                  onClick={loadAccountingPreview}
                  disabled={loadingPreview}
                  className="btn-secondary"
                  data-testid="export-refresh-btn"
                >
                  {loadingPreview ? 'Chargement...' : 'Actualiser'}
                </button>
              </div>
            </div>

            {/* Preview */}
            {loadingPreview ? (
              <div className="card-default text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8] mx-auto mb-2" />
                <p className="text-[#5C6A61]">Chargement des données...</p>
              </div>
            ) : accountingPreview ? (
              <div className="card-default">
                <h4 className="font-semibold text-[#2A312C] mb-4">
                  Aperçu - {accountingPreview.period_label}
                </h4>
                
                {accountingPreview.can_export ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-[#F9F6F0] rounded-lg text-center">
                        <p className="text-3xl font-semibold text-[#2A312C]">{accountingPreview.transaction_count}</p>
                        <p className="text-sm text-[#5C6A61]">Transactions</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-3xl font-semibold text-green-600">{formatCurrency(accountingPreview.total_ttc)}</p>
                        <p className="text-sm text-[#5C6A61]">Total encaissé</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg text-center">
                        <p className="text-3xl font-semibold text-red-600">-{formatCurrency(accountingPreview.total_refunded)}</p>
                        <p className="text-sm text-[#5C6A61]">Remboursé</p>
                      </div>
                      <div className="p-4 bg-[#A5BBA8]/20 rounded-lg text-center">
                        <p className="text-3xl font-semibold text-[#2A312C]">{formatCurrency(accountingPreview.total_net)}</p>
                        <p className="text-sm text-[#5C6A61]">Net encaissé</p>
                      </div>
                    </div>

                    {/* SAP / NON SAP breakdown */}
                    <div className="grid grid-cols-2 gap-4 mb-6" data-testid="export-sap-breakdown">
                      <div className="p-4 bg-[#A5BBA8]/10 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#2A312C]">SAP</p>
                          <p className="text-xs text-[#5C6A61]">Éligible crédit d'impôt</p>
                        </div>
                        <p className="text-2xl font-semibold text-[#2A312C]">{accountingPreview.sap_count}</p>
                      </div>
                      <div className="p-4 bg-[#F9F6F0] rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#2A312C]">NON SAP</p>
                          <p className="text-xs text-[#5C6A61]">Non éligible</p>
                        </div>
                        <p className="text-2xl font-semibold text-[#2A312C]">{accountingPreview.non_sap_count}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-[#F9F6F0] rounded-lg mb-4">
                      <p className="font-medium text-[#2A312C] mb-2">Contenu de l'export :</p>
                      <ul className="text-sm text-[#5C6A61] space-y-1">
                        <li>📁 <strong>/factures/</strong> - {accountingPreview.transaction_count} factures PDF individuelles</li>
                        <li>📄 <strong>ventes / paiements / remboursements (CSV)</strong> - Détail de toutes les transactions</li>
                        <li>📊 <strong>resume_comptable.pdf</strong> - Synthèse avec répartition SAP / NON SAP</li>
                      </ul>
                    </div>

                    <button
                      onClick={handleAccountingExport}
                      disabled={exportingAccounting}
                      className="btn-primary w-full"
                      data-testid="export-accounting-btn"
                    >
                      {exportingAccounting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Télécharger l'export comptable ZIP
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-[#E3DED5] mx-auto mb-3" />
                    <p className="text-[#5C6A61]">Aucune transaction pour cette période</p>
                    <p className="text-sm text-[#5C6A61]">Sélectionnez un autre mois</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card-default text-center py-8">
                <FileText className="w-12 h-12 text-[#E3DED5] mx-auto mb-3" />
                <p className="text-[#5C6A61]">Sélectionnez une période et cliquez sur "Actualiser"</p>
              </div>
            )}

            {/* Info box */}
            <div className="card-default bg-[#A5BBA8]/10 border-[#A5BBA8]">
              <h4 className="font-semibold text-[#2A312C] mb-2">Format de l'export</h4>
              <div className="text-sm text-[#5C6A61] space-y-2">
                <p>
                  <strong>Fichier CSV (recap)</strong> - Compatible Excel/Google Sheets avec les colonnes : 
                  date prestation, date facture, numéro facture, client, type SAP/NON SAP, montants TTC/remboursé/net, 
                  statut paiement, ID Stripe.
                </p>
                <p>
                  <strong>Factures PDF</strong> - Nommage standardisé : FACTURE_YYYYMM_NUMERO_CLIENT.pdf. 
                  Mention SAP automatique sur les prestations éligibles au crédit d'impôt.
                </p>
                <p>
                  <strong>Récapitulatif PDF</strong> - Synthèse mensuelle avec totaux, répartition SAP/NON SAP, 
                  et estimation du crédit d'impôt clients.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Effectuer un remboursement</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F9F6F0] rounded-lg">
                <p className="font-medium text-[#2A312C]">{selectedTransaction.client_name}</p>
                <p className="text-sm text-[#5C6A61]">{selectedTransaction.invoice_reference}</p>
                <p className="text-sm text-[#5C6A61]">
                  Montant payé: {formatCurrency(selectedTransaction.amount)}
                  {selectedTransaction.refunded_amount > 0 && (
                    <span className="text-red-600"> (déjà remboursé: {formatCurrency(selectedTransaction.refunded_amount)})</span>
                  )}
                </p>
              </div>
              
              <div>
                <Label className="text-sm mb-1 block">Montant à rembourser (laisser vide pour remboursement total)</Label>
                <input
                  type="number"
                  step="0.01"
                  max={selectedTransaction.amount - (selectedTransaction.refunded_amount || 0)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={`Max: ${(selectedTransaction.amount - (selectedTransaction.refunded_amount || 0)).toFixed(2)}€`}
                  className="input-default w-full"
                />
              </div>
              
              <div>
                <Label className="text-sm mb-1 block">Raison du remboursement</Label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="input-default w-full"
                  rows={2}
                  placeholder="Raison..."
                />
              </div>
              
              <div className="flex gap-2">
                <button onClick={() => setShowRefundDialog(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button
                  onClick={handleRefund}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Traitement...' : 'Confirmer le remboursement'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



function ReviewStars({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= rating ? 'text-[#E0A458] fill-[#E0A458]' : 'text-[#D8D2C7]'}`} />
      ))}
    </div>
  );
}

function ReviewsManagement() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getReviews(filter || undefined);
      setReviews(res.data || []);
    } catch (e) {
      toast.error("Impossible de charger les avis.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (id, action) => {
    setBusyId(id);
    try {
      await adminApi.moderateReview(id, action);
      toast.success(action === 'publish' ? 'Avis publié.' : 'Avis masqué.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const feature = async (id, featured) => {
    setBusyId(id);
    try {
      await adminApi.featureReview(id, featured);
      toast.success(featured ? 'Avis mis en avant sur l\'accueil.' : 'Mise en avant retirée.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const statusBadge = (s) => {
    if (s === 'published') return <Badge className="bg-[#A5BBA8]/20 text-[#4B6B54] hover:bg-[#A5BBA8]/20">Publié</Badge>;
    if (s === 'hidden') return <Badge className="bg-[#C86B51]/15 text-[#C86B51] hover:bg-[#C86B51]/15">Masqué</Badge>;
    return <Badge className="bg-[#E0A458]/15 text-[#9A6B1F] hover:bg-[#E0A458]/15">En attente</Badge>;
  };

  const filters = [
    { key: '', label: 'Tous' },
    { key: 'pending', label: 'En attente' },
    { key: 'published', label: 'Publiés' },
    { key: 'hidden', label: 'Masqués' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#2A312C]">Avis clients</h1>
          <p className="text-[#5C6A61] text-sm">Modérez les avis et choisissez celui mis en avant sur l'accueil.</p>
        </div>
        <div className="flex gap-2">
          {filters.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm ${filter === f.key ? 'bg-[#A5BBA8] text-white' : 'bg-[#F9F6F0] text-[#5C6A61] hover:bg-[#E3DED5]'}`}
              data-testid={`reviews-filter-${f.key || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" /></div>
      ) : reviews.length === 0 ? (
        <div className="card-default text-center text-[#5C6A61] py-10" data-testid="reviews-admin-empty">Aucun avis pour ce filtre.</div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.review_id} className="card-default" data-testid={`admin-review-${r.review_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <ReviewStars rating={r.rating} />
                    {statusBadge(r.status)}
                    {r.is_featured && (
                      <Badge className="bg-[#C86B51]/15 text-[#C86B51] hover:bg-[#C86B51]/15">
                        <Star className="w-3 h-3 mr-1 fill-[#C86B51]" /> Mis en avant
                      </Badge>
                    )}
                  </div>
                  <p className="text-[#2A312C] mt-2 whitespace-pre-line">{r.comment}</p>
                  <p className="text-sm text-[#5C6A61] mt-2">— {r.display_name}</p>
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  {r.status !== 'published' ? (
                    <button onClick={() => moderate(r.review_id, 'publish')} disabled={busyId === r.review_id} className="btn-primary inline-flex items-center gap-2 justify-center" data-testid={`review-publish-${r.review_id}`}>
                      <Eye className="w-4 h-4" /> Publier
                    </button>
                  ) : (
                    <button onClick={() => moderate(r.review_id, 'hide')} disabled={busyId === r.review_id} className="btn-outline inline-flex items-center gap-2 justify-center" data-testid={`review-hide-${r.review_id}`}>
                      <EyeOff className="w-4 h-4" /> Masquer
                    </button>
                  )}
                  {r.status === 'published' && (
                    r.is_featured ? (
                      <button onClick={() => feature(r.review_id, false)} disabled={busyId === r.review_id} className="btn-outline inline-flex items-center gap-2 justify-center" data-testid={`review-unfeature-${r.review_id}`}>
                        <Star className="w-4 h-4" /> Retirer la mise en avant
                      </button>
                    ) : (
                      <button onClick={() => feature(r.review_id, true)} disabled={busyId === r.review_id} className="btn-outline inline-flex items-center gap-2 justify-center" data-testid={`review-feature-${r.review_id}`}>
                        <Star className="w-4 h-4" /> Mettre en avant
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

